import { getMcActiveOrgId, mcGetItem, mcSetItem, mcSetItemSilent, mcSetCachedItem, mcRemoveItem, mcGetCloudItem, mcSetCloudItem, mcSetCloudItemImmediate, mcListCloudSessionDetails, mcTransitionCountLedger, mcCreateImmutableFinalSnapshot } from "../utils/mcCloudSync";
import { idbGetJson, idbSetJson, idbListItems } from "../utils/mcStorageEngine";
import { buildProfileDisplayName, getCurrentProfile, getProfileAllowedSectionIds, isAdminProfile, normalizeStoreValue } from "../utils/profileAccess";
import { activeSections, formatSectionLabel, normalizeSectionId } from "../utils/mcSections";
import { resolveSite, isInventoryHeader } from "../utils/mcOrgData";
import { appendVarianceHistoryForArchivedSession } from "./mcAccountability";
import { appendResponseEventFromSession } from "./mcResponseEvents";

const DUPLICATE_REVIEW_DECISIONS_KEY = "mc-inventory-review-decisions-v4";
const DUPLICATE_REVIEW_LOCAL_KEY = "matrixos-inventory-review-decisions-v4";

function currentDecisionOrgIds() {
  const ids = [];
  const runtime = String(getMcActiveOrgId?.() || "").trim();
  if (runtime) ids.push(runtime);
  if (typeof window !== "undefined") {
    try {
      const sessionOrg = String(window.sessionStorage?.getItem("mc-active-org-id") || "").trim();
      if (sessionOrg) ids.push(sessionOrg);
    } catch (_) {}
  }
  if (!ids.length) ids.push("pending-org");
  return Array.from(new Set(ids));
}

function duplicateReviewDecisionStorageKey(scope = "ALL") {
  return scopedStorageKey(DUPLICATE_REVIEW_DECISIONS_KEY, normalizeScopeValue(scope));
}

function duplicateReviewLocalKeys(scope = "ALL") {
  return currentDecisionOrgIds().map(
    (orgId) => `${DUPLICATE_REVIEW_LOCAL_KEY}:${orgId}:${normalizeScopeValue(scope)}`,
  );
}

function normalizeDuplicateReviewDecisions(value = {}) {
  const unique = (items = []) => Array.from(new Set(
    (Array.isArray(items) ? items : [])
      .map((item) => String(item || "").trim().toLowerCase())
      .filter(Boolean),
  ));
  return {
    acceptedDuplicateKeys: unique(value?.acceptedDuplicateKeys),
    ignoredInventorySkus: unique(value?.ignoredInventorySkus),
    ignoredInventoryKeys: unique(value?.ignoredInventoryKeys),
    updatedAt: value?.updatedAt || "",
  };
}

function mergeDuplicateReviewDecisions(...values) {
  const normalized = values.map((value) => normalizeDuplicateReviewDecisions(value));
  return {
    acceptedDuplicateKeys: Array.from(new Set(normalized.flatMap((item) => item.acceptedDuplicateKeys))),
    ignoredInventorySkus: Array.from(new Set(normalized.flatMap((item) => item.ignoredInventorySkus))),
    ignoredInventoryKeys: Array.from(new Set(normalized.flatMap((item) => item.ignoredInventoryKeys))),
    updatedAt: normalized.map((item) => item.updatedAt).filter(Boolean).sort().pop() || "",
  };
}

function readRawJson(raw) {
  try { return raw ? JSON.parse(raw) : {}; } catch (_) { return {}; }
}

function readDirectDecision(scope = "ALL") {
  if (typeof window === "undefined") return {};
  const values = [];
  duplicateReviewLocalKeys(scope).forEach((key) => {
    try { values.push(readRawJson(window.localStorage.getItem(key))); } catch (_) {}
    try { values.push(readRawJson(window.sessionStorage?.getItem(key))); } catch (_) {}
  });
  return mergeDuplicateReviewDecisions(...values);
}

function writeDirectDecision(scope = "ALL", payload = {}) {
  if (typeof window === "undefined") return false;
  const value = JSON.stringify(normalizeDuplicateReviewDecisions(payload));
  let wrote = false;
  duplicateReviewLocalKeys(scope).forEach((key) => {
    try {
      window.localStorage.setItem(key, value);
      wrote = true;
    } catch (_) {
      try {
        window.sessionStorage?.setItem(key, value);
        wrote = true;
      } catch (_) {}
    }
  });
  return wrote;
}

function duplicateDecisionTimestamp(value = {}) {
  const time = Date.parse(String(value?.updatedAt || ""));
  return Number.isFinite(time) ? time : 0;
}

// A decision ledger is a replacement authority, not an append-only set.
// Unioning stale cache/cloud copies resurrects products that were explicitly
// restored from Ignore. Pick the newest complete copy for each scope first,
// then combine global + site authorities.
function newestDuplicateReviewDecision(...values) {
  const candidates = values
    .map((value, index) => ({ value: normalizeDuplicateReviewDecisions(value), index }))
    .filter(({ value }) =>
      Boolean(value.updatedAt) ||
      value.acceptedDuplicateKeys.length ||
      value.ignoredInventorySkus.length ||
      value.ignoredInventoryKeys.length
    );
  if (!candidates.length) return normalizeDuplicateReviewDecisions({});
  candidates.sort((a, b) => {
    const delta = duplicateDecisionTimestamp(b.value) - duplicateDecisionTimestamp(a.value);
    return delta || (b.index - a.index);
  });
  return candidates[0].value;
}

function readDuplicateReviewDecisionScope(scope = "ALL") {
  return newestDuplicateReviewDecision(
    readRawJson(mcGetItem(duplicateReviewDecisionStorageKey(scope))),
    readDirectDecision(scope),
  );
}

function readDuplicateReviewDecisions(scope = "ALL") {
  const normalizedScope = normalizeScopeValue(scope);
  const globalDecision = readDuplicateReviewDecisionScope("ALL");
  const scopeDecision = normalizedScope === "ALL"
    ? {}
    : readDuplicateReviewDecisionScope(normalizedScope);
  return mergeDuplicateReviewDecisions(globalDecision, scopeDecision);
}

function applyDuplicateReviewDecisions(state, scope = "ALL") {
  if (!state || typeof state !== "object") return state;
  const decisions = readDuplicateReviewDecisions(scope);
  return {
    ...state,
    config: {
      ...(state.config || {}),
      acceptedDuplicateKeys: decisions.acceptedDuplicateKeys,
      ignoredInventorySkus: decisions.ignoredInventorySkus,
      ignoredInventoryKeys: decisions.ignoredInventoryKeys,
    },
  };
}

export async function hydrateDuplicateReviewDecisions(scope = "ALL") {
  const normalizedScope = normalizeScopeValue(scope);
  const globalKey = duplicateReviewDecisionStorageKey("ALL");
  const scopeKey = duplicateReviewDecisionStorageKey(normalizedScope);
  const localGlobal = readDuplicateReviewDecisionScope("ALL");
  const localScope = normalizedScope === "ALL"
    ? {}
    : readDuplicateReviewDecisionScope(normalizedScope);

  const [globalCloudRaw, scopeCloudRaw] = await Promise.all([
    withMcTimeout(mcGetCloudItem(globalKey), 5000, null),
    scopeKey === globalKey ? Promise.resolve(null) : withMcTimeout(mcGetCloudItem(scopeKey), 5000, null),
  ]);
  const globalAuthority = newestDuplicateReviewDecision(
    readRawJson(globalCloudRaw),
    localGlobal,
  );
  const scopeAuthority = normalizedScope === "ALL"
    ? {}
    : newestDuplicateReviewDecision(
        readRawJson(scopeCloudRaw),
        localScope,
      );
  const payload = mergeDuplicateReviewDecisions(globalAuthority, scopeAuthority);
  const value = JSON.stringify(payload);

  // The direct org-scoped browser ledger is deliberately outside the MC cache
  // bootstrap. MC hydration may replace/remove mc-* cache keys while the org is
  // starting; this key survives that cycle and is the refresh authority.
  writeDirectDecision("ALL", payload);
  if (normalizedScope !== "ALL") writeDirectDecision(normalizedScope, payload);
  // Hydration is a cloud -> browser cache operation. It must never schedule the
  // same value back to Firestore or emit mc-cloud-sync, otherwise Review can
  // trigger its own hydration again and enter a read/write feedback loop.
  mcSetItemSilent(globalKey, value);
  if (scopeKey !== globalKey) mcSetItemSilent(scopeKey, value);
  return payload;
}

export async function saveDuplicateReviewDecisions(state, scope = "ALL") {
  const normalizedScope = normalizeScopeValue(scope);
  const globalKey = duplicateReviewDecisionStorageKey("ALL");
  const scopeKey = duplicateReviewDecisionStorageKey(normalizedScope);
  const incoming = {
    acceptedDuplicateKeys: Array.isArray(state?.config?.acceptedDuplicateKeys) ? state.config.acceptedDuplicateKeys : [],
    ignoredInventorySkus: Array.isArray(state?.config?.ignoredInventorySkus) ? state.config.ignoredInventorySkus : [],
    ignoredInventoryKeys: Array.isArray(state?.config?.ignoredInventoryKeys) ? state.config.ignoredInventoryKeys : [],
  };
  // A save is an authoritative replacement, not a union. Union semantics make
  // removals impossible: clearing an ignored SKU/key would immediately merge
  // the stale value back from the previous ledger. Keep the caller's complete
  // decision arrays exactly as supplied and replace both global/scope copies.
  const payload = {
    ...normalizeDuplicateReviewDecisions(incoming),
    updatedAt: new Date().toISOString(),
  };
  const value = JSON.stringify(payload);

  // Finish the durable browser write before React changes the UI. The compact
  // decision ledger has an explicit immediate Firestore writer below, so cache
  // it silently here. Routing the same value through mcSetItem() would schedule
  // a second background cloud write and emit mc-cloud-sync while Review is
  // already handling the user's decision.
  writeDirectDecision("ALL", payload);
  if (normalizedScope !== "ALL") writeDirectDecision(normalizedScope, payload);
  mcSetItemSilent(globalKey, value);
  if (scopeKey !== globalKey) mcSetItemSilent(scopeKey, value);

  const writes = [withMcTimeout(mcSetCloudItemImmediate(globalKey, value), 6000, false)];
  if (scopeKey !== globalKey) writes.push(withMcTimeout(mcSetCloudItemImmediate(scopeKey, value), 6000, false));
  const results = await Promise.all(writes);
  return results.every(Boolean);
}

function withMcTimeout(promise, ms = 4500, fallback = null) {
  return new Promise((resolve) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        resolve(fallback);
      }
    }, ms);
    Promise.resolve(promise)
      .then((value) => {
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          resolve(value);
        }
      })
      .catch(() => {
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          resolve(fallback);
        }
      });
  });
}

async function persistMcCloudItemFast(key, value) {
  return withMcTimeout(mcSetCloudItem(key, value), 3500, false);
}

export const DEFAULT_MAPPING = {
  product: "name",
  category: "product_category",
  cost: "retail_price",
  systemSoh: "",
  sku: "sku",
};

export const DEFAULT_RULES = [];

export const DEFAULT_UNIT_LIBRARY = [
  "Unit",
  "Piece",
  "Pack",
  "Box",
  "Case",
  "Crate",
  "Bottle",
  "Bag",
  "Kg",
  "Gram",
  "Litre",
  "ml",
  "Roll",
  "Pallet",
  "Tray",
  "Meter",
  "Pair",
];

export const DEFAULT_UNIT_RULE = {
  countUnit: "Unit",
  baseUnit: "Unit",
  systemUnitSize: 1,
  priceUnitSize: 1,
  priceBasis: "count",
};

export const DEFAULT_CONFIG = {
  storeName: "ALL",
  lsPerReal: 2,
  categoryOrder: [],
  halfGramCategories: [],
  negativeFlagThreshold: -300,
  positiveFlagThreshold: 300,
  hideCountedInMosPdf: false,
  hideZeroOrderItemsInMosPdf: false,
  categoryGroups: [],
  categoryOwnership: {},
  unitLibrary: DEFAULT_UNIT_LIBRARY,
  defaultUnitRule: DEFAULT_UNIT_RULE,
  categoryUnitRules: {},
};

export const SAMPLE_CSV_TEXT = `id,handle,sku,name,retail_price,product_category,inventory_District_Six
6ef11d,apple-gelato-b-greendoor,10030,Apple Gelato Greendoor - B+ - 0.5g,65,Cannabis - Flower / Greendoor - B+,16
e301fd,banana-dream-aa-indoor,10010,Banana Dream - Indoor - AA - 0.5g,70,Cannabis - Flower / Indoor - AA,10
96da84,blood-orange-vape-cartridge,10529,Blood Orange Vape - Cartridge - Sativa,220,Cannabis - Vapes / Cartridge,9
93584c,blueberry-lemonade-sodaze,10378,Blueberry Lemonade Sodaze - 250ml,25,Sodaze,14
24190c,dank-aaa-pre-roll,10476,Dank AAA Pre Roll,35,Cannabis - Pre-Rolls / Dank,20
9911af,blue-unicorn-aaa-indoor,10024,Blue Unicorn - Indoor - AAA - 0.5g,75,Cannabis - Flower / Indoor - AAA,12`;

const ENGINE_STORAGE_KEY = "mc-logic-pass";
const SHARED_GROUPS_STORAGE_KEY = "mc-shared-category-groups";
const SHARED_OWNERSHIP_STORAGE_KEY = "mc-shared-category-ownership";
const SESSION_SEED_KEY = "mc-session-seed";
const SESSION_COUNTER_KEY = "mc-session-counter";
const SESSION_LIST_KEY = "mc-session-list";
const SESSION_LIST_BACKUP_KEY = "mc-session-list-backup";
const ARCHIVE_LIST_KEY = "mc-session-archive";
const FLAG_CONFIG_KEY = "mc-flag-config";
// 🔐 Storage key helpers (FIX for archive delete + engine lock)
const sessionEngineStorageKeyFor = (sessionId) => `mc-session-engine:${sessionId}`;
const archivedSessionStorageKeyFor = (sessionId) => `mc-archive-session:${sessionId}`;

const legacySessionEngineStorageKeyFor = (sessionId) => `mc-session-engine-${sessionId}`;
export const MC_ENGINE_EVENT = "mc-engine-change";
export const MC_SESSION_EVENT = "mc-session-change";


function updateSessionTransitionOverride(kind = "", sessionId = "") {
  if (typeof window === "undefined") return;
  const cleanKind = String(kind || "").trim();
  const cleanId = String(sessionId || "").trim();
  if (!cleanKind || !cleanId) return;

  const archiveKey = "__mc_session_archive_overrides";
  const restoreKey = "__mc_session_restore_overrides";
  const targetKey = cleanKind === "archive" ? archiveKey : restoreKey;
  const oppositeKey = cleanKind === "archive" ? restoreKey : archiveKey;
  const now = new Date().toISOString();

  const readMap = (key) => {
    try {
      const raw = window.localStorage?.getItem(key) || "{}";
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch (_) {
      return {};
    }
  };

  try {
    const target = readMap(targetKey);
    target[cleanId] = now;
    window.localStorage?.setItem(targetKey, JSON.stringify(target));
  } catch (_) {}

  try {
    const opposite = readMap(oppositeKey);
    if (Object.prototype.hasOwnProperty.call(opposite, cleanId)) {
      delete opposite[cleanId];
      window.localStorage?.setItem(oppositeKey, JSON.stringify(opposite));
    }
  } catch (_) {}
}

function markSessionArchivedForSync(sessionId = "") {
  updateSessionTransitionOverride("archive", sessionId);
}

function markSessionRestoredForSync(sessionId = "") {
  updateSessionTransitionOverride("restore", sessionId);
}




function normalizeScopeValue(scope = "ALL") {
  const raw = String(scope || "ALL").trim();
  if (!raw || raw.toUpperCase() === "ALL") return "ALL";
  return normalizeStoreValue(raw);
}

function scopedStorageKey(baseKey, scope = "ALL") {
  const normalized = normalizeScopeValue(scope);
  return normalized === "ALL" ? baseKey : `${baseKey}:${normalized}`;
}

function sharedConfigStorageKey(baseKey, scope = "ALL") {
  return scopedStorageKey(baseKey, scope);
}

function readSharedConfigOverlay(scope = "ALL") {
  const groupsRaw = mcGetItem(sharedConfigStorageKey(SHARED_GROUPS_STORAGE_KEY, scope));
  const ownershipRaw = mcGetItem(sharedConfigStorageKey(SHARED_OWNERSHIP_STORAGE_KEY, scope));
  let categoryGroups = null;
  let categoryOwnership = null;
  try { categoryGroups = groupsRaw ? JSON.parse(groupsRaw) : null; } catch (_) {}
  try { categoryOwnership = ownershipRaw ? JSON.parse(ownershipRaw) : null; } catch (_) {}
  return {
    categoryGroups: Array.isArray(categoryGroups) ? categoryGroups : null,
    categoryOwnership: categoryOwnership && typeof categoryOwnership === "object" && !Array.isArray(categoryOwnership) ? categoryOwnership : null,
  };
}

function applySharedConfigOverlay(state, scope = "ALL") {
  if (!state || typeof state !== "object") return state;

  const normalizedScope = normalizeScopeValue(scope);
  const scopedConfig = state.config || {};
  const explicit = scopedConfig.configurationScopeOverrides && typeof scopedConfig.configurationScopeOverrides === "object"
    ? scopedConfig.configurationScopeOverrides
    : {};

  // All Sites is the authority. A site only reads its own shared Groups /
  // Ownership record when that exact Inventory Configuration section has an
  // explicit local override. Older builds left site-scoped shared records
  // behind even after the site was returned to inherited mode; applying those
  // records unconditionally made Review appear as if every site had to learn
  // the same new outputs independently.
  const globalOverlay = readSharedConfigOverlay("ALL");
  const siteOverlay = normalizedScope === "ALL" ? globalOverlay : readSharedConfigOverlay(normalizedScope);
  const groupsOverlay = normalizedScope === "ALL"
    ? globalOverlay.categoryGroups
    : (explicit.groups === true ? siteOverlay.categoryGroups : globalOverlay.categoryGroups);
  const ownershipOverlay = normalizedScope === "ALL"
    ? globalOverlay.categoryOwnership
    : (explicit.ownership === true ? siteOverlay.categoryOwnership : globalOverlay.categoryOwnership);

  if (!groupsOverlay && !ownershipOverlay) return state;
  return {
    ...state,
    config: {
      ...scopedConfig,
      ...(groupsOverlay ? { categoryGroups: groupsOverlay } : {}),
      ...(ownershipOverlay ? { categoryOwnership: ownershipOverlay } : {}),
    },
  };
}

export function saveSharedCategoryGroups(groups = [], scope = "ALL") {
  const cleanGroups = Array.isArray(groups) ? groups : [];
  mcSetItem(sharedConfigStorageKey(SHARED_GROUPS_STORAGE_KEY, scope), JSON.stringify(cleanGroups));
  dispatchMcEvent(MC_ENGINE_EVENT, { scope: normalizeScopeValue(scope), sharedConfig: "groups" });
}

export function saveSharedCategoryOwnership(ownership = {}, scope = "ALL") {
  const cleanOwnership = ownership && typeof ownership === "object" && !Array.isArray(ownership) ? ownership : {};
  mcSetItem(sharedConfigStorageKey(SHARED_OWNERSHIP_STORAGE_KEY, scope), JSON.stringify(cleanOwnership));
  dispatchMcEvent(MC_ENGINE_EVENT, { scope: normalizeScopeValue(scope), sharedConfig: "ownership" });
}

function scopeFromScopedKey(key, baseKey) {
  if (key === baseKey) return "ALL";
  if (key.startsWith(`${baseKey}:`)) return key.slice(baseKey.length + 1) || "ALL";
  return null;
}

function listScopedPayloads(baseKey) {
  if (typeof window === "undefined") return [];
  const out = [];
  const scanStore = (store, source = "storage") => {
    if (!store) return;
    for (let index = 0; index < store.length; index += 1) {
      const key = store.key(index) || "";
      const scope = scopeFromScopedKey(key, baseKey);
      if (scope === null) continue;
      try {
        const raw = store.getItem(key);
        if (!raw) continue;
        out.push({ key, scope, source, payload: JSON.parse(raw) });
      } catch (err) {
        console.error(`Failed to parse scoped payload for ${key}`, err);
      }
    }
  };
  try { scanStore(window.localStorage, "localStorage"); } catch (_) {}
  try { scanStore(window.sessionStorage, "sessionStorage"); } catch (_) {}
  return out;
}

const CATEGORY_BACKUP_KEY = "mc-category-settings-backup";
const UNIT_INTELLIGENCE_KEY = "mc-unit-intelligence-config";

function categoryBackupKey(scope = "ALL") {
  return scopedStorageKey(CATEGORY_BACKUP_KEY, scope);
}

function listCategoryBackups() {
  return listScopedPayloads(CATEGORY_BACKUP_KEY)
    .map((item) => ({ ...item, payload: item.payload && typeof item.payload === "object" ? item.payload : null }))
    .filter((item) => item.payload && engineHasCategorySetup({ rules: item.payload.rules || [], config: item.payload.config || {} }));
}

function buildCategoryBackup(state = {}, scope = "ALL") {
  if (!engineHasCategorySetup(state)) return null;
  return {
    savedAt: new Date().toISOString(),
    scope: normalizeScopeValue(scope),
    rules: Array.isArray(state.rules) ? state.rules : [],
    config: extractCategoryConfig(state.config || {}),
  };
}

function saveCategoryBackup(state = {}, scope = "ALL") {
  if (typeof window === "undefined") return null;
  const backup = buildCategoryBackup(state, scope);
  if (!backup) return null;
  try { mcSetItem(categoryBackupKey(scope), JSON.stringify(backup)); } catch (_) {}
  return backup;
}

export async function saveCategoryBackupDurable(state = {}, scope = "ALL") {
  const backup = buildCategoryBackup(state, scope);
  if (!backup) return false;

  // Durable category saves already have an explicit immediate Firestore path.
  // Cache silently here so we do not also queue the exact same compact payload
  // through the generic mcSetItem background writer.
  const key = categoryBackupKey(scope);
  try { mcSetItemSilent(key, JSON.stringify(backup)); } catch (_) {}
  return mcSetCloudItemImmediate(key, JSON.stringify(backup));
}

function parseCategoryBackup(raw) {
  try {
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (_) {
    return null;
  }
}

function categoryBackupTimestamp(backup = null) {
  const time = Date.parse(String(backup?.savedAt || backup?.updatedAt || ""));
  return Number.isFinite(time) ? time : 0;
}

function newestCategoryBackup(cloudRaw, localRaw) {
  const cloud = parseCategoryBackup(cloudRaw);
  const local = parseCategoryBackup(localRaw);
  if (!cloud) return local;
  if (!local) return cloud;
  return categoryBackupTimestamp(cloud) >= categoryBackupTimestamp(local) ? cloud : local;
}

export async function hydrateCategoryConfigurationFromCloud(scope = "ALL") {
  const normalizedScope = normalizeScopeValue(scope);
  const globalKey = categoryBackupKey("ALL");
  const scopedKey = categoryBackupKey(normalizedScope);

  const [globalCloudRaw, scopedCloudRaw] = await Promise.all([
    withMcTimeout(mcGetCloudItem(globalKey), 5000, null),
    scopedKey === globalKey
      ? Promise.resolve(null)
      : withMcTimeout(mcGetCloudItem(scopedKey), 5000, null),
  ]);

  const globalBackup = newestCategoryBackup(globalCloudRaw, mcGetItem(globalKey));
  const scopedBackup = scopedKey === globalKey
    ? globalBackup
    : newestCategoryBackup(scopedCloudRaw, mcGetItem(scopedKey));

  // Cloud hydration is read-only from the browser's point of view. Never route
  // hydrated values back through mcSetItem(), otherwise opening Review/Config
  // can create a write echo similar to the old Review decision feedback loop.
  if (globalBackup) {
    try { mcSetItemSilent(globalKey, JSON.stringify(globalBackup)); } catch (_) {}
  }
  if (scopedKey !== globalKey && scopedBackup) {
    try { mcSetItemSilent(scopedKey, JSON.stringify(scopedBackup)); } catch (_) {}
  }

  return { global: globalBackup, scoped: scopedBackup };
}

export async function saveCategoryConfigurationDurable(state = {}, scope = "ALL") {
  const normalizedScope = normalizeScopeValue(scope);
  const backup = buildCategoryBackup(state, normalizedScope);
  if (!backup) return false;

  const writes = [];
  const backupKey = categoryBackupKey(normalizedScope);
  const backupValue = JSON.stringify(backup);
  try { mcSetItemSilent(backupKey, backupValue); } catch (_) {}
  writes.push(mcSetCloudItemImmediate(backupKey, backupValue));

  const groups = Array.isArray(state?.config?.categoryGroups) ? state.config.categoryGroups : [];
  const groupsKey = sharedCategoryGroupsStorageKeyForScope(normalizedScope);
  const groupsValue = JSON.stringify(groups);
  try { mcSetItemSilent(groupsKey, groupsValue); } catch (_) {}
  writes.push(mcSetCloudItemImmediate(groupsKey, groupsValue));

  const ownership = state?.config?.categoryOwnership && typeof state.config.categoryOwnership === "object" && !Array.isArray(state.config.categoryOwnership)
    ? state.config.categoryOwnership
    : {};
  const ownershipKey = sharedCategoryOwnershipStorageKeyForScope(normalizedScope);
  const ownershipValue = JSON.stringify(ownership);
  try { mcSetItemSilent(ownershipKey, ownershipValue); } catch (_) {}
  writes.push(mcSetCloudItemImmediate(ownershipKey, ownershipValue));

  const results = await Promise.all(writes);
  dispatchMcEvent(MC_ENGINE_EVENT, { scope: normalizedScope, sharedConfig: "category-configuration" });
  return results.every((saved) => saved === true);
}

function applyLatestCategoryBackup(state = {}, scope = "ALL") {
  try {
    const raw = mcGetItem(categoryBackupKey(scope));
    if (!raw) return state;
    const backup = JSON.parse(raw);
    if (!backup || typeof backup !== "object") return state;
    return {
      ...state,
      rules: Array.isArray(backup.rules) ? backup.rules : (state.rules || []),
      config: {
        ...(state.config || {}),
        ...(backup.config || {}),
        storeName: state.config?.storeName,
      },
    };
  } catch (_) {
    return state;
  }
}


function storeNameFromScope(scope = "ALL") {
  const normalized = normalizeScopeValue(scope);
  return normalized === "ALL" ? DEFAULT_CONFIG.storeName : labelFromInventoryHeader(normalized);
}

function storeKeyFromScope(scope = "ALL") {
  const normalized = normalizeScopeValue(scope);
  return normalized === "ALL" ? buildStoreInventoryHeader(DEFAULT_CONFIG.storeName) : normalized;
}

export function listEngineScopes() {
  if (typeof window === "undefined") return ["ALL"];
  const scopes = new Set(["ALL"]);
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index) || "";
    if (key.startsWith(`${ENGINE_STORAGE_KEY}:`)) {
      scopes.add(key.slice(`${ENGINE_STORAGE_KEY}:`.length));
    }
  }
  return Array.from(scopes);
}

export function buildEmptyState(scope = "ALL") {
  const storeName = storeNameFromScope(scope);
  return recomputeEngineState({
    csvText: "",
    mapping: { ...DEFAULT_MAPPING, systemSoh: storeKeyFromScope(scope) },
    rules: DEFAULT_RULES,
    config: { ...DEFAULT_CONFIG, storeName },
    fileName: "",
  }, { forceInventoryRemap: false });
}

export function saveSharedLogicAcrossScopes(nextState = {}) {
  const scopes = listEngineScopes();
  const sourceConfig = nextState.config || {};
  const categoryConfig = extractCategoryConfig(sourceConfig);
  const sourceRules = Array.isArray(nextState.rules) && nextState.rules.length ? nextState.rules : DEFAULT_RULES;

  scopes.forEach((scope) => {
    const current = loadEngineState(scope) || buildEmptyState(scope);
    const storeName = scope === "ALL" ? (current.config?.storeName || DEFAULT_CONFIG.storeName) : storeNameFromScope(scope);
    const merged = recomputeEngineState({
      ...current,
      rules: sourceRules,
      config: {
        ...(current.config || {}),
        ...categoryConfig,
        storeName,
        categoryOverride: false,
      },
      mapping: {
        ...(current.mapping || DEFAULT_MAPPING),
        systemSoh: scope === "ALL" ? (current.mapping?.systemSoh || DEFAULT_MAPPING.systemSoh) : storeKeyFromScope(scope),
      },
    }, { forceInventoryRemap: scope !== "ALL" });
    saveEngineState(merged, scope);
  });

  return loadEngineState("ALL") || buildEmptyState("ALL");
}
function dispatchMcEvent(name, detail = null) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

/**
 * Parse RFC 4180-style CSV, including quoted fields containing commas,
 * escaped quotes and embedded CR/LF newlines. The previous implementation
 * split the source into physical lines before parsing, which turned a single
 * Lightspeed product with a multiline HTML description into several fake
 * inventory rows and shifted its SOH/price columns to zero.
 */
export function parseCsv(text) {
  const source = String(text || "").replace(/^\uFEFF/, "");
  if (!source.trim()) return { headers: [], rows: [], errors: [] };

  const records = [];
  const errors = [];
  let record = [];
  let field = "";
  let inQuotes = false;
  let rowNumber = 1;

  const commitField = () => {
    record.push(field);
    field = "";
  };
  const commitRecord = () => {
    commitField();
    const isEmpty = record.every((value) => String(value || "").trim() === "");
    if (!isEmpty) records.push(record);
    record = [];
  };

  for (let i = 0; i < source.length; i += 1) {
    const ch = source[i];

    if (inQuotes) {
      if (ch === '"') {
        if (source[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        // Preserve embedded newlines inside quoted descriptions. They belong
        // to the field and must never create a new product record.
        field += ch;
        if (ch === "\n") rowNumber += 1;
      }
      continue;
    }

    if (ch === '"' && field.length === 0) {
      inQuotes = true;
    } else if (ch === ",") {
      commitField();
    } else if (ch === "\n") {
      commitRecord();
      rowNumber += 1;
    } else if (ch === "\r") {
      // Treat CRLF as one record boundary and support lone CR exports too.
      if (source[i + 1] === "\n") i += 1;
      commitRecord();
      rowNumber += 1;
    } else {
      field += ch;
    }
  }

  if (inQuotes) {
    errors.push({ row: rowNumber, code: "UNCLOSED_QUOTE", message: "CSV ended inside a quoted field." });
  }
  if (field.length || record.length) commitRecord();
  if (!records.length) return { headers: [], rows: [], errors };

  const headers = records[0].map((header, index) => {
    const value = String(header ?? "").trim();
    return value || `column_${index + 1}`;
  });

  const body = records.slice(1).map((values, index) => {
    if (values.length !== headers.length) {
      errors.push({
        row: index + 2,
        code: "COLUMN_COUNT_MISMATCH",
        expected: headers.length,
        actual: values.length,
        message: `Expected ${headers.length} columns but parsed ${values.length}.`,
      });
    }

    const obj = {};
    headers.forEach((header, columnIndex) => {
      obj[header] = values[columnIndex] ?? "";
    });
    return obj;
  });

  return { headers, rows: body, errors };
}

export function basicNorm(value) {
  return String(value || "").toLowerCase().replace(/\u00A0/g, " ").replace(/\s+/g, " ").trim();
}

function normalizeExactValue(value) {
  return String(value ?? "")
    .toLowerCase()
    .replace(/\u00A0/g, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeExactNumber(value) {
  const n = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? String(Math.round(n * 10000) / 10000) : "0";
}

function firstPresent(row = {}, keys = []) {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return "";
}

function rowVariantSuffix(row = {}) {
  const values = [
    firstPresent(row, ["variant_option_one_value", "option_one_value", "option1", "size", "Size"]),
    firstPresent(row, ["variant_option_two_value", "option_two_value", "option2", "colour", "color", "Colour", "Color"]),
    firstPresent(row, ["variant_option_three_value", "option_three_value", "option3"]),
  ]
    .map((v) => String(v || "").trim())
    .filter(Boolean);

  if (Array.isArray(row?.options)) {
    row.options.forEach((option) => {
      const v = option?.value || option?.name || option?.label;
      if (v && !values.includes(String(v).trim())) values.push(String(v).trim());
    });
  }

  return values.join(" / ");
}

function looksLikeProductDescription(value) {
  const text = String(value || "").trim();
  if (!text) return false;
  const htmlLike = /<\/?[a-z][^>]*>|&(?:nbsp|amp|lt|gt|quot);/i.test(text);
  const sentenceLike = text.length > 140 || (text.length > 80 && /[.!?]\s/.test(text));
  return htmlLike || sentenceLike;
}

function cleanProductLabel(value) {
  return String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/\s+/g, " ")
    .trim();
}

export function displayProductNameFromRow(row = {}, mapping = DEFAULT_MAPPING) {
  // Mapping remains authoritative only when the mapped value is actually a
  // product label. Some Lightspeed exports occasionally place rich HTML copy in
  // a nominal name/display field; those values must never become count labels.
  const mappedProductHeader = String(mapping?.product || "").trim();
  const mappedProduct = mappedProductHeader ? firstPresent(row, [mappedProductHeader]) : "";
  const conciseCandidates = [
    mappedProduct,
    firstPresent(row, ["name"]),
    firstPresent(row, ["product"]),
    firstPresent(row, ["product_name"]),
    firstPresent(row, ["title"]),
    firstPresent(row, ["variant_name"]),
    firstPresent(row, ["composite_name"]),
  ];
  let selectedBase = conciseCandidates.find((value) => value && !looksLikeProductDescription(value)) || "";
  if (!selectedBase) {
    const fallback = conciseCandidates.find(Boolean) || "";
    selectedBase = cleanProductLabel(fallback).slice(0, 120);
  }
  const suffix = rowVariantSuffix(row);
  if (selectedBase && suffix && !normalizeExactValue(selectedBase).includes(normalizeExactValue(suffix))) {
    return `${String(selectedBase).trim()} / ${suffix}`;
  }
  return String(selectedBase || "").trim();
}

function duplicateMergeKey(row = {}, mapping = DEFAULT_MAPPING) {
  const product = displayProductNameFromRow(row, mapping);
  const sku = firstPresent(row, [mapping.sku, "sku", "variant_sku", "composite_sku", "code", "barcode"]);
  const category = firstPresent(row, [mapping.category, "category", "product_category"]);
  const cost = firstPresent(row, [mapping.cost, "cost", "retail_price", "supply_price", "price"]);
  const soh = firstPresent(row, [mapping.systemSoh, "systemSoh", "system_soh", "soh", "inventory", "inventory_Main_Outlet"]);
  return [product, sku, category, normalizeExactNumber(cost), normalizeExactNumber(soh)]
    .map(normalizeExactValue)
    .join("::");
}

function scoreHeader(header, keywords = [], excludes = []) {
  const norm = basicNorm(header);
  let score = 0;
  keywords.forEach((keyword) => {
    if (norm.includes(keyword)) score += keyword.length;
  });
  excludes.forEach((keyword) => {
    if (norm.includes(keyword)) score -= keyword.length * 2;
  });
  return score;
}

function bestHeader(headers, keywords, excludes = []) {
  let winner = "";
  let best = -9999;
  headers.forEach((header) => {
    const score = scoreHeader(header, keywords, excludes);
    if (score > best) {
      best = score;
      winner = header;
    }
  });
  return best > 0 ? winner : "";
}

function buildStoreInventoryHeader(storeName = "District_Six") {
  const compact = String(storeName || "District_Six").trim().replace(/\s+/g, "_");
  return `inventory_${compact}`;
}

function siteCompareKey(value = "") {
  return labelFromInventoryHeader(value).toLowerCase().replace(/[^a-z0-9]/g, "");
}

function bestInventoryHeaderForStore(headers = [], storeNameOrScope = "") {
  const inventoryHeaders = (headers || []).filter((header) => isInventoryHeader(header));
  if (!inventoryHeaders.length) return "";

  const desiredInventoryHeader = buildStoreInventoryHeader(storeNameOrScope || DEFAULT_CONFIG.storeName);
  const exactDesired = inventoryHeaders.find((header) => basicNorm(header) === basicNorm(desiredInventoryHeader));
  if (exactDesired) return exactDesired;

  const site = resolveSite(storeNameOrScope || desiredInventoryHeader, headers);
  const candidates = Array.from(new Set([
    storeNameOrScope,
    desiredInventoryHeader,
    site?.siteId,
    site?.name,
    site?.storeKey,
    ...(site?.aliases || []),
  ].filter(Boolean)));

  const candidateKeys = candidates.map(siteCompareKey).filter(Boolean);

  const byResolvedSite = inventoryHeaders.find((header) => {
    const headerSite = resolveSite(header, headers);
    return site?.siteId && headerSite?.siteId && headerSite.siteId === site.siteId;
  });
  if (byResolvedSite) return byResolvedSite;

  const byFuzzyName = inventoryHeaders.find((header) => {
    const headerKey = siteCompareKey(header);
    return candidateKeys.some((candidateKey) => {
      if (!candidateKey || !headerKey) return false;
      return headerKey === candidateKey || headerKey.includes(candidateKey) || candidateKey.includes(headerKey);
    });
  });
  if (byFuzzyName) return byFuzzyName;

  return "";
}

function labelFromInventoryHeader(header = "inventory_District_Six") {
  const clean = String(header || "")
    .replace(/^inventory_/i, "")
    .replace(/^outlet_tax_/i, "")
    .replace(/_Store$/i, "")
    .trim();
  if (!clean) return DEFAULT_CONFIG.storeName;
  return clean
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

const UNIVERSAL_FIELD_ALIASES = {
  product: ["product", "product name", "item", "item name", "description", "title", "name"],
  category: ["category", "product category", "department", "group", "product group", "type"],
  cost: ["retail price", "selling price", "sale price", "price", "cost", "unit cost", "supply price", "purchase price", "wholesale price"],
  systemSoh: ["soh", "stock on hand", "stock", "quantity on hand", "qty on hand", "quantity", "qty", "available stock", "inventory"],
  sku: ["sku", "product sku", "item code", "product code", "code", "variant sku"],
  site: ["site", "store", "location", "branch", "warehouse", "outlet"],
  barcode: ["barcode", "ean", "upc", "gtin"],
  brand: ["brand", "manufacturer"],
  supplier: ["supplier", "vendor"],
  price: ["selling price", "sale price", "price", "retail price"],
  unit: ["unit", "uom", "unit of measure", "measure"],
};

function normalizedHeaderToken(value = "") {
  return basicNorm(value).replace(/[_-]+/g, " ").replace(/[^a-z0-9 ]/g, "").replace(/\s+/g, " ").trim();
}

function headerAliasScore(header = "", aliases = []) {
  const token = normalizedHeaderToken(header);
  if (!token) return 0;
  let best = 0;
  aliases.forEach((alias, index) => {
    const wanted = normalizedHeaderToken(alias);
    if (!wanted) return;
    if (token === wanted) best = Math.max(best, 100 - index);
    else if (token.startsWith(`${wanted} `) || token.endsWith(` ${wanted}`)) best = Math.max(best, 82 - index);
    else if (token.includes(wanted)) best = Math.max(best, 62 - index);
  });
  return best;
}

function sampleColumnValues(rows = [], header = "", limit = 30) {
  return (Array.isArray(rows) ? rows : [])
    .slice(0, limit)
    .map((row) => row?.[header])
    .filter((value) => value !== undefined && value !== null && String(value).trim() !== "");
}

function valueShapeScore(field = "", values = []) {
  if (!values.length) return 0;
  const numeric = values.filter((value) => Number.isFinite(Number(String(value).replace(/[^0-9.-]/g, "")))).length / values.length;
  const unique = new Set(values.map((value) => basicNorm(value))).size / values.length;
  if (["cost", "price", "systemSoh"].includes(field)) return numeric >= .8 ? 18 : numeric >= .5 ? 8 : -12;
  if (field === "site") return unique <= .65 ? 14 : unique <= .9 ? 6 : -4;
  if (field === "sku" || field === "barcode") return unique >= .8 ? 12 : 2;
  if (field === "product") return unique >= .6 ? 10 : 3;
  return 4;
}

function detectField(headers = [], rows = [], field = "") {
  let winner = "";
  let score = 0;
  (headers || []).forEach((header) => {
    let candidate = headerAliasScore(header, UNIVERSAL_FIELD_ALIASES[field] || []);
    candidate += valueShapeScore(field, sampleColumnValues(rows, header));
    if (field === "systemSoh" && isInventoryHeader(header)) candidate += 35;
    if (field === "site" && isInventoryHeader(header)) candidate -= 50;
    if (candidate > score) { score = candidate; winner = header; }
  });
  const confidence = score >= 95 ? "high" : score >= 70 ? "medium" : score >= 45 ? "low" : "unresolved";
  return { header: score >= 45 ? winner : "", score, confidence };
}

export function detectUniversalImportProfile(headers = [], rows = [], preferredStoreName = "") {
  const fields = {};
  Object.keys(UNIVERSAL_FIELD_ALIASES).forEach((field) => { fields[field] = detectField(headers, rows, field); });
  const inventoryHeaders = (headers || []).filter((header) => isInventoryHeader(header));
  const detectedRowSiteHeader = fields.site?.header || "";
  const detectedRowSiteToken = normalizedHeaderToken(detectedRowSiteHeader);
  const rowSiteLooksLikeTax = /(^| )(tax|vat)( |$)/i.test(detectedRowSiteToken) || /^outlet[ _-]*tax/i.test(String(detectedRowSiteHeader || "").trim());
  // Multi-site column exports are authoritative when they expose multiple
  // inventory_<site> columns. A header such as outlet_tax_Default_Tax can score
  // as a false Site match because it contains the word "outlet"; allowing that
  // to win makes All Sites SOH collapse into a fake "Default Tax" site.
  const rowSiteLooksLikeInventory = inventoryHeaders.includes(detectedRowSiteHeader);
  const rowSiteHeader = (rowSiteLooksLikeTax || rowSiteLooksLikeInventory) ? "" : detectedRowSiteHeader;
  const layout = inventoryHeaders.length > 1
    ? "column_site"
    : rowSiteHeader
      ? "row_site"
      : inventoryHeaders.length
        ? "column_site"
        : "single_site";
  const required = ["product", "systemSoh"];
  const unresolvedRequired = required.filter((field) => !fields[field]?.header);
  if (layout === "row_site" && !rowSiteHeader) unresolvedRequired.push("site");
  return {
    version: 1,
    detectedAt: new Date().toISOString(),
    preferredStoreName: preferredStoreName || "",
    layout,
    rowSiteHeader,
    inventoryHeaders,
    fields,
    required,
    unresolvedRequired: Array.from(new Set(unresolvedRequired)),
    readyForNormalization: unresolvedRequired.length === 0,
    headers: [...(headers || [])],
    samples: Object.fromEntries((headers || []).map((header) => [header, sampleColumnValues(rows, header, 5)])),
  };
}

export function detectBestMapping(headers = [], preferredStoreName = "District_Six", rows = []) {
  const profile = detectUniversalImportProfile(headers, rows, preferredStoreName);
  const storeInventoryHeader = buildStoreInventoryHeader(preferredStoreName);
  const legacyInventoryHeader =
    headers.find((h) => basicNorm(h) === basicNorm(storeInventoryHeader)) ||
    headers.find((h) => basicNorm(h).includes(basicNorm(storeInventoryHeader))) ||
    bestHeader(headers, ["inventory_"], ["restock", "reorder", "tax"]);

  return {
    product: profile.fields.product.header || bestHeader(headers, ["name"], ["option", "brand", "supplier", "composite"]),
    category: profile.fields.category.header || bestHeader(headers, ["product_category", "category"], ["map"]),
    cost: profile.fields.cost.header || bestHeader(headers, ["retail_price", "price", "cost"], ["loyalty", "purchase"]),
    systemSoh: profile.fields.systemSoh.header || legacyInventoryHeader,
    sku: profile.fields.sku.header || bestHeader(headers, ["sku"], ["composite"]),
    site: profile.rowSiteHeader || "",
    barcode: profile.fields.barcode.header || "",
    brand: profile.fields.brand.header || "",
    supplier: profile.fields.supplier.header || "",
    price: profile.fields.price.header || "",
    unit: profile.fields.unit.header || "",
  };
}

export const UNMAPPED_CATEGORY = "__unmapped__";

export function listNormalizedCategories(rules = DEFAULT_RULES) {
  const out = [];
  const seen = new Set();
  (rules || []).forEach((rule) => {
    const value = basicNorm(rule.output);
    if (!value || seen.has(value)) return;
    seen.add(value);
    out.push(value);
  });
  return out;
}

export function normalizeCategory(product, category, rules = DEFAULT_RULES) {
  const productNorm = basicNorm(product);
  const categoryNorm = basicNorm(category);
  const combined = `${productNorm} | ${categoryNorm}`;

  // A product can legitimately match several learned rules (for example
  // "Durban Poison Vape - Dab - Cartridge" matches "vape", "dab" and
  // "cartridge"). Rule array order must not decide the result. Resolve all
  // matches by intent first, then specificity:
  //   equals > starts_with > contains; longer match values beat shorter ones.
  // This lets "cartridge" override generic "vape" / "dab" rules while
  // preserving explicit exact rules as the strongest source of truth.
  let bestMatch = null;

  const consider = (rule, target, targetPriority = 0) => {
    const val = basicNorm(rule?.value);
    const output = basicNorm(rule?.output);
    const type = String(rule?.type || "").trim().toLowerCase();
    if (!val || !output || !target) return;

    let typePriority = 0;
    let matched = false;
    if (type === "equals") {
      matched = target === val;
      typePriority = 3;
    } else if (type === "starts_with" || type === "startswith") {
      matched = target.startsWith(val);
      typePriority = 2;
    } else if (type === "contains") {
      matched = target.includes(val);
      typePriority = 1;
    }
    if (!matched) return;

    const candidate = { rule, typePriority, specificity: val.length, targetPriority };
    if (!bestMatch
      || candidate.typePriority > bestMatch.typePriority
      || (candidate.typePriority === bestMatch.typePriority && candidate.specificity > bestMatch.specificity)
      || (candidate.typePriority === bestMatch.typePriority
        && candidate.specificity === bestMatch.specificity
        && candidate.targetPriority > bestMatch.targetPriority)) {
      bestMatch = candidate;
    }
  };

  for (const rule of rules || []) {
    // Prefer a direct source-category match only when rule type and specificity
    // are otherwise tied. Product-name specificity still wins normally.
    consider(rule, combined, 0);
    consider(rule, categoryNorm, 1);
  }

  return bestMatch ? basicNorm(bestMatch.rule.output) : UNMAPPED_CATEGORY;
}

function numericUnitValue(value, fallback = 1) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function normalizeUnitName(value = "Unit") {
  const clean = String(value || "").trim();
  return clean || "Unit";
}

function inferUnitSizeFromProductName(product = "") {
  const text = String(product || "").toLowerCase();
  const match = text.match(/(?:^|[^0-9])([0-9]+(?:[.,][0-9]+)?)\s*(kg|g|gram|grams|l|litre|liter|litres|liters|ml|pack|pcs|pieces|piece|box|case|crate|bottle|bag)(?:\b|[^a-z])/i);
  if (!match) return null;
  const amount = Number(String(match[1] || "").replace(",", "."));
  if (!Number.isFinite(amount) || amount <= 0) return null;
  const rawUnit = String(match[2] || "").toLowerCase();
  const unit = rawUnit === "g" || rawUnit.startsWith("gram") ? "Gram"
    : rawUnit === "kg" ? "Kg"
    : rawUnit === "l" || rawUnit.startsWith("lit") ? "Litre"
    : rawUnit === "ml" ? "ml"
    : rawUnit.startsWith("pack") ? "Pack"
    : rawUnit.startsWith("pcs") || rawUnit.startsWith("piece") ? "Piece"
    : rawUnit.startsWith("box") ? "Box"
    : rawUnit.startsWith("case") ? "Case"
    : rawUnit.startsWith("crate") ? "Crate"
    : rawUnit.startsWith("bottle") ? "Bottle"
    : rawUnit.startsWith("bag") ? "Bag"
    : "Unit";
  return { amount, unit };
}

function normalizeUnitRule(rule = {}, fallback = DEFAULT_UNIT_RULE) {
  return {
    countUnit: normalizeUnitName(rule.countUnit || fallback.countUnit || "Unit"),
    baseUnit: normalizeUnitName(rule.baseUnit || fallback.baseUnit || "Unit"),
    systemUnitSize: numericUnitValue(rule.systemUnitSize ?? fallback.systemUnitSize, 1),
    priceUnitSize: numericUnitValue(rule.priceUnitSize ?? fallback.priceUnitSize, 1),
    priceBasis: String(rule.priceBasis || fallback.priceBasis || "count") === "base" ? "base" : "count",
    autoDetectSize: rule.autoDetectSize === true,
  };
}

export function resolveUnitRuleForPreparedRow(product = "", category = "", config = DEFAULT_CONFIG) {
  const safeConfig = { ...DEFAULT_CONFIG, ...(config || {}) };
  const defaultRule = normalizeUnitRule(safeConfig.defaultUnitRule || DEFAULT_UNIT_RULE, DEFAULT_UNIT_RULE);
  const categoryRules = safeConfig.categoryUnitRules && typeof safeConfig.categoryUnitRules === "object" && !Array.isArray(safeConfig.categoryUnitRules)
    ? safeConfig.categoryUnitRules
    : {};
  const categoryKey = basicNorm(category);
  const categoryOverride = categoryRules[categoryKey];
  const hasCategoryRule = !!categoryOverride;
  let rule = {
    ...normalizeUnitRule(categoryOverride || {}, defaultRule),
    ruleSource: hasCategoryRule ? "category" : "default",
    ruleCategory: hasCategoryRule ? category : "",
  };
  const shouldAutoDetect = hasCategoryRule
    ? categoryOverride.autoDetectSize === true
    : defaultRule.autoDetectSize === true;

  // Response Designed rule:
  // Unit Intelligence must never be applied just because an old/legacy
  // halfGramCategories or conversionCategories list exists in storage.
  // No explicit categoryUnitRule + Auto Detect OFF = no conversion.
  // Legacy fields are intentionally ignored here so prepared data and live
  // sessions only transform quantities when the admin configures a unit rule
  // or intentionally enables Auto Detect.

  if (shouldAutoDetect) {
    const inferred = inferUnitSizeFromProductName(product);
    if (inferred) {
      rule = {
        ...rule,
        baseUnit: inferred.unit,
        systemUnitSize: inferred.amount,
        priceUnitSize: inferred.amount,
        priceBasis: "count",
        detectedSize: inferred.amount,
        detectedUnit: inferred.unit,
        autoDetected: true,
        ruleSource: hasCategoryRule ? "category" : "default",
        ruleCategory: hasCategoryRule ? category : "",
      };
    }
  }

  return rule;
}

function buildUnitMetaForPreparedRow(product = "", category = "", cost = 0, systemSoh = 0, config = DEFAULT_CONFIG) {
  const rule = resolveUnitRuleForPreparedRow(product, category, config);
  const realSoh = Number(systemSoh || 0) * Number(rule.systemUnitSize || 1);
  const effectiveCost = rule.priceBasis === "base"
    ? Number(cost || 0)
    : Number(cost || 0) / numericUnitValue(rule.priceUnitSize, 1);
  const isConverted = Number(rule.systemUnitSize || 1) !== 1 || Number(rule.priceUnitSize || 1) !== 1 || rule.priceBasis === "base";
  const conversionLabel = isConverted
    ? `${rule.countUnit} → ${rule.systemUnitSize} ${rule.baseUnit}; price ${rule.priceBasis === "base" ? `per ${rule.baseUnit}` : `per ${rule.priceUnitSize} ${rule.baseUnit}`}`
    : "";
  return {
    realSoh,
    effectiveCost,
    sourceCost: Number(cost || 0),
    countUnit: rule.countUnit,
    baseUnit: rule.baseUnit,
    systemUnitSize: rule.systemUnitSize,
    priceUnitSize: rule.priceUnitSize,
    priceBasis: rule.priceBasis,
    isConverted,
    conversionLabel,
    autoDetected: rule.autoDetected === true,
    detectedSize: rule.detectedSize || null,
    detectedUnit: rule.detectedUnit || null,
    unitRuleSource: rule.ruleSource || "default",
    unitRuleCategory: rule.ruleCategory || "",
  };
}


export function buildDuplicateMergeSummary(rawRows = [], mapping = DEFAULT_MAPPING) {
  const groups = new Map();
  (rawRows || []).forEach((row, index) => {
    const product = displayProductNameFromRow(row, mapping);
    if (!String(product || "").trim()) return;
    const sku = firstPresent(row, [mapping.sku, "sku", "variant_sku", "composite_sku", "code", "barcode"]);
    const key = duplicateMergeKey(row, mapping);
    if (!key.replace(/:/g, "")) return;
    if (!groups.has(key)) {
      groups.set(key, {
        product,
        sku,
        rowsFound: 0,
        merged: 0,
        firstRow: index,
      });
    }
    const group = groups.get(key);
    group.rowsFound += 1;
    group.merged = Math.max(0, group.rowsFound - 1);
    if (!group.sku && sku) group.sku = sku;
  });

  const items = Array.from(groups.values())
    .filter((item) => item.rowsFound > 1)
    .sort((a, b) => a.firstRow - b.firstRow)
    .map(({ firstRow, ...item }) => ({ ...item, duplicateKey: `${basicNorm(item.sku || "")}::${basicNorm(item.product || "")}` }));

  return {
    count: items.reduce((sum, item) => sum + item.merged, 0),
    items,
  };
}

export function buildPreparedData(rawRows, mapping = DEFAULT_MAPPING, config = DEFAULT_CONFIG, rules = DEFAULT_RULES) {
  const safeMapping = { ...DEFAULT_MAPPING, ...(mapping || {}) };
  const safeConfig = { ...DEFAULT_CONFIG, ...(config || {}) };
  const categoryRank = {};
  (safeConfig.categoryOrder || []).forEach((item, index) => {
    categoryRank[basicNorm(item)] = index;
  });
  const seenProducts = new Set();

  const prepared = (Array.isArray(rawRows) ? rawRows : [])
    .filter((row) => row && typeof row === "object")
    .map((row, index) => {
      const product = displayProductNameFromRow(row, safeMapping);
      const categoryRaw = row[safeMapping.category] || row.category || row.product_category || "";
      const sourceCategory = basicNorm(categoryRaw);
      const category = normalizeCategory(product, categoryRaw, rules);
      const cost = Number(row[safeMapping.cost] ?? row.cost ?? row.retail_price ?? 0);
      const systemSoh = Number(row[safeMapping.systemSoh] ?? row.systemSoh ?? row.system_soh ?? row.soh ?? 0);
      const unitMeta = buildUnitMetaForPreparedRow(product, category, cost, systemSoh, safeConfig);

      return {
        id: row.id || row[safeMapping.sku] || `row-${index + 1}`,
        product,
        sku: row[safeMapping.sku] || "",
        category,
        sourceCategory,
        isUnmappedCategory: category === UNMAPPED_CATEGORY,
        cost: unitMeta.effectiveCost,
        sourceCost: unitMeta.sourceCost,
        systemSoh,
        realSoh: unitMeta.realSoh,
        countUnit: unitMeta.countUnit,
        baseUnit: unitMeta.baseUnit,
        systemUnitSize: unitMeta.systemUnitSize,
        priceUnitSize: unitMeta.priceUnitSize,
        priceBasis: unitMeta.priceBasis,
        effectiveCost: unitMeta.effectiveCost,
        isConverted: unitMeta.isConverted,
        conversionLabel: unitMeta.conversionLabel,
        autoDetected: unitMeta.autoDetected,
        detectedSize: unitMeta.detectedSize,
        detectedUnit: unitMeta.detectedUnit,
        unitRuleSource: unitMeta.unitRuleSource,
        unitRuleCategory: unitMeta.unitRuleCategory,
        sourceIndex: index,
      };
    })
    .filter((row) => row.product)
    .filter((row) => {
      const key = duplicateMergeKey({
        product: row.product,
        sku: row.sku,
        category: row.category,
        cost: row.cost,
        systemSoh: row.systemSoh,
      }, { product: "product", sku: "sku", category: "category", cost: "cost", systemSoh: "systemSoh" });
      if (!key || seenProducts.has(key)) return false;
      seenProducts.add(key);
      return true;
    });

  prepared.sort((a, b) => {
    const rankA = categoryRank[a.category] ?? 9999;
    const rankB = categoryRank[b.category] ?? 9999;
    if (rankA !== rankB) return rankA - rankB;
    return a.sourceIndex - b.sourceIndex;
  });

  return prepared.map(({ sourceIndex, ...row }) => row);
}

function mergeMapping(existingMapping = {}, autoMapping = {}, headers = [], config = DEFAULT_CONFIG, options = {}) {
  const nextMapping = {
    product: existingMapping.product || autoMapping.product || "",
    category: existingMapping.category || autoMapping.category || "",
    cost: existingMapping.cost || autoMapping.cost || "",
    sku: existingMapping.sku || autoMapping.sku || "",
    systemSoh: existingMapping.systemSoh || autoMapping.systemSoh || "",
  };

  const desiredInventoryHeader = bestInventoryHeaderForStore(headers, config.storeName) || buildStoreInventoryHeader(config.storeName);
  const desiredHeaderExists = headers.some((header) => basicNorm(header) === basicNorm(desiredInventoryHeader));
  const preserveSystemSoh = options.preserveSystemSoh === true;

  if (!preserveSystemSoh && (options.forceInventoryRemap || !nextMapping.systemSoh)) {
    nextMapping.systemSoh = desiredHeaderExists ? desiredInventoryHeader : (autoMapping.systemSoh || nextMapping.systemSoh || "");
  }

  return nextMapping;
}

export function recomputeEngineState(baseState = {}, options = {}) {
  const config = { ...DEFAULT_CONFIG, ...(baseState.config || {}) };
  const rules = Array.isArray(baseState.rules) && baseState.rules.length ? baseState.rules : DEFAULT_RULES;
  const csvText = String(baseState.csvText ?? "");
  const isPosMode = String(config.dataSourceMode || "csv").toLowerCase() === "pos";
  const parsedFromCsv = parseCsv(csvText);
  const existingRows = Array.isArray(baseState.rawRows) ? baseState.rawRows.filter((row) => row && typeof row === "object") : [];
  let parsed = isPosMode && existingRows.length
    ? { headers: Array.isArray(baseState.parsedHeaders) ? baseState.parsedHeaders : [], rows: existingRows }
    : parsedFromCsv;

  // Guard against stale csvText winning over the latest imported rawRows. This
  // was the District Six revert: csvText still contained the old 1-row export,
  // while rawRows/fileName had the latest 93-row upload. Prefer the row payload
  // when it is newer/larger or when headers were lost.
  const shouldPreferExistingRows = existingRows.length > 0 && (
    !csvText.trim() ||
    parsedFromCsv.rows.length === 0 ||
    (baseState.fileName && baseState.uploadedAt && parsedFromCsv.rows.length !== existingRows.length)
  );
  if (!isPosMode && shouldPreferExistingRows) {
    parsed = {
      headers: Array.isArray(baseState.parsedHeaders) && baseState.parsedHeaders.length
        ? baseState.parsedHeaders
        : Object.keys(existingRows[0] || {}),
      rows: existingRows,
    };
  }

  // If an older cached/cloud engine has rows but lost csvText/headers, do not
  // treat it as a 0-column import. Rebuild headers from row keys so Prepared
  // Data does not crash and the latest uploaded rows remain usable.
  if ((!parsed.headers || parsed.headers.length === 0) && existingRows.length) {
    parsed = { headers: Object.keys(existingRows[0] || {}), rows: existingRows };
  }
  const autoMapping = detectBestMapping(parsed.headers, config.storeName || DEFAULT_CONFIG.storeName);
  const ruleCategories = listNormalizedCategories(rules);
  const mappedCategoryKey = (baseState.mapping || DEFAULT_MAPPING).category || autoMapping.category || DEFAULT_MAPPING.category;
  const rawRowCategories = Array.from(new Set((parsed.rows || [])
    .map((row) => basicNorm(firstPresent(row, [mappedCategoryKey, autoMapping.category, "category", "product_category"])))
    .filter(Boolean)));

  // Important: raw POS/source categories are NOT normalized outputs.
  // Unknown source categories must stay in an unmapped queue instead of
  // polluting category order, ownership, unit rules, and live count buckets.
  const explicitOutputSet = new Set(ruleCategories);
  const existingOrder = (config.categoryOrder || []).map(basicNorm).filter((item) => item && explicitOutputSet.has(item));
  const existingUnitRuleCategories = config.categoryUnitRules && typeof config.categoryUnitRules === "object" && !Array.isArray(config.categoryUnitRules)
    ? Object.keys(config.categoryUnitRules).map(basicNorm).filter((item) => item && explicitOutputSet.has(item))
    : [];
  const normalizedCategories = Array.from(new Set([...ruleCategories, ...existingOrder, ...existingUnitRuleCategories]));
  const knownCategories = normalizedCategories;
  const nextCategoryOrder = existingOrder.filter((item) => knownCategories.includes(item));
  ruleCategories.forEach((item) => {
    if (item && !nextCategoryOrder.includes(item)) nextCategoryOrder.push(item);
  });
  const unmappedSourceCategories = rawRowCategories.filter((raw) => !rawRowCategories.some((x) => false) && !rules.some((rule) => normalizeCategory('', raw, [rule]) !== UNMAPPED_CATEGORY));
  const existingConversions = (config.halfGramCategories || config.conversionCategories || []).map(basicNorm).filter(Boolean);
  const existingGroups = Array.isArray(config.categoryGroups) ? config.categoryGroups : [];
  const existingOwnership = config.categoryOwnership && typeof config.categoryOwnership === "object" && !Array.isArray(config.categoryOwnership) ? config.categoryOwnership : {};
  const nextOwnership = {};
  Object.entries(existingOwnership).forEach(([category, sectionId]) => {
    const cleanCategory = basicNorm(category);
    const cleanSectionId = String(sectionId || "").trim();
    if (cleanCategory && cleanSectionId && normalizedCategories.includes(cleanCategory)) nextOwnership[cleanCategory] = cleanSectionId;
  });
  const nextConfig = {
    ...config,
    categoryOrder: nextCategoryOrder,
    halfGramCategories: existingConversions.filter((item) => normalizedCategories.includes(item)),
    categoryGroups: existingGroups
      .map((group) => ({
        name: String(group?.name || "").trim(),
        outputs: Array.from(new Set((group?.outputs || []).map(basicNorm).filter((item) => normalizedCategories.includes(item)))),
      }))
      .filter((group) => group.name),
    categoryOwnership: nextOwnership,
    unmappedSourceCategories,
    unitLibrary: Array.isArray(config.unitLibrary) && config.unitLibrary.length ? Array.from(new Set(config.unitLibrary.map(normalizeUnitName))) : DEFAULT_UNIT_LIBRARY,
    defaultUnitRule: normalizeUnitRule(config.defaultUnitRule || DEFAULT_UNIT_RULE, DEFAULT_UNIT_RULE),
    categoryUnitRules: config.categoryUnitRules && typeof config.categoryUnitRules === "object" && !Array.isArray(config.categoryUnitRules)
      ? Object.fromEntries(Object.entries(config.categoryUnitRules).map(([category, rule]) => [basicNorm(category), normalizeUnitRule(rule, config.defaultUnitRule || DEFAULT_UNIT_RULE)]).filter(([category]) => knownCategories.length === 0 || knownCategories.includes(category)))
      : {},
  };
  const mapping = mergeMapping(baseState.mapping || DEFAULT_MAPPING, autoMapping, parsed.headers, nextConfig, options);
  const preparedRows = buildPreparedData(parsed.rows, mapping, nextConfig, rules);

  return {
    ...baseState,
    csvText,
    parsedHeaders: parsed.headers,
    rawRows: parsed.rows,
    mapping,
    rules,
    config: nextConfig,
    preparedRows,
    lastGeneratedAt: new Date().toISOString(),
    fileName: baseState.fileName || "",
  };
}

function extractCategoryConfig(config = {}) {
  return {
    categoryOrder: Array.isArray(config.categoryOrder) ? config.categoryOrder : DEFAULT_CONFIG.categoryOrder,
    halfGramCategories: Array.isArray(config.halfGramCategories) ? config.halfGramCategories : DEFAULT_CONFIG.halfGramCategories,
    conversionCategories: Array.isArray(config.conversionCategories) ? config.conversionCategories : config.conversionCategories,
    categoryGroups: Array.isArray(config.categoryGroups) ? config.categoryGroups : DEFAULT_CONFIG.categoryGroups,
    categoryOwnership: config.categoryOwnership && typeof config.categoryOwnership === "object" && !Array.isArray(config.categoryOwnership) ? config.categoryOwnership : DEFAULT_CONFIG.categoryOwnership,
    unitLibrary: Array.isArray(config.unitLibrary) && config.unitLibrary.length ? config.unitLibrary : DEFAULT_CONFIG.unitLibrary,
    defaultUnitRule: config.defaultUnitRule && typeof config.defaultUnitRule === "object" ? config.defaultUnitRule : DEFAULT_CONFIG.defaultUnitRule,
    categoryUnitRules: config.categoryUnitRules && typeof config.categoryUnitRules === "object" && !Array.isArray(config.categoryUnitRules) ? config.categoryUnitRules : DEFAULT_CONFIG.categoryUnitRules,
  };
}

function extractUnitIntelligenceConfig(config = {}) {
  return {
    savedAt: new Date().toISOString(),
    unitLibrary: Array.isArray(config.unitLibrary) && config.unitLibrary.length ? config.unitLibrary : DEFAULT_CONFIG.unitLibrary,
    defaultUnitRule: config.defaultUnitRule && typeof config.defaultUnitRule === "object" ? config.defaultUnitRule : DEFAULT_CONFIG.defaultUnitRule,
    categoryUnitRules: config.categoryUnitRules && typeof config.categoryUnitRules === "object" && !Array.isArray(config.categoryUnitRules) ? config.categoryUnitRules : {},
  };
}

export function loadUnitIntelligenceConfig() {
  try {
    const raw = mcGetItem(UNIT_INTELLIGENCE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      unitLibrary: Array.isArray(parsed.unitLibrary) && parsed.unitLibrary.length ? parsed.unitLibrary : DEFAULT_CONFIG.unitLibrary,
      defaultUnitRule: parsed.defaultUnitRule && typeof parsed.defaultUnitRule === "object" ? parsed.defaultUnitRule : DEFAULT_CONFIG.defaultUnitRule,
      categoryUnitRules: parsed.categoryUnitRules && typeof parsed.categoryUnitRules === "object" && !Array.isArray(parsed.categoryUnitRules) ? parsed.categoryUnitRules : {},
    };
  } catch (_) {
    return null;
  }
}

export function mergeUnitIntelligenceConfig(state = {}, scope = "ALL") {
  // The legacy Unit Intelligence cache is a global compatibility record. Site
  // engines already inherit Unit Intelligence from All Sites in
  // inheritCategorySettingsForScope(), unless the site explicitly overrides
  // that section. Re-applying this global cache to every site here would
  // silently overwrite a legitimate site override.
  if (normalizeScopeValue(scope) !== "ALL") return state;
  const unitConfig = loadUnitIntelligenceConfig();
  if (!unitConfig) return state;
  return {
    ...state,
    config: {
      ...(state.config || {}),
      ...unitConfig,
    },
  };
}

export function saveUnitIntelligenceConfig(config = {}) {
  const payload = extractUnitIntelligenceConfig(config || {});
  try { mcSetItem(UNIT_INTELLIGENCE_KEY, JSON.stringify(payload)); } catch (_) {}
  return payload;
}

function inheritCategorySettingsForScope(state = {}, scope = "ALL") {
  const normalizedScope = normalizeScopeValue(scope);
  if (normalizedScope === "ALL" || !state) return state;
  const allRaw = mcGetItem(scopedStorageKey(ENGINE_STORAGE_KEY, "ALL"));
  if (!allRaw) return state;
  let allState = null;
  try { allState = JSON.parse(allRaw); } catch (_) { allState = null; }
  if (!allState) return state;

  // MC494 — the compact category backup is the durable learned-rule authority.
  // The full global engine mirror can legitimately be older/oversized; always
  // overlay the newest compact backup before a site inherits global Rules,
  // Groups, Ownership, Ordering or Unit Intelligence.
  allState = applyLatestCategoryBackup(allState, "ALL");

  // Inventory Configuration is section-scoped. A local override in one section
  // must not freeze every other section at an older site copy. Each section
  // independently resolves either from the site payload or from All Sites.
  const scopedConfig = state.config || {};
  const globalConfig = allState.config || {};
  const explicit = scopedConfig.configurationScopeOverrides && typeof scopedConfig.configurationScopeOverrides === "object"
    ? scopedConfig.configurationScopeOverrides
    : {};
  const isOverridden = (key) => explicit[key] === true;
  const clone = (value, fallback) => {
    try { return JSON.parse(JSON.stringify(value ?? fallback)); } catch (_) { return value ?? fallback; }
  };

  const inheritedConfig = {
    ...scopedConfig,
    categoryGroups: isOverridden("groups")
      ? clone(scopedConfig.categoryGroups, [])
      : clone(globalConfig.categoryGroups, scopedConfig.categoryGroups || []),
    categoryOwnership: isOverridden("ownership")
      ? { ...(scopedConfig.categoryOwnership || {}) }
      : { ...(globalConfig.categoryOwnership || scopedConfig.categoryOwnership || {}) },
    categoryOrder: (isOverridden("ordering") || isOverridden("outputs"))
      ? clone(scopedConfig.categoryOrder, [])
      : clone(globalConfig.categoryOrder, scopedConfig.categoryOrder || []),
    unitLibrary: isOverridden("unitIntelligence")
      ? clone(scopedConfig.unitLibrary, DEFAULT_CONFIG.unitLibrary)
      : clone(globalConfig.unitLibrary, scopedConfig.unitLibrary || DEFAULT_CONFIG.unitLibrary),
    defaultUnitRule: isOverridden("unitIntelligence")
      ? { ...(scopedConfig.defaultUnitRule || DEFAULT_CONFIG.defaultUnitRule) }
      : { ...(globalConfig.defaultUnitRule || scopedConfig.defaultUnitRule || DEFAULT_CONFIG.defaultUnitRule) },
    categoryUnitRules: isOverridden("unitIntelligence")
      ? clone(scopedConfig.categoryUnitRules, {})
      : clone(globalConfig.categoryUnitRules, scopedConfig.categoryUnitRules || {}),
    configurationSectionVersions: {
      ...(scopedConfig.configurationSectionVersions || {}),
      ...Object.fromEntries(
        Object.entries(globalConfig.configurationSectionVersions || {})
          .filter(([key]) => !isOverridden(key))
          .map(([key, value]) => [key, { ...(value || {}), inherited: true }])
      ),
    },
    configurationScopeOverrides: { ...explicit },
    storeName: scopedConfig.storeName || storeNameFromScope(normalizedScope),
    categoryOverride: Object.values(explicit).some(Boolean),
  };

  const inheritedRules = (isOverridden("rules") || isOverridden("outputs"))
    ? (Array.isArray(state.rules) ? state.rules : [])
    : (Array.isArray(allState.rules) ? allState.rules : (state.rules || []));

  return recomputeEngineState({
    ...state,
    rules: inheritedRules,
    config: inheritedConfig,
    mapping: {
      ...(state.mapping || DEFAULT_MAPPING),
      systemSoh: state.mapping?.systemSoh || storeKeyFromScope(normalizedScope),
    },
  }, {
    forceInventoryRemap: !(isOverridden("rules") || isOverridden("outputs"))
      || !(isOverridden("ordering") || isOverridden("outputs"))
      || !isOverridden("unitIntelligence"),
  });
}

function engineHasCategorySetup(state = {}) {
  return Boolean(
    (Array.isArray(state?.rules) && state.rules.length > 0) ||
    (Array.isArray(state?.config?.categoryOrder) && state.config.categoryOrder.length > 0) ||
    (Array.isArray(state?.config?.categoryGroups) && state.config.categoryGroups.length > 0) ||
    (state?.config?.categoryOwnership && Object.keys(state.config.categoryOwnership || {}).length > 0)
  );
}

function engineCategoryWeight(state = {}) {
  return (Array.isArray(state?.rules) ? state.rules.length * 5 : 0)
    + (Array.isArray(state?.config?.categoryOrder) ? state.config.categoryOrder.length * 3 : 0)
    + (Array.isArray(state?.config?.categoryGroups) ? state.config.categoryGroups.reduce((sum, group) => sum + 2 + (Array.isArray(group?.outputs) ? group.outputs.length : 0), 0) : 0)
    + (state?.config?.categoryOwnership ? Object.keys(state.config.categoryOwnership || {}).length : 0);
}

function recoverCategoryEngineFromScopedCache(scope = "ALL") {
  const normalizedScope = normalizeScopeValue(scope);
  const enginePayloads = listScopedPayloads(ENGINE_STORAGE_KEY)
    .map((item) => ({ ...item, payload: item.payload && typeof item.payload === "object" ? item.payload : null }))
    .filter((item) => item.payload && engineHasCategorySetup(item.payload));

  const backupPayloads = listCategoryBackups().map((item) => ({
    ...item,
    payload: {
      csvText: "",
      mapping: { ...DEFAULT_MAPPING, systemSoh: normalizedScope === "ALL" ? DEFAULT_MAPPING.systemSoh : storeKeyFromScope(normalizedScope) },
      rawRows: [],
      rules: item.payload.rules || [],
      config: { ...DEFAULT_CONFIG, ...(item.payload.config || {}) },
      fileName: "category settings backup",
    },
  }));

  const payloads = [...enginePayloads, ...backupPayloads];
  if (!payloads.length) return null;

  const exact = payloads
    .filter((item) => normalizeScopeValue(item.scope) === normalizedScope)
    .sort((a, b) => engineCategoryWeight(b.payload) - engineCategoryWeight(a.payload))[0];
  const selected = exact || payloads.sort((a, b) => engineCategoryWeight(b.payload) - engineCategoryWeight(a.payload))[0];
  if (!selected?.payload) return null;

  const targetStoreName = normalizedScope === "ALL"
    ? (selected.payload?.config?.storeName || DEFAULT_CONFIG.storeName)
    : storeNameFromScope(normalizedScope);

  return recomputeEngineState({
    ...selected.payload,
    config: {
      ...(selected.payload.config || {}),
      storeName: targetStoreName,
      categoryOverride: normalizedScope !== "ALL",
    },
    mapping: {
      ...(selected.payload.mapping || DEFAULT_MAPPING),
      systemSoh: normalizedScope === "ALL" ? (selected.payload.mapping?.systemSoh || DEFAULT_MAPPING.systemSoh) : storeKeyFromScope(normalizedScope),
    },
  }, { forceInventoryRemap: normalizedScope !== "ALL" });
}

export function loadEngineState(scope = "ALL") {
  try {
    const normalizedScope = normalizeScopeValue(scope);
    const raw = mcGetItem(scopedStorageKey(ENGINE_STORAGE_KEY, normalizedScope));
    if (!raw) {
      const recovered = recoverCategoryEngineFromScopedCache(normalizedScope);
      if (recovered) return applyDuplicateReviewDecisions(applySharedConfigOverlay(mergeUnitIntelligenceConfig(recovered, normalizedScope), normalizedScope), normalizedScope);
      if (normalizedScope !== "ALL") {
        const inherited = inheritCategorySettingsForScope(buildEmptyState(normalizedScope), normalizedScope);
        return inherited ? applyDuplicateReviewDecisions(applySharedConfigOverlay(mergeUnitIntelligenceConfig(inherited, normalizedScope), normalizedScope), normalizedScope) : null;
      }
      return null;
    }
    const parsed = JSON.parse(raw);
    const backedUp = applyLatestCategoryBackup(parsed, normalizedScope);
    const inherited = inheritCategorySettingsForScope(backedUp, normalizedScope);
    if (!engineHasCategorySetup(inherited)) {
      const recovered = recoverCategoryEngineFromScopedCache(normalizedScope);
      if (recovered) return applyDuplicateReviewDecisions(applySharedConfigOverlay(mergeUnitIntelligenceConfig(recovered, normalizedScope), normalizedScope), normalizedScope);
    }
    return applyDuplicateReviewDecisions(applySharedConfigOverlay(mergeUnitIntelligenceConfig(inherited, normalizedScope), normalizedScope), normalizedScope);
  } catch (err) {
    console.error("Failed to load MC engine state", err);
    const recovered = recoverCategoryEngineFromScopedCache(scope);
    return recovered ? applyDuplicateReviewDecisions(applySharedConfigOverlay(mergeUnitIntelligenceConfig(recovered, normalizeScopeValue(scope)), normalizeScopeValue(scope)), normalizeScopeValue(scope)) : null;
  }
}


export function loadLatestImportEngineState(scope = "ALL") {
  return loadEngineState(scope);
}

export function engineStorageKeyForScope(scope = "ALL") {
  return scopedStorageKey(ENGINE_STORAGE_KEY, scope);
}

export function sharedCategoryGroupsStorageKeyForScope(scope = "ALL") {
  return sharedConfigStorageKey(SHARED_GROUPS_STORAGE_KEY, scope);
}

export function sharedCategoryOwnershipStorageKeyForScope(scope = "ALL") {
  return sharedConfigStorageKey(SHARED_OWNERSHIP_STORAGE_KEY, scope);
}

export async function saveEngineStateDurable(state, scope = "ALL", options = {}) {
  const { persistGroups = true, persistOwnership = true } = options || {};
  const normalizedScope = normalizeScopeValue(scope);
  const payload = state && typeof state === "object" ? state : buildEmptyState(normalizedScope);

  // Write the browser cache first so the UI remains immediate, then bypass the
  // hydration queue for the authoritative Firestore commit. The normal queued
  // writer can be overtaken by a cloud hydration event, which previously made
  // newly learned/manual rules appear briefly and disappear after refresh.
  saveEngineState(payload, normalizedScope);

  // Duplicate-review decisions are intentionally stored in a compact,
  // dedicated record. Large imports can make the full engine payload exceed
  // Firestore document limits; these decisions must still survive refresh.
  const writes = [
    saveDuplicateReviewDecisions(payload, normalizedScope),
    mcSetCloudItemImmediate(
      engineStorageKeyForScope(normalizedScope),
      JSON.stringify(payload),
    ),
  ];

  if (persistGroups && Array.isArray(payload?.config?.categoryGroups)) {
    const groups = payload.config.categoryGroups;
    saveSharedCategoryGroups(groups, normalizedScope);
    writes.push(
      mcSetCloudItemImmediate(
        sharedCategoryGroupsStorageKeyForScope(normalizedScope),
        JSON.stringify(groups),
      ),
    );
  }

  if (
    persistOwnership &&
    payload?.config?.categoryOwnership &&
    typeof payload.config.categoryOwnership === "object" &&
    !Array.isArray(payload.config.categoryOwnership)
  ) {
    const ownership = payload.config.categoryOwnership;
    saveSharedCategoryOwnership(ownership, normalizedScope);
    writes.push(
      mcSetCloudItemImmediate(
        sharedCategoryOwnershipStorageKeyForScope(normalizedScope),
        JSON.stringify(ownership),
      ),
    );
  }

  const results = await Promise.all(writes);
  return results.every((saved) => saved === true);
}

export function saveEngineState(state, scope = "ALL") {
  try {
    mcSetItem(scopedStorageKey(ENGINE_STORAGE_KEY, scope), JSON.stringify(state));
    saveUnitIntelligenceConfig(state.config || {});
    saveCategoryBackup(state, scope);
    dispatchMcEvent(MC_ENGINE_EVENT, { scope: normalizeScopeValue(scope), state });
  } catch (err) {
    console.error("Failed to save MC engine state", err);
  }
}

export function generateNextSessionId() {
  const now = new Date();
  const y = String(now.getFullYear()).slice(-2);
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  const ss = String(now.getSeconds()).padStart(2, "0");

  // MC409: Session identity must never depend on a browser-local counter.
  // The old mc-session-counter resets independently per browser/device/profile,
  // which allowed two real counts on the same day to receive the same ID. Since
  // sessionId is the key for the live ledger, Sessions, Archives and Lifecycle,
  // that collision can hide a live count behind an older archived count.
  //
  // Keep the date/time human-readable, then append 48 bits of cryptographic
  // entropy (12 hex chars). The fallback still mixes timestamp + randomness for
  // older browsers where crypto.getRandomValues is unavailable.
  let entropy = "";
  try {
    if (typeof window !== "undefined" && window.crypto?.getRandomValues) {
      const bytes = new Uint8Array(6);
      window.crypto.getRandomValues(bytes);
      entropy = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("").toUpperCase();
    }
  } catch (_) {}
  if (!entropy) {
    entropy = `${now.getTime().toString(16)}${Math.random().toString(16).slice(2)}`
      .replace(/[^a-f0-9]/gi, "")
      .slice(-12)
      .padStart(12, "0")
      .toUpperCase();
  }

  // Retire the legacy local counter so it can never become authoritative again.
  try { mcRemoveItem(SESSION_COUNTER_KEY); } catch (_) {}

  return `MC-${y}${m}${d}-${hh}${mm}${ss}-${entropy}`;
}

function compactSessionPayload(payload = {}) {
  const preparedRows = Array.isArray(payload.preparedRows) ? payload.preparedRows : [];
  return {
    sessionId: payload.sessionId || "",
    siteId: payload.siteId || payload.meta?.siteId || '',
    operationalDate: payload.operationalDate || payload.meta?.operationalDate || '',
    productScopeType: payload.productScopeType || payload.meta?.productScopeType || 'all',
    productIds: payload.productIds || payload.meta?.productIds || [],
    productNames: payload.productNames || payload.meta?.productNames || [],
    productScopeLabel: payload.productScopeLabel || payload.meta?.productScopeLabel || 'All products',

    storeKey: payload.storeKey || payload.mapping?.systemSoh || "",
    storeName: payload.storeName || labelFromInventoryHeader(payload.storeKey || payload.mapping?.systemSoh || ""),
    fileName: payload.fileName || "prepared import",
    startedAt: payload.startedAt || payload.createdAt || new Date().toISOString(),
    createdAt: payload.createdAt || payload.startedAt || new Date().toISOString(),
    billingStartVersion: Number(payload.billingStartVersion || payload.meta?.billingStartVersion || 0),
    billingOriginalStartedAt: payload.billingOriginalStartedAt || payload.meta?.billingOriginalStartedAt || payload.startedAt || payload.meta?.startedAt || "",
    startedBy: payload.startedBy || "User",
    lastEditedBy: payload.lastEditedBy || payload.startedBy || "User",
    sectionId: payload.sectionId || payload.meta?.sectionId || '',
    sectionName: payload.sectionName || payload.meta?.sectionName || '',
    fullSite: payload.fullSite === true || payload.meta?.fullSite === true,
    sessionType: payload.sessionType || payload.meta?.sessionType || (payload.fullSite === true || payload.meta?.fullSite === true ? 'full-count' : 'section'),
    countScope: payload.countScope || payload.meta?.countScope || (payload.fullSite === true || payload.meta?.fullSite === true ? 'full-site' : 'section'),
    categoryScopeType: payload.categoryScopeType || payload.meta?.categoryScopeType || 'all',
    categoryNames: Array.isArray(payload.categoryNames) ? payload.categoryNames : (Array.isArray(payload.meta?.categoryNames) ? payload.meta.categoryNames : []),
    countGroupNames: Array.isArray(payload.countGroupNames) ? payload.countGroupNames : (Array.isArray(payload.meta?.countGroupNames) ? payload.meta.countGroupNames : (Array.isArray(payload.categoryNames) ? payload.categoryNames : [])),
    categoryIds: Array.isArray(payload.categoryIds) ? payload.categoryIds : (Array.isArray(payload.meta?.categoryIds) ? payload.meta.categoryIds : []),
    categoryScopeLabel: payload.categoryScopeLabel || payload.meta?.categoryScopeLabel || 'All Count Groups',
    categoryScopeSource: payload.categoryScopeSource || payload.meta?.categoryScopeSource || 'count-groups',
    cycleId: payload.cycleId || payload.cycleKey || payload.cycle?.cycleId || payload.cycle?.id || payload.meta?.cycleId || payload.meta?.cycleKey || payload.meta?.cycle?.cycleId || payload.meta?.cycle?.id || '',
    cycleName: payload.cycleName || payload.cycle?.cycleName || payload.cycle?.name || payload.meta?.cycleName || payload.meta?.cycle?.cycleName || payload.meta?.cycle?.name || '',
    cycleKey: payload.cycleKey || payload.cycleId || payload.cycle?.cycleId || payload.cycle?.id || payload.meta?.cycleKey || payload.meta?.cycleId || '',
    cycle: payload.cycle || payload.meta?.cycle || undefined,
    preparedRows,
    mapping: payload.mapping || DEFAULT_MAPPING,
    config: payload.config || DEFAULT_CONFIG,
  };
}

export function savePreparedRowsToSessionSeed(payload, scope = "ALL") {
  try {
    const compact = compactSessionPayload(payload);
    mcSetItem(scopedStorageKey(SESSION_SEED_KEY, scope), JSON.stringify(compact));
    if (compact.sessionId) {
      mcSetItem(scopedStorageKey(SESSION_SEED_KEY, compact.storeKey || scope), JSON.stringify(compact));
    }
    dispatchMcEvent(MC_SESSION_EVENT, { type: "seed", scope: normalizeScopeValue(scope), payload: compact });
  } catch (err) {
    console.error("Failed to save MC session seed", err);
  }
}

export function loadPreparedRowsFromSessionSeed(scope = "ALL") {
  try {
    const raw = mcGetItem(scopedStorageKey(SESSION_SEED_KEY, scope));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (err) {
    console.error("Failed to load MC session seed", err);
    return null;
  }
}


export function listSessionSeeds() {
  return listScopedPayloads(SESSION_SEED_KEY)
    .map(({ key, scope, payload }) => ({ ...payload, __storageKey: key, __scope: scope }))
    .filter((item) => item?.sessionId);
}

export function findSessionSeedById(sessionId) {
  return listSessionSeeds().find((item) => item.sessionId === sessionId) || null;
}

export function removeSessionSeedById(sessionId) {
  if (typeof window === "undefined") return;
  listSessionSeeds()
    .filter((item) => item.sessionId === sessionId)
    .forEach((item) => mcRemoveItem(item.__storageKey));
  dispatchMcEvent(MC_SESSION_EVENT, { type: "seed-remove", sessionId });
}

function buildSessionRecordFromSeed(seed) {
  if (!seed?.sessionId) return null;
  const fullSite = seed.fullSite === true || seed.isFullSite === true || seed.fullCount === true || String(seed.countScope || seed.sessionType || '').toLowerCase().includes('full');
  const cycleId = seed.cycleId || seed.cycleKey || seed.cycle?.cycleId || seed.cycle?.id || seed.meta?.cycleId || seed.meta?.cycleKey || '';
  const cycleName = seed.cycleName || seed.cycle?.cycleName || seed.cycle?.name || seed.meta?.cycleName || seed.meta?.cycle?.name || '';
  const sectionId = fullSite ? '' : (seed.sectionId || seed.meta?.sectionId || '');
  const sectionName = fullSite ? '' : (seed.sectionName || seed.meta?.sectionName || '');
  return {
    sessionId: seed.sessionId,
    storeName: seed.storeName || labelFromInventoryHeader(seed.storeKey || ''),
    storeKey: seed.storeKey || seed.mapping?.systemSoh || '',
    sectionId,
    sectionName,
    fullSite,
    isFullSite: fullSite,
    fullCount: fullSite,
    sessionType: seed.sessionType || seed.meta?.sessionType || (fullSite ? 'full-count' : 'section'),
    countScope: seed.countScope || seed.meta?.countScope || (fullSite ? 'full-site' : 'section'),
    cycleId,
    cycleName,
    cycleKey: seed.cycleKey || cycleId,
    cycle: seed.cycle || seed.meta?.cycle || (cycleId || cycleName ? { cycleId, id: cycleId, name: cycleName } : undefined),
    meta: {
      ...(seed.meta || {}),
      cycleId,
      cycleName,
      cycleKey: seed.cycleKey || cycleId,
      sectionId,
      sectionName,
      fullSite,
      isFullSite: fullSite,
      fullCount: fullSite,
      sessionType: seed.sessionType || seed.meta?.sessionType || (fullSite ? 'full-count' : 'section'),
      countScope: seed.countScope || seed.meta?.countScope || (fullSite ? 'full-site' : 'section'),
    },
    fileName: seed.fileName || 'prepared import',
    startedAt: seed.startedAt || seed.createdAt || new Date().toISOString(),
    updatedAt: seed.startedAt || seed.createdAt || new Date().toISOString(),
    startedBy: seed.startedBy || 'User',
    lastEditedBy: seed.lastEditedBy || seed.startedBy || 'User',
    totalLines: seed.preparedRows?.length || 0,
    completedLines: 0,
    flaggedCount: 0,
    status: 'Active',
    costExposure: 0,
  };
}

function isArchivedLikeSessionRecord(record = {}) {
  const status = String(record?.status || "").trim().toLowerCase();
  return record?.archived === true || !!record?.archivedAt || !!record?.archiveType || status === "archived" || status === "archive";
}


function readSessionTransitionOverrideIds(key = "") {
  const ids = new Set();
  if (typeof window === "undefined" || !key) return ids;
  try {
    const raw = window.localStorage?.getItem(key) || "{}";
    const parsed = JSON.parse(raw);
    Object.entries(parsed || {}).forEach(([id, stamp]) => {
      const time = Date.parse(stamp || "");
      if (id && Number.isFinite(time) && Date.now() - time < 24 * 60 * 60 * 1000) {
        ids.add(String(id).trim());
      }
    });
  } catch (_) {}
  return ids;
}

function archivedSessionIdsForActiveList() {
  const ids = new Set();
  const addRows = (rows) => {
    (Array.isArray(rows) ? rows : []).forEach((item) => {
      const id = String(item?.sessionId || "").trim();
      if (id) ids.add(id);
    });
  };
  const readList = (area) => {
    try { addRows(JSON.parse(area?.getItem(ARCHIVE_LIST_KEY) || "[]")); } catch (_) {}
  };

  if (typeof window !== "undefined") {
    readList(window.localStorage);
    readList(window.sessionStorage);

    const scanArea = (area) => {
      if (!area) return;
      try {
        for (let i = 0; i < area.length; i += 1) {
          const key = area.key(i);
          if (!key || !key.startsWith("mc-archive-session:")) continue;
          const idFromKey = key.replace("mc-archive-session:", "").trim();
          try {
            const parsed = JSON.parse(area.getItem(key) || "{}");
            const id = String(parsed?.sessionId || idFromKey || "").trim();
            if (id) ids.add(id);
          } catch (_) {
            if (idFromKey) ids.add(idFromKey);
          }
        }
      } catch (_) {}
    };
    scanArea(window.localStorage);
    scanArea(window.sessionStorage);
  }

  readSessionTransitionOverrideIds("__mc_session_archive_overrides").forEach((id) => ids.add(id));
  readSessionTransitionOverrideIds("__mc_session_restore_overrides").forEach((id) => ids.delete(id));
  return ids;
}



function canonicalLedgerDiscoveryRecords() {
  if (typeof window === "undefined") return [];
  const activeOrgId = String(getMcActiveOrgId() || "").trim();
  if (!activeOrgId) return [];
  const byId = new Map();

  const scanArea = (area) => {
    if (!area) return;
    try {
      for (let i = 0; i < area.length; i += 1) {
        const key = area.key(i);
        if (!key || !key.startsWith("mc-count-ledger:")) continue;
        const sessionIdFromKey = key.slice("mc-count-ledger:".length).trim();
        try {
          const raw = area.getItem(key);
          const payload = raw ? JSON.parse(raw) : null;
          if (!payload || !Array.isArray(payload?.lines) || !payload.lines.length) continue;
          const meta = payload?.meta && typeof payload.meta === "object" ? payload.meta : {};
          const sessionId = String(meta.sessionId || sessionIdFromKey || "").trim();
          if (!sessionId) continue;
          const orgId = String(meta.tenantOrgId || meta.orgId || meta.organisationId || activeOrgId).trim();
          if (orgId !== activeOrgId) continue;
          const lifecycle = String(meta.lifecycleState || "").trim().toLowerCase();
          const status = String(meta.status || "Active").trim();
          const statusLower = status.toLowerCase();
          if (meta.immutable === true || meta.finalizedAt || lifecycle === "finalized" || statusLower === "archived" || statusLower === "archive") continue;

          const summary = meta.sessionSummary && typeof meta.sessionSummary === "object" ? meta.sessionSummary : {};
          const completedLines = payload.lines.filter((line) => line && (line.counted !== "" || line.waste !== "" || String(line.note || line.notes || "").trim())).length;
          const isFull = meta.fullSite === true || meta.isFullSite === true || meta.fullCount === true || String(meta.sessionType || meta.countScope || "").toLowerCase().includes("full");
          const record = {
            ...meta,
            sessionId,
            id: sessionId,
            tenantOrgId: activeOrgId,
            orgId: activeOrgId,
            status: status || "Active",
            storeName: meta.storeName || meta.store || "",
            store: meta.store || meta.storeName || "",
            cycleId: meta.cycleId || meta.cycleKey || meta.cycle?.cycleId || meta.cycle?.id || "",
            cycleName: meta.cycleName || meta.cycle?.cycleName || meta.cycle?.name || "",
            cycleKey: meta.cycleKey || meta.cycleId || "",
            cycle: meta.cycle || undefined,
            sectionId: isFull ? "" : (meta.sectionId || ""),
            sectionName: isFull ? "" : (meta.sectionName || ""),
            fullSite: isFull,
            isFullSite: isFull,
            fullCount: isFull,
            sessionType: meta.sessionType || (isFull ? "full-count" : "section"),
            countScope: meta.countScope || (isFull ? "full-site" : "section"),
            startedBy: meta.startedBy || meta.createdBy || "User",
            lastEditedBy: meta.lastEditedBy || meta.startedBy || meta.createdBy || "User",
            startedAt: meta.startedAt || meta.createdAt || payload.savedAt || "",
            updatedAt: payload.savedAt || meta.updatedAt || meta.completedAt || meta.sectionCompletedAt || meta.startedAt || "",
            totalLines: Number(summary.totalLines ?? meta.totalLines ?? payload.lines.length),
            completedLines: Number(summary.completedLines ?? meta.completedLines ?? completedLines),
            flaggedCount: Number(summary.flaggedCount ?? summary.flaggedItems ?? meta.flaggedCount ?? 0),
            flaggedProducts: Number(summary.flaggedProducts ?? summary.flaggedCount ?? meta.flaggedProducts ?? meta.flaggedCount ?? 0),
            costExposure: Number(summary.costExposure ?? summary.exposure ?? meta.costExposure ?? meta.exposure ?? 0),
            exposure: Number(summary.exposure ?? summary.costExposure ?? meta.exposure ?? meta.costExposure ?? 0),
            finalExposure: Number(summary.finalExposure ?? summary.exposure ?? meta.finalExposure ?? meta.exposure ?? meta.costExposure ?? 0),
            verificationStatus: meta.verificationStatus || (meta.verifiedAt ? "verified" : ""),
            verifiedAt: meta.verifiedAt || "",
            verifiedBy: meta.verifiedBy || "",
          };
          const existing = byId.get(sessionId);
          const existingTime = Date.parse(existing?.updatedAt || existing?.startedAt || "") || 0;
          const recordTime = Date.parse(record.updatedAt || record.startedAt || "") || 0;
          if (!existing || recordTime >= existingTime) byId.set(sessionId, record);
        } catch (_) {}
      }
    } catch (_) {}
  };

  scanArea(window.localStorage);
  scanArea(window.sessionStorage);
  return Array.from(byId.values());
}

function cleanActiveSessionRecords(records = []) {
  const archivedIds = archivedSessionIdsForActiveList();
  return (Array.isArray(records) ? records : [])
    .filter((item) => item?.sessionId)
    .filter((item) => !archivedIds.has(String(item?.sessionId || "").trim()))
    .filter((item) => !isArchivedLikeSessionRecord(item));
}

export function hydrateSessionRecordsFromSeeds() {
  // Final source-of-truth lock:

  // Sessions must ONLY come from mc-session-list.
  // Old prepared/session-engine documents are kept for detail viewing only and must never
  // promote themselves back into the active sessions list. This prevents ghost sessions
  // from reappearing after archive / permanent delete / manual Firestore cleanup.
  return loadSessionRecords();
}
export function loadSessionRecords() {
  try {
    const raw = mcGetItem(SESSION_LIST_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    const activeOrgId = String(getMcActiveOrgId() || "").trim();
    const listRows = (Array.isArray(parsed) ? parsed : []).filter((record) => {
      const stampedOrgId = String(record?.tenantOrgId || record?.orgId || "").trim();
      return Boolean(activeOrgId && stampedOrgId && stampedOrgId === activeOrgId);
    });

    // MC400 — canonical ledger discovery safety net. mc-session-list is a routing
    // index, not operational truth. If that index is missing/stale after refresh,
    // a non-finalized canonical Count Ledger must still remain visible in Sessions.
    // This is READ ONLY: it never recreates or writes the shared session list.
    const ledgerRows = canonicalLedgerDiscoveryRecords();
    const byId = new Map();
    [...listRows, ...ledgerRows].forEach((record) => {
      const id = String(record?.sessionId || "").trim();
      if (!id) return;
      const existing = byId.get(id);
      if (!existing) {
        byId.set(id, record);
        return;
      }
      // Canonical-ledger rows are appended last and therefore win lifecycle and
      // metric fields while retaining any discovery-only metadata from the list.
      byId.set(id, { ...existing, ...record, meta: { ...(existing?.meta || {}), ...(record?.meta || {}) } });
    });
    return cleanActiveSessionRecords(Array.from(byId.values()));
  } catch (err) {
    console.error("Failed to load MC session records", err);
    return cleanActiveSessionRecords(canonicalLedgerDiscoveryRecords());
  }
}


function hydrateSessionRecordMetadataFromDetail(record = {}) {
  const sessionId = String(record?.sessionId || "").trim();
  if (!sessionId) return record;
  try {
    const raw = mcGetItem(sessionEngineStorageKeyFor(sessionId)) || mcGetItem(legacySessionEngineStorageKeyFor(sessionId));
    let parsed = raw ? JSON.parse(raw) : null;
    let meta = parsed?.meta && typeof parsed.meta === "object" ? parsed.meta : {};

    // Cross-device guard: the compact seed can arrive before the large session
    // detail document. Use it as a metadata fallback so Sessions does not show
    // "Unassigned Cycle" and Imports rules still detect active counts.
    if (!meta || !Object.keys(meta).length) {
      const seed = findSessionSeedById(sessionId);
      if (seed) {
        parsed = parsed || {};
        meta = {
          ...(seed.meta || {}),
          sessionId,
          storeName: seed.storeName || seed.store || "",
          store: seed.store || seed.storeName || "",
          storeKey: seed.storeKey || seed.mapping?.systemSoh || "",
          cycleId: seed.cycleId || seed.cycleKey || seed.cycle?.cycleId || seed.cycle?.id || seed.meta?.cycleId || seed.meta?.cycleKey || "",
          cycleName: seed.cycleName || seed.cycle?.cycleName || seed.cycle?.name || seed.meta?.cycleName || seed.meta?.cycle?.name || "",
          cycleKey: seed.cycleKey || seed.cycleId || seed.meta?.cycleKey || "",
          cycle: seed.cycle || seed.meta?.cycle || undefined,
          sectionId: seed.sectionId || seed.meta?.sectionId || "",
          sectionName: seed.sectionName || seed.meta?.sectionName || "",
          fullSite: seed.fullSite === true || seed.meta?.fullSite === true,
          sessionType: seed.sessionType || seed.meta?.sessionType || "",
          countScope: seed.countScope || seed.meta?.countScope || "",
          startedBy: seed.startedBy || "User",
          lastEditedBy: seed.lastEditedBy || seed.startedBy || "User",
          startedAt: seed.startedAt || seed.createdAt || "",
        };
      }
    }

    if (!meta || !Object.keys(meta).length) return record;

    const preferMeta = (recordValue, metaValue) => {
      const cleanRecord = String(recordValue || "").trim();
      if (!cleanRecord || cleanRecord === "cycle" || cleanRecord === "section") return metaValue || recordValue || "";
      return recordValue;
    };

    const merged = {
      ...record,
      storeName: record.storeName || meta.storeName || meta.store || "",
      store: record.store || meta.store || meta.storeName || "",
      storeKey: record.storeKey || meta.storeKey || "",
      siteName: record.siteName || meta.siteName || meta.storeName || "",
      siteId: record.siteId || meta.siteId || "",
      siteKey: record.siteKey || meta.siteKey || "",
      cycleId: preferMeta(record.cycleId || record.cycleKey || record.cycle?.cycleId || record.cycle?.id, meta.cycleId || meta.cycleKey || meta.cycle?.cycleId || meta.cycle?.id),
      cycleName: preferMeta(record.cycleName || record.cycle?.cycleName || record.cycle?.name, meta.cycleName || meta.cycle?.cycleName || meta.cycle?.name),
      cycle: record.cycle || meta.cycle || undefined,
      sectionId: preferMeta(record.sectionId, meta.sectionId),
      sectionName: preferMeta(record.sectionName, meta.sectionName),
      sessionType: record.sessionType || meta.sessionType || "",
      countScope: record.countScope || meta.countScope || "",
      startedBy: record.startedBy || meta.startedBy || "User",
      lastEditedBy: record.lastEditedBy || meta.lastEditedBy || meta.startedBy || record.startedBy || "User",
      startedAt: record.startedAt || meta.startedAt || meta.createdAt || "",
      updatedAt: record.updatedAt || meta.updatedAt || meta.lastEditedAt || parsed.savedAt || "",
    };

    const canonicalSummary = meta?.sessionSummary && typeof meta.sessionSummary === "object"
      ? meta.sessionSummary
      : null;
    const normalizedRecordStatus = String(record?.status || "").trim().toLowerCase();
    const normalizedMetaStatus = String(meta?.status || "").trim().toLowerCase();
    const hasFrozenSummary = Boolean(
      meta?.sectionCompletedAt
      || record?.sectionCompletedAt
      || normalizedMetaStatus === "section complete"
      || normalizedMetaStatus === "completed"
      || normalizedRecordStatus === "section complete"
      || normalizedRecordStatus === "completed"
    );

    // A sessionSummary is a frozen/canonical value only after explicit completion.
    // While the count is Active the Sessions list is continuously updated from the
    // live detail page with the current exposure/flags. Re-applying an older summary
    // here was overwriting those live values with the session's initial R0 snapshot.
    if (canonicalSummary && hasFrozenSummary) {
      const canonicalExposure = Number(
        canonicalSummary.exposure
        ?? canonicalSummary.costExposure
        ?? canonicalSummary.finalExposure
        ?? meta.costExposure
        ?? meta.totalExposure
        ?? meta.exposure
        ?? 0
      );
      const canonicalFlags = Number(
        canonicalSummary.flaggedCount
        ?? canonicalSummary.flaggedItems
        ?? canonicalSummary.flaggedProducts
        ?? meta.flaggedCount
        ?? meta.flaggedProducts
        ?? 0
      );
      merged.sessionSummary = canonicalSummary;
      merged.costExposure = canonicalExposure;
      merged.totalExposure = canonicalExposure;
      merged.exposure = canonicalExposure;
      merged.finalExposure = Number(canonicalSummary.finalExposure ?? canonicalExposure);
      merged.flaggedCount = canonicalFlags;
      merged.flaggedProducts = Number(canonicalSummary.flaggedProducts ?? canonicalFlags);
      merged.completedLines = Number(canonicalSummary.completedLines ?? meta.completedLines ?? merged.completedLines ?? 0);
      merged.totalLines = Number(canonicalSummary.totalLines ?? meta.totalLines ?? merged.totalLines ?? 0);
    }

    const isFull = record.fullSite === true || meta.fullSite === true ||
      record.isFullSite === true || meta.isFullSite === true ||
      record.fullCount === true || meta.fullCount === true ||
      String(record.sessionType || record.countScope || meta.sessionType || meta.countScope || "").toLowerCase().includes("full");
    if (isFull) {
      merged.fullSite = true;
      merged.isFullSite = true;
      merged.fullCount = true;
      merged.sessionType = merged.sessionType || "full-count";
      merged.countScope = merged.countScope || "full-site";
      merged.sectionId = "";
      merged.sectionName = "";
    }

    return merged;
  } catch (_) {
    return record;
  }
}


export function saveSessionRecords(records) {
  try {
    const tenantOrgId = String(getMcActiveOrgId() || "").trim();
    const safeRecords = cleanActiveSessionRecords((Array.isArray(records) ? records : [])
      .map(hydrateSessionRecordMetadataFromDetail)
      .map((record) => ({ ...record, tenantOrgId: record?.tenantOrgId || tenantOrgId, orgId: record?.orgId || tenantOrgId })));
    const value = JSON.stringify(safeRecords);
    mcSetItemSilent(SESSION_LIST_KEY, value);
    mcSetCloudItem(SESSION_LIST_KEY, value).catch((err) => {
      console.warn("Morpheus Count could not immediately persist session list to Firestore", err);
    });
    try {
      window.localStorage?.setItem("__mc_session_local_pending", JSON.stringify({
        updatedAt: new Date().toISOString(),
        orgId: tenantOrgId,
        records: safeRecords,
      }));
    } catch (_) {}
    dispatchMcEvent(MC_SESSION_EVENT, { type: "active", records: safeRecords });
  } catch (err) {
    console.error("Failed to save MC session records", err);
  }
}

const ARCHIVE_INDEX_VERSION = 2;

function archiveIndexText(value = "") {
  return String(value ?? "").trim();
}

function archiveIndexNumber(...values) {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function archiveIndexNames(...sources) {
  for (const source of sources) {
    if (!Array.isArray(source)) continue;
    const names = Array.from(new Set(source.map((item) => archiveIndexText(item)).filter(Boolean)));
    if (names.length) return names.slice(0, 24);
  }
  return [];
}

function archiveIndexLatestCorrectionAt(record = {}, meta = {}) {
  const direct = archiveIndexText(record.correctedAt || meta.correctedAt || record.lastCorrectedAt || meta.lastCorrectedAt);
  if (direct) return direct;
  const versions = Array.isArray(record.correctionVersions)
    ? record.correctionVersions
    : (Array.isArray(meta.correctionVersions) ? meta.correctionVersions : []);
  const stamps = versions
    .map((item) => archiveIndexText(item?.correctedAt || item?.updatedAt || item?.createdAt || item?.at))
    .filter(Boolean)
    .sort();
  return stamps.length ? stamps[stamps.length - 1] : "";
}

// MC496 — mc-session-archive is a ROUTING / SUMMARY index, not historical
// evidence. Older rows inherited the entire live session projection through
// `{ ...target }`, which meant lifecycle arrays, correction history, nested cycle
// objects and migration-era payloads accumulated inside one shared document.
// The immutable final snapshot + individual archive/session documents already own
// the detailed evidence. Keep this list deliberately small and explicit so every
// Morpheus page can safely read it without downloading ~1 MiB of history.
function compactArchivedSessionRecordForList(record = {}) {
  if (!record || typeof record !== "object") return record;

  const detailMeta = record?.detailSnapshot?.meta && typeof record.detailSnapshot.meta === "object"
    ? record.detailSnapshot.meta
    : {};
  const meta = record?.meta && typeof record.meta === "object" ? record.meta : {};
  const mergedMeta = { ...meta, ...detailMeta };
  const lines = Array.isArray(record?.detailSnapshot?.lines)
    ? record.detailSnapshot.lines
    : (Array.isArray(record?.lines) ? record.lines : []);

  const sessionId = archiveIndexText(record.sessionId || mergedMeta.sessionId);
  const tenantOrgId = archiveIndexText(record.tenantOrgId || record.orgId || mergedMeta.tenantOrgId || mergedMeta.orgId || getMcActiveOrgId());
  const storeName = archiveIndexText(record.storeName || record.store || record.siteName || mergedMeta.storeName || mergedMeta.store);
  const store = archiveIndexText(record.store || record.storeName || mergedMeta.store || mergedMeta.storeName || storeName);
  const storeKey = archiveIndexText(record.storeKey || record.siteId || mergedMeta.storeKey || mergedMeta.siteId);
  const cycleId = archiveIndexText(record.cycleId || record.cycleKey || record.cycle?.cycleId || record.cycle?.id || mergedMeta.cycleId || mergedMeta.cycleKey || mergedMeta.cycle?.cycleId || mergedMeta.cycle?.id);
  const cycleName = archiveIndexText(record.cycleName || record.cycle?.cycleName || record.cycle?.name || mergedMeta.cycleName || mergedMeta.cycle?.cycleName || mergedMeta.cycle?.name);
  const fullSite = record.fullSite === true || record.isFullSite === true || record.fullCount === true || mergedMeta.fullSite === true || mergedMeta.isFullSite === true || mergedMeta.fullCount === true;
  const sectionId = fullSite ? "" : archiveIndexText(record.sectionId || record.ownerSectionId || record.countSectionId || mergedMeta.sectionId || mergedMeta.ownerSectionId || mergedMeta.countSectionId);
  const sectionName = fullSite ? "" : archiveIndexText(record.sectionName || record.ownerSectionName || record.countSectionName || mergedMeta.sectionName || mergedMeta.ownerSectionName || mergedMeta.countSectionName);
  const operationalDate = archiveIndexText(record.operationalDate || record.businessDate || record.countDate || mergedMeta.operationalDate || mergedMeta.businessDate || mergedMeta.countDate).slice(0, 10);
  const totalLines = archiveIndexNumber(record.totalLines, record.finalTotalLines, mergedMeta.totalLines, mergedMeta.sessionSummary?.totalLines, lines.length);
  const completedLines = archiveIndexNumber(record.completedLines, record.finalCompletedLines, mergedMeta.completedLines, mergedMeta.sessionSummary?.completedLines, mergedMeta.sessionSummary?.countedItems);
  const costExposure = archiveIndexNumber(record.costExposure, record.finalExposure, record.totalExposure, record.exposure, mergedMeta.costExposure, mergedMeta.finalExposure, mergedMeta.sessionSummary?.costExposure, mergedMeta.sessionSummary?.exposure);
  const flaggedCount = archiveIndexNumber(record.flaggedCount, record.finalFlagCount, record.flaggedProducts, mergedMeta.flaggedCount, mergedMeta.flaggedProducts, mergedMeta.sessionSummary?.flaggedCount, mergedMeta.sessionSummary?.flaggedItems);
  const correctedAt = archiveIndexLatestCorrectionAt(record, mergedMeta);
  const correctionCount = Math.max(
    archiveIndexNumber(record.correctionCount, record.correctionVersion, mergedMeta.correctionCount, mergedMeta.correctionVersion),
    Array.isArray(record.correctionVersions) ? record.correctionVersions.length : 0,
    Array.isArray(mergedMeta.correctionVersions) ? mergedMeta.correctionVersions.length : 0,
  );
  const restoreCount = Math.max(
    archiveIndexNumber(record.restoreCount, mergedMeta.restoreCount),
    Array.isArray(record.restoreAudit) ? record.restoreAudit.length : 0,
    Array.isArray(mergedMeta.restoreAudit) ? mergedMeta.restoreAudit.length : 0,
  );
  const countGroupNames = archiveIndexNames(record.countGroupNames, mergedMeta.countGroupNames);
  const categoryNames = archiveIndexNames(record.categoryNames, mergedMeta.categoryNames);
  const completedAt = archiveIndexText(record.completedAt || record.sectionCompletedAt || mergedMeta.completedAt || mergedMeta.sectionCompletedAt);
  const finalizedAt = archiveIndexText(record.finalizedAt || mergedMeta.finalizedAt);
  const archivedAt = archiveIndexText(record.archivedAt || mergedMeta.archivedAt || finalizedAt || record.updatedAt || mergedMeta.updatedAt);
  const archiveReason = archiveIndexText(record.archiveReason || record.reason || mergedMeta.archiveReason || "Archived session");
  const archiveTypeRaw = archiveIndexText(record.archiveType || mergedMeta.archiveType).toLowerCase();
  const archiveType = archiveTypeRaw === "completed" || finalizedAt || /completed|finalized/i.test(archiveReason)
    ? "completed"
    : "incomplete";

  const trustedSummary = record.archiveSummaryTrusted === true
    || Number(record.archiveIndexVersion || 0) >= ARCHIVE_INDEX_VERSION;

  return {
    archiveIndexVersion: trustedSummary ? ARCHIVE_INDEX_VERSION : Number(record.archiveIndexVersion || 1),
    archiveSummaryTrusted: trustedSummary,
    sessionId,
    tenantOrgId,
    orgId: archiveIndexText(record.orgId || tenantOrgId),
    status: "Archived",
    archived: true,
    archiveType,
    archiveReason,
    archivedAt,
    archivedBy: archiveIndexText(record.archivedBy || mergedMeta.archivedBy),
    finalizedAt,
    finalizedBy: archiveIndexText(record.finalizedBy || mergedMeta.finalizedBy),
    completedAt,
    completedBy: archiveIndexText(record.completedBy || mergedMeta.completedBy),
    verified: record.verified === true || record.isVerified === true || Boolean(record.verifiedAt || mergedMeta.verifiedAt),
    verificationStatus: archiveIndexText(record.verificationStatus || mergedMeta.verificationStatus),
    verifiedAt: archiveIndexText(record.verifiedAt || mergedMeta.verifiedAt || mergedMeta.verification?.verifiedAt),
    verifiedBy: archiveIndexText(record.verifiedBy || mergedMeta.verifiedBy || mergedMeta.verification?.verifiedBy),
    verifiedByProfileId: archiveIndexText(record.verifiedByProfileId || mergedMeta.verifiedByProfileId || mergedMeta.verification?.verifiedByProfileId),
    storeName,
    store,
    storeKey,
    siteId: archiveIndexText(record.siteId || mergedMeta.siteId || storeKey),
    cycleId,
    cycleKey: archiveIndexText(record.cycleKey || mergedMeta.cycleKey || cycleId),
    cycleName,
    sectionId,
    sectionName,
    fullSite,
    sessionType: archiveIndexText(record.sessionType || mergedMeta.sessionType || (fullSite ? "full-count" : "section")),
    countScope: archiveIndexText(record.countScope || mergedMeta.countScope || (fullSite ? "full-site" : "section")),
    categoryScopeLabel: archiveIndexText(record.categoryScopeLabel || mergedMeta.categoryScopeLabel || "All Count Groups"),
    countGroupNames,
    categoryNames,
    operationalDate,
    businessDate: operationalDate,
    countDate: operationalDate,
    startedBy: archiveIndexText(record.startedBy || record.createdBy || record.userName || mergedMeta.startedBy || mergedMeta.createdBy || "User"),
    lastEditedBy: archiveIndexText(record.lastEditedBy || mergedMeta.lastEditedBy),
    startedAt: archiveIndexText(record.startedAt || record.createdAt || mergedMeta.startedAt || mergedMeta.createdAt),
    createdAt: archiveIndexText(record.createdAt || mergedMeta.createdAt),
    updatedAt: archiveIndexText(record.updatedAt || mergedMeta.updatedAt || archivedAt),
    totalLines,
    completedLines: archiveType === "completed" && totalLines > 0 ? Math.max(completedLines, totalLines) : completedLines,
    finalTotalLines: totalLines,
    finalCompletedLines: archiveType === "completed" && totalLines > 0 ? Math.max(completedLines, totalLines) : completedLines,
    costExposure,
    totalExposure: archiveIndexNumber(record.totalExposure, record.exposure, costExposure),
    exposure: archiveIndexNumber(record.exposure, record.totalExposure, costExposure),
    finalExposure: archiveIndexNumber(record.finalExposure, costExposure),
    flaggedCount,
    flaggedProducts: archiveIndexNumber(record.flaggedProducts, flaggedCount),
    finalFlagCount: archiveIndexNumber(record.finalFlagCount, flaggedCount),
    correctedAt,
    correctionVersion: correctionCount,
    correctionCount,
    hasCorrections: Boolean(correctedAt || correctionCount > 0),
    restoredAt: archiveIndexText(record.restoredAt || mergedMeta.restoredAt),
    restoredBy: archiveIndexText(record.restoredBy || mergedMeta.restoredBy),
    restoreReason: archiveIndexText(record.restoreReason || mergedMeta.restoreReason),
    restoredFromArchive: record.restoredFromArchive === true || mergedMeta.restoredFromArchive === true || Boolean(record.restoredAt || mergedMeta.restoredAt),
    restoreCount,
    fileName: archiveIndexText(record.fileName || mergedMeta.fileName),
    finalSnapshotKey: archiveIndexText(record.finalSnapshotKey || mergedMeta.finalSnapshotKey),
    finalSnapshotCapturedAt: archiveIndexText(record.finalSnapshotCapturedAt || mergedMeta.finalSnapshotCapturedAt),
    finalSnapshotSourceRevision: archiveIndexNumber(record.finalSnapshotSourceRevision, mergedMeta.finalSnapshotSourceRevision),
    finalSnapshotDigest: archiveIndexText(record.finalSnapshotDigest || mergedMeta.finalSnapshotDigest),
    lifecycleState: archiveIndexText(record.lifecycleState || mergedMeta.lifecycleState || (finalizedAt ? "finalized" : "archived")),
  };
}

function compactArchivedSessionRecordsForList(records = []) {
  return (Array.isArray(records) ? records : [])
    .map(compactArchivedSessionRecordForList)
    .filter((record) => record?.sessionId);
}

function archivedRecordsFromStorageFallback() {
  if (typeof window === "undefined") return [];
  const rows = [];
  const seen = new Set();
  const readArea = (area) => {
    if (!area) return;
    for (let i = 0; i < area.length; i += 1) {
      const key = area.key(i);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      try {
        const raw = area.getItem(key);
        if (!raw) continue;
        if (key.startsWith("mc-archive-session:")) {
          const parsed = JSON.parse(raw);
          if (parsed?.sessionId) rows.push(parsed);
          continue;
        }
        if (key.startsWith("mc-session-engine:") || key.startsWith("mc-session-engine-")) {
          const parsed = JSON.parse(raw);
          const meta = parsed?.meta || {};
          const sessionId = meta.sessionId || key.replace(/^mc-session-engine[:\-]/, "");
          const archived = meta.archived === true || meta.archivedAt || String(meta.status || "").toLowerCase() === "archived";
          if (!sessionId || !archived) continue;
          const lines = Array.isArray(parsed?.lines) ? parsed.lines : [];
          rows.push({
            sessionId,
            status: "Archived",
            archived: true,
            archivedAt: meta.archivedAt || parsed.savedAt || meta.updatedAt || new Date().toISOString(),
            archiveReason: meta.archiveReason || "Archived session",
            archiveType: String(meta.archiveType || meta.status || "").toLowerCase() === "completed" ? "completed" : "incomplete",
            storeName: meta.storeName || meta.store || "",
            store: meta.store || meta.storeName || "",
            cycleId: meta.cycleId || meta.cycleKey || meta.cycle?.cycleId || meta.cycle?.id || "",
            cycleName: meta.cycleName || meta.cycle?.cycleName || meta.cycle?.name || "",
            cycle: meta.cycle || undefined,
            sectionId: meta.sectionId || "",
            sectionName: meta.sectionName || "",
            fullSite: meta.fullSite === true,
            sessionType: meta.sessionType || (meta.fullSite === true ? "full-count" : "section"),
            countScope: meta.countScope || (meta.fullSite === true ? "full-site" : "section"),
            startedBy: meta.startedBy || meta.createdBy || "User",
            lastEditedBy: meta.lastEditedBy || meta.startedBy || meta.createdBy || "User",
            startedAt: meta.startedAt || meta.createdAt || parsed.savedAt || new Date().toISOString(),
            updatedAt: meta.updatedAt || parsed.savedAt || meta.archivedAt || new Date().toISOString(),
            fileName: meta.fileName || "",
            totalLines: Number(meta.totalLines || lines.length || 0),
            completedLines: Number(meta.completedLines || lines.filter((line) => line && (line.counted !== "" || line.waste !== "" || String(line.note || line.notes || "").trim())).length || 0),
            flaggedCount: Number(meta.flaggedCount || 0),
            costExposure: Number(meta.costExposure || 0),
          });
        }
      } catch (_) {}
    }
  };
  try { readArea(window.localStorage); } catch (_) {}
  try { readArea(window.sessionStorage); } catch (_) {}
  return rows;
}

export function loadArchivedSessionRecords() {
  try {
    const raw = mcGetItem(ARCHIVE_LIST_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    const listRows = Array.isArray(parsed) ? parsed : [];
    const fallbackRows = archivedRecordsFromStorageFallback();
    const activeOrgId = String(getMcActiveOrgId() || "").trim();
    // MC497 — the compact archive index is the authority when both the index and
    // legacy per-session/browser fallbacks contain the same session. The old merge
    // order placed fallbackRows last, so stale legacy rows overwrote the newly
    // migrated v2 rows and stripped archiveIndexVersion/archiveSummaryTrusted.
    // Archives then believed migration had never completed and re-downloaded every
    // immutable final snapshot on each refresh event. Put fallbacks first so they
    // only fill missing sessions; listRows always win for duplicate sessionIds.
    const merged = [...fallbackRows, ...listRows]
      .filter((item) => item?.sessionId)
      .filter((item) => {
        const stampedOrgId = String(item?.tenantOrgId || item?.orgId || "").trim();
        return Boolean(activeOrgId && stampedOrgId && stampedOrgId === activeOrgId);
      });
    return Array.from(new Map(merged.map((item) => [item.sessionId, item])).values()).sort(
      (a, b) => new Date(b.archivedAt || b.updatedAt || 0).getTime() - new Date(a.archivedAt || a.updatedAt || 0).getTime()
    );
  } catch (err) {
    console.error("Failed to load MC archived sessions", err);
    return archivedRecordsFromStorageFallback();
  }
}

export function saveArchivedSessionRecords(records) {
  try {
    const tenantOrgId = String(getMcActiveOrgId() || "").trim();
    const compactRecords = compactArchivedSessionRecordsForList(records)
      .map((record) => ({ ...record, tenantOrgId, orgId: record?.orgId || tenantOrgId }));
    const value = JSON.stringify(compactRecords);
    mcSetItemSilent(ARCHIVE_LIST_KEY, value);
    mcSetCloudItem(ARCHIVE_LIST_KEY, value).catch((err) => {
      console.warn("Morpheus Count could not immediately persist archive list to Firestore", err);
    });
    dispatchMcEvent(MC_SESSION_EVENT, { type: "archived", records: compactRecords });
  } catch (err) {
    console.error("Failed to save MC archived sessions", err);
  }
}


export async function saveSessionRecordsDurable(records) {
  const tenantOrgId = String(getMcActiveOrgId() || "").trim();
  const safeRecords = cleanActiveSessionRecords((Array.isArray(records) ? records : [])
    .map(hydrateSessionRecordMetadataFromDetail)
    .map((record) => ({ ...record, tenantOrgId: record?.tenantOrgId || tenantOrgId, orgId: record?.orgId || tenantOrgId })));
  const value = JSON.stringify(safeRecords);
  mcSetItemSilent(SESSION_LIST_KEY, value);
  const sessionListSaved = await mcSetCloudItemImmediate(SESSION_LIST_KEY, value);
  if (sessionListSaved !== true) {
    throw new Error("Morpheus could not confirm the active Sessions index in Firestore.");
  }
  try {
    window.localStorage?.setItem("__mc_session_local_pending", JSON.stringify({
      updatedAt: new Date().toISOString(),
      orgId: tenantOrgId,
      records: safeRecords,
    }));
  } catch (_) {}
  dispatchMcEvent(MC_SESSION_EVENT, { type: "active", records: safeRecords });
  return safeRecords;
}

export async function upsertSessionRecordDurable(record) {
  if (isArchivedLikeSessionRecord(record)) return loadSessionRecords();
  const current = loadSessionRecords();
  const next = [record, ...current.filter((item) => item.sessionId !== record.sessionId)].sort(
    (a, b) => new Date(b.updatedAt || b.startedAt || 0).getTime() - new Date(a.updatedAt || a.startedAt || 0).getTime()
  );
  return saveSessionRecordsDurable(next);
}

export function upsertSessionRecord(record) {
  if (isArchivedLikeSessionRecord(record)) return loadSessionRecords();
  const current = loadSessionRecords();
  const next = [record, ...current.filter((item) => item.sessionId !== record.sessionId)].sort(
    (a, b) => new Date(b.updatedAt || b.startedAt || 0).getTime() - new Date(a.updatedAt || a.startedAt || 0).getTime()
  );
  saveSessionRecords(next);
  return next;
}

function localOperationalDateFromRecord(record = {}, meta = {}) {
  const explicit = String(
    record?.operationalDate
      || record?.businessDate
      || record?.countDate
      || meta?.operationalDate
      || meta?.businessDate
      || meta?.countDate
      || ""
  ).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(explicit)) return explicit;

  const raw = record?.startedAt || record?.createdAt || meta?.startedAt || meta?.createdAt || record?.updatedAt;
  const date = raw ? new Date(raw) : new Date();
  const safe = Number.isNaN(date.getTime()) ? new Date() : date;
  const year = safe.getFullYear();
  const month = String(safe.getMonth() + 1).padStart(2, "0");
  const day = String(safe.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export async function archiveSessionRecord(sessionId, archiveReason = "Manual archive", forcedSnapshot = null, forcedTarget = null) {
  const localActive = hydrateSessionRecordsFromSeeds();
  const archived = loadArchivedSessionRecords();
  const cleanSessionId = String(sessionId || "").trim();

  // MC399 — archive/finalize must never derive the next shared session-list from a
  // partially hydrated browser cache. P5.3 exposed a dangerous edge case: when a
  // page had only the forced target available locally, finalizing that target could
  // persist [] to mc-session-list and make every other live session disappear.
  // Hydrate the active list from the org-scoped Firestore document before removing
  // exactly this session. Browser state is fallback only.
  let cloudActive = [];
  let cloudSessionListRead = false;
  try {
    const rawCloudActive = await mcGetCloudItem(SESSION_LIST_KEY);
    cloudSessionListRead = true;
    const parsedCloudActive = rawCloudActive ? JSON.parse(rawCloudActive) : [];
    cloudActive = Array.isArray(parsedCloudActive) ? cleanActiveSessionRecords(parsedCloudActive) : [];
  } catch (_) {
    cloudSessionListRead = false;
    cloudActive = [];
  }
  const active = cloudActive.length ? cloudActive : localActive;
  const fallbackTarget = forcedTarget && forcedTarget.sessionId === cleanSessionId ? forcedTarget : null;
  let target = active.find((item) => item.sessionId === cleanSessionId) || fallbackTarget;
  if (!target) {
    try {
      const snapshot = await loadSessionDetailSnapshotForRestore(cleanSessionId, {});
      const meta = snapshot?.meta || {};
      if (snapshot && Array.isArray(snapshot.lines)) {
        target = {
          sessionId: cleanSessionId,
          status: meta.status || "Active",
          storeName: meta.storeName || meta.store || "",
          store: meta.store || meta.storeName || "",
          cycleId: meta.cycleId || meta.cycleKey || meta.cycle?.cycleId || meta.cycle?.id || "",
          cycleName: meta.cycleName || meta.cycle?.cycleName || meta.cycle?.name || "",
          cycle: meta.cycle || undefined,
          sectionId: meta.sectionId || "",
          sectionName: meta.sectionName || "",
          fullSite: meta.fullSite === true,
          sessionType: meta.sessionType || (meta.fullSite === true ? "full-count" : "section"),
          countScope: meta.countScope || (meta.fullSite === true ? "full-site" : "section"),
          startedBy: meta.startedBy || meta.createdBy || "User",
          lastEditedBy: meta.lastEditedBy || meta.startedBy || meta.createdBy || "User",
          startedAt: meta.startedAt || meta.createdAt || snapshot.savedAt || new Date().toISOString(),
          updatedAt: snapshot.savedAt || meta.updatedAt || new Date().toISOString(),
          fileName: meta.fileName || "",
          totalLines: snapshot.lines.length,
          completedLines: snapshot.lines.filter((line) => line && (line.counted !== "" || line.waste !== "" || String(line.note || line.notes || "").trim())).length,
          flaggedCount: Number(meta.flaggedCount || 0),
          costExposure: Number(meta.costExposure || 0),
        };
        forcedSnapshot = forcedSnapshot || snapshot;
      }
    } catch (_) {}
  }
  if (!target) return { active, archived };

  // P5.1 — Finalization is a hard canonical-ledger boundary, not merely an
  // archive-list operation. Lock the verified completed ledger in Firestore
  // before removing it from Live Sessions. If the lock cannot be acknowledged,
  // abort finalization rather than creating an archive from uncertain state.
  const isFinalizationRequest = /^finalized\b/i.test(String(archiveReason || "").trim());
  let immutableFinalSnapshot = null;
  let immutableFinalSnapshotKey = "";
  if (isFinalizationRequest) {
    const ledgerKey = `mc-count-ledger:${cleanSessionId}`;
    let ledgerValue = "";
    try { ledgerValue = await mcGetCloudItem(ledgerKey); } catch (_) {}
    if (!ledgerValue) ledgerValue = mcGetItem(ledgerKey) || "";

    // P5.3 hardening — some sessions created during the architecture migration can
    // have a durable completed session-detail document but no usable canonical
    // ledger document. Finalization must not crash with invalid-payload in that
    // case. Rebuild the missing ledger candidate ONCE from durable session detail,
    // carrying the already-acknowledged Complete/Verified lifecycle metadata from
    // Sessions. The transaction below still decides whether the finalization lock
    // is legal, and Firestore becomes authoritative immediately after this repair.
    let ledgerCandidate = ledgerValue;
    let parsedLedgerCandidate = null;
    try { parsedLedgerCandidate = ledgerCandidate ? JSON.parse(ledgerCandidate) : null; } catch (_) {}
    if (!parsedLedgerCandidate || !Array.isArray(parsedLedgerCandidate?.lines)) {
      try {
        const restored = await loadSessionDetailSnapshotForRestore(cleanSessionId, target || {});
        if (restored && Array.isArray(restored?.lines)) {
          const restoredMeta = { ...(restored.meta || {}) };
          restoredMeta.sessionId = restoredMeta.sessionId || cleanSessionId;
          restoredMeta.status = "Completed";
          restoredMeta.completedAt = restoredMeta.completedAt || target?.completedAt || target?.sectionCompletedAt || target?.updatedAt || new Date().toISOString();
          restoredMeta.sectionCompletedAt = restoredMeta.sectionCompletedAt || target?.sectionCompletedAt || restoredMeta.completedAt;
          const targetVerified = Boolean(
            target?.verified === true || target?.isVerified === true || target?.verifiedAt ||
            String(target?.verificationStatus || "").toLowerCase() === "verified"
          );
          if (targetVerified) {
            restoredMeta.verificationStatus = "verified";
            restoredMeta.verifiedAt = restoredMeta.verifiedAt || target?.verifiedAt || target?.updatedAt || new Date().toISOString();
            restoredMeta.verifiedBy = restoredMeta.verifiedBy || target?.verifiedBy || target?.lastEditedBy || target?.startedBy || "Administrator";
            restoredMeta.verifiedByProfileId = restoredMeta.verifiedByProfileId || target?.verifiedByProfileId || "";
          }
          ledgerCandidate = JSON.stringify({ ...restored, meta: restoredMeta });
        }
      } catch (err) {
        console.warn("Could not rebuild missing canonical ledger before finalization", { sessionId: cleanSessionId, err });
      }
    }

    // MC406 — a restored count can be corrected after its original canonical ledger
    // snapshot was created. Session detail is the durable working document for those
    // audited corrections/restores, so promote it into the ledger candidate when it
    // is newer than the ledger. This ensures re-finalization freezes the latest
    // corrected exposure, lines and lifecycle instead of the pre-restore version.
    try {
      const latestDetail = await loadSessionDetailSnapshotForRestore(cleanSessionId, target || {});
      if (latestDetail && Array.isArray(latestDetail?.lines)) {
        let parsedCandidate = null;
        try { parsedCandidate = ledgerCandidate ? JSON.parse(ledgerCandidate) : null; } catch (_) {}
        const stamp = (payload) => {
          const m = payload?.meta || {};
          const raw = m.correctedAt || m.lastEditedAt || m.restoredAt || payload?.savedAt || m.updatedAt || m.completedAt || m.createdAt || '';
          const value = raw ? new Date(raw).getTime() : 0;
          return Number.isFinite(value) ? value : 0;
        };
        if (!parsedCandidate || stamp(latestDetail) >= stamp(parsedCandidate)) {
          const promotedMeta = { ...(latestDetail.meta || {}) };
          promotedMeta.sessionId = promotedMeta.sessionId || cleanSessionId;
          promotedMeta.status = 'Completed';
          promotedMeta.completedAt = promotedMeta.completedAt || target?.completedAt || target?.sectionCompletedAt || latestDetail.savedAt || new Date().toISOString();
          promotedMeta.sectionCompletedAt = promotedMeta.sectionCompletedAt || target?.sectionCompletedAt || promotedMeta.completedAt;
          const targetVerified = Boolean(target?.verified === true || target?.isVerified === true || target?.verifiedAt || String(target?.verificationStatus || '').toLowerCase() === 'verified');
          if (targetVerified) {
            promotedMeta.verificationStatus = 'verified';
            promotedMeta.verifiedAt = promotedMeta.verifiedAt || target?.verifiedAt || target?.updatedAt || new Date().toISOString();
            promotedMeta.verifiedBy = promotedMeta.verifiedBy || target?.verifiedBy || target?.lastEditedBy || target?.startedBy || 'Administrator';
          }
          ledgerCandidate = JSON.stringify({ ...latestDetail, meta: promotedMeta });
        }
      }
    } catch (err) {
      console.warn('Could not promote latest corrected session detail before finalization', { sessionId: cleanSessionId, err });
    }

    // MC433 — Finalize Day / bulk finalization may arrive with a stale projection
    // that has enough summary metadata to render in Sessions but no row-bearing
    // ledger payload in this browser. Single-count finalization often escapes this
    // because its detail page is already hydrated. Before every finalization lock,
    // guarantee that a canonical ledger exists (including the explicit legacy
    // zero-row terminal escape hatch) instead of sending an empty candidate to the
    // transaction and failing with `invalid-payload`. This runs only when the
    // candidate is missing/invalid, so healthy modern sessions do not incur another
    // write.
    try {
      let parsedCandidate = null;
      try { parsedCandidate = ledgerCandidate ? JSON.parse(ledgerCandidate) : null; } catch (_) {}
      if (!parsedCandidate || !Array.isArray(parsedCandidate?.lines)) {
        const ensured = await ensureCanonicalCountLedger(cleanSessionId, target || {});
        if (ensured?.ok && ensured?.value) {
          ledgerCandidate = ensured.value;
        }
      }
    } catch (err) {
      console.warn('Could not ensure canonical ledger before finalization lock', { sessionId: cleanSessionId, err });
    }

    const lockResult = await mcTransitionCountLedger(
      ledgerKey,
      ledgerCandidate,
      "finalize-lock",
      { name: target.verifiedBy || target.lastEditedBy || target.startedBy || "Administrator", profileId: target.verifiedByProfileId || "" }
    );
    if (!lockResult?.ok) {
      const error = new Error(`Canonical finalization lock failed: ${lockResult?.reason || "unknown"}`);
      error.code = "MC_FINALIZATION_LOCK_FAILED";
      throw error;
    }
    if (lockResult?.payload) forcedSnapshot = lockResult.payload;

    // P5.2 — before the operational ledger can leave Sessions, persist a separate
    // write-once historical snapshot. Finalization is aborted if Firestore cannot
    // acknowledge this evidence record. Archive/history will consume this source
    // directly in P5.3; for now we retain the existing archive projection too.
    // MC407 — each successful finalization is immutable evidence, but a restored +
    // corrected count creates a NEW authoritative finalization. Never reuse the
    // original session-level snapshot key: its write-once semantics correctly keep
    // the original evidence and would otherwise make Archives snap back to it.
    // Point the archive projection at a revision-specific immutable snapshot instead.
    const finalRevision = Number(
      lockResult?.payload?.revision ||
      lockResult?.payload?.meta?.finalizationRevision ||
      lockResult?.payload?.meta?.lifecycleRevision ||
      Date.now()
    );
    immutableFinalSnapshotKey = `mc-final-count-snapshot:${cleanSessionId}:r${finalRevision}`;
    const finalSnapshotResult = await mcCreateImmutableFinalSnapshot(
      immutableFinalSnapshotKey,
      lockResult.payload || forcedSnapshot,
      {
        name: lockResult?.payload?.meta?.finalizedBy || target.verifiedBy || target.lastEditedBy || target.startedBy || "Administrator",
        profileId: lockResult?.payload?.meta?.finalizedByProfileId || target.verifiedByProfileId || "",
      }
    );
    if (!finalSnapshotResult?.ok) {
      const error = new Error(`Immutable final snapshot failed: ${finalSnapshotResult?.reason || "unknown"}`);
      error.code = "MC_FINAL_SNAPSHOT_FAILED";
      throw error;
    }
    immutableFinalSnapshot = finalSnapshotResult.payload || null;
  }

  // Archive is a state transition. Mark it immediately so any in-flight
  // Firestore snapshot or merged session-list write cannot resurrect this
  // session back into Live Sessions while the archive write is still finishing.
  markSessionArchivedForSync(cleanSessionId);

  let detailSnapshot = forcedSnapshot && Array.isArray(forcedSnapshot?.lines) ? forcedSnapshot : null;
  if (!detailSnapshot) {
    try {
      detailSnapshot = await loadSessionDetailSnapshotForRestore(cleanSessionId, target);
    } catch (err) {
      console.warn("Failed to snapshot MC session detail before archive", err);
    }
  }

  const snapshotLines = Array.isArray(detailSnapshot?.lines) ? detailSnapshot.lines : [];
  const snapshotMeta = detailSnapshot?.meta && typeof detailSnapshot.meta === "object" ? detailSnapshot.meta : {};
  const totalLines = snapshotLines.length || Number(target.totalLines || 0);
  const completedLines = snapshotLines.length
    ? snapshotLines.filter((line) => line && (line.counted !== "" || line.waste !== "" || String(line.note || line.notes || "").trim())).length
    : Number(target.completedLines || 0);

  const archivedAt = new Date().toISOString();
  const operationalDate = localOperationalDateFromRecord(target, snapshotMeta);
  const isFinalization = /^finalized\b/i.test(String(archiveReason || "").trim());
  const finalizedAt = isFinalization ? archivedAt : (target.finalizedAt || snapshotMeta.finalizedAt || null);
  const finalizedBy = isFinalization
    ? (target.finalizedBy || snapshotMeta.finalizedBy || target.verifiedBy || snapshotMeta.verifiedBy || target.lastEditedBy || snapshotMeta.lastEditedBy || target.startedBy || snapshotMeta.startedBy || "Administrator")
    : (target.finalizedBy || snapshotMeta.finalizedBy || "");
  const existingLifecycleEvents = Array.isArray(snapshotMeta.lifecycleEvents)
    ? snapshotMeta.lifecycleEvents
    : (Array.isArray(target.lifecycleEvents) ? target.lifecycleEvents : []);
  const finalizationEvent = isFinalization ? {
    id: `finalized-${Date.now()}`,
    type: "finalized",
    at: finalizedAt,
    actor: finalizedBy,
    detail: "The count was locked as the authoritative operational result.",
  } : null;
  const nextLifecycleEvents = finalizationEvent
    ? [...existingLifecycleEvents, finalizationEvent]
    : existingLifecycleEvents;
  const archivedDetailSnapshot = detailSnapshot ? {
    ...detailSnapshot,
    meta: {
      ...snapshotMeta,
      sessionId: cleanSessionId,
      status: "Archived",
      archivedAt,
      archiveReason,
      finalizedAt,
      finalizedBy,
      finalSnapshotKey: immutableFinalSnapshotKey || snapshotMeta.finalSnapshotKey || "",
      finalSnapshotCapturedAt: immutableFinalSnapshot?.capturedAt || snapshotMeta.finalSnapshotCapturedAt || "",
      finalSnapshotSourceRevision: Number(immutableFinalSnapshot?.sourceLedgerRevision || snapshotMeta.finalSnapshotSourceRevision || 0),
      finalSnapshotDigest: immutableFinalSnapshot?.sourceLedgerDigest || snapshotMeta.finalSnapshotDigest || "",
      lifecycleEvents: nextLifecycleEvents,
      operationalDate,
      businessDate: operationalDate,
      countDate: operationalDate,
      store: target.store || snapshotMeta.store || snapshotMeta.storeName || target.storeName || "",
      storeName: target.storeName || snapshotMeta.storeName || snapshotMeta.store || target.store || "",
      cycleId: target.cycleId || target.cycleKey || target.cycle?.cycleId || target.cycle?.id || snapshotMeta.cycleId || snapshotMeta.cycleKey || snapshotMeta.cycle?.cycleId || snapshotMeta.cycle?.id || "",
      cycleName: target.cycleName || target.cycle?.cycleName || target.cycle?.name || snapshotMeta.cycleName || snapshotMeta.cycle?.cycleName || snapshotMeta.cycle?.name || "",
      cycle: target.cycle || snapshotMeta.cycle || undefined,
      sectionId: target.fullSite === true || snapshotMeta.fullSite === true ? "" : (target.sectionId || snapshotMeta.sectionId || ""),
      sectionName: target.fullSite === true || snapshotMeta.fullSite === true ? "" : (target.sectionName || snapshotMeta.sectionName || ""),
      fullSite: target.fullSite === true || snapshotMeta.fullSite === true,
      sessionType: target.sessionType || snapshotMeta.sessionType || ((target.fullSite === true || snapshotMeta.fullSite === true) ? "full-count" : "section"),
      countScope: target.countScope || snapshotMeta.countScope || ((target.fullSite === true || snapshotMeta.fullSite === true) ? "full-site" : "section"),
    },
    lines: snapshotLines,
    savedAt: detailSnapshot.savedAt || target.updatedAt || archivedAt,
  } : null;

  if (archivedDetailSnapshot) {
    mcSetItem(sessionEngineStorageKeyFor(cleanSessionId), JSON.stringify(archivedDetailSnapshot));
  }

  const archivedRecord = {
    ...target,
    // MC496 — this projection is built from the exact archive/finalization snapshot
    // above, so its compact index summary is safe to trust without re-downloading
    // the full immutable snapshot on every Archives visit.
    archiveSummaryTrusted: true,
    archiveIndexVersion: ARCHIVE_INDEX_VERSION,
    storeName: target.storeName || snapshotMeta.storeName || snapshotMeta.store || target.store || "",
    store: target.store || snapshotMeta.store || snapshotMeta.storeName || target.storeName || "",
    cycleId: target.cycleId || target.cycleKey || target.cycle?.cycleId || target.cycle?.id || snapshotMeta.cycleId || snapshotMeta.cycleKey || snapshotMeta.cycle?.cycleId || snapshotMeta.cycle?.id || "",
    cycleName: target.cycleName || target.cycle?.cycleName || target.cycle?.name || snapshotMeta.cycleName || snapshotMeta.cycle?.cycleName || snapshotMeta.cycle?.name || "",
    cycle: target.cycle || snapshotMeta.cycle || undefined,
    sectionId: target.fullSite === true || snapshotMeta.fullSite === true ? "" : (target.sectionId || snapshotMeta.sectionId || ""),
    sectionName: target.fullSite === true || snapshotMeta.fullSite === true ? "" : (target.sectionName || snapshotMeta.sectionName || ""),
    fullSite: target.fullSite === true || snapshotMeta.fullSite === true,
    sessionType: target.sessionType || snapshotMeta.sessionType || ((target.fullSite === true || snapshotMeta.fullSite === true) ? "full-count" : "section"),
    countScope: target.countScope || snapshotMeta.countScope || ((target.fullSite === true || snapshotMeta.fullSite === true) ? "full-site" : "section"),
    operationalDate,
    businessDate: operationalDate,
    countDate: operationalDate,
    totalLines,
    completedLines,
    // MC406 — archive projection must mirror the frozen detail/final snapshot,
    // not a potentially stale session-list summary from before restore/correction.
    costExposure: Number(snapshotMeta.costExposure ?? snapshotMeta.sessionSummary?.costExposure ?? snapshotMeta.sessionSummary?.exposure ?? target.costExposure ?? target.finalExposure ?? 0),
    totalExposure: Number(snapshotMeta.totalExposure ?? snapshotMeta.sessionSummary?.exposure ?? snapshotMeta.sessionSummary?.costExposure ?? target.totalExposure ?? target.costExposure ?? 0),
    exposure: Number(snapshotMeta.exposure ?? snapshotMeta.sessionSummary?.exposure ?? snapshotMeta.sessionSummary?.costExposure ?? target.exposure ?? target.costExposure ?? 0),
    finalExposure: Number(snapshotMeta.finalExposure ?? snapshotMeta.sessionSummary?.finalExposure ?? snapshotMeta.sessionSummary?.exposure ?? target.finalExposure ?? target.costExposure ?? 0),
    flaggedCount: Number(snapshotMeta.flaggedCount ?? snapshotMeta.sessionSummary?.flaggedCount ?? snapshotMeta.sessionSummary?.flaggedItems ?? target.flaggedCount ?? 0),
    flaggedProducts: Number(snapshotMeta.flaggedProducts ?? snapshotMeta.sessionSummary?.flaggedProducts ?? snapshotMeta.sessionSummary?.flaggedCount ?? target.flaggedProducts ?? target.flaggedCount ?? 0),
    correctionVersions: Array.isArray(snapshotMeta.correctionVersions) ? snapshotMeta.correctionVersions : (Array.isArray(target.correctionVersions) ? target.correctionVersions : []),
    restoreAudit: Array.isArray(snapshotMeta.restoreAudit) ? snapshotMeta.restoreAudit : (Array.isArray(target.restoreAudit) ? target.restoreAudit : []),
    restoredAt: snapshotMeta.restoredAt || target.restoredAt || null,
    restoredBy: snapshotMeta.restoredBy || target.restoredBy || "",
    restoreReason: snapshotMeta.restoreReason || target.restoreReason || "",
    status: "Archived",
    archived: true,
    archivedAt,
    archiveReason,
    finalizedAt,
    finalizedBy,
    finalSnapshotKey: immutableFinalSnapshotKey || snapshotMeta.finalSnapshotKey || "",
    finalSnapshotCapturedAt: immutableFinalSnapshot?.capturedAt || snapshotMeta.finalSnapshotCapturedAt || "",
    finalSnapshotSourceRevision: Number(immutableFinalSnapshot?.sourceLedgerRevision || snapshotMeta.finalSnapshotSourceRevision || 0),
    finalSnapshotDigest: immutableFinalSnapshot?.sourceLedgerDigest || snapshotMeta.finalSnapshotDigest || "",
    lifecycleEvents: nextLifecycleEvents,
    archiveType: String(target.status || "Active").toLowerCase() === "completed" ? "completed" : "incomplete",
  };

  try {
    appendVarianceHistoryForArchivedSession(archivedRecord, archivedDetailSnapshot || detailSnapshot || { lines: snapshotLines, meta: snapshotMeta });
  } catch (err) {
    console.warn("Morpheus Count could not update accountability history", err);
  }

  // Response Designed notifications are completion-only. Replaced/abandoned
  // active counts are archived as incomplete for audit history, but must never
  // create WhatsApp/email inventory alerts.
  if (archivedRecord.archiveType === "completed") {
    try {
      appendResponseEventFromSession(archivedRecord, archivedDetailSnapshot || detailSnapshot || { lines: snapshotLines, meta: snapshotMeta });
    } catch (err) {
      console.warn("Morpheus Count could not create response event", err);
    }
  }

  const individualArchiveRecord = compactArchivedSessionRecordForList(archivedRecord);
  mcSetItem(archivedSessionStorageKeyFor(cleanSessionId), JSON.stringify(individualArchiveRecord));

  const nextActive = active.filter((item) => item.sessionId !== cleanSessionId);
  const nextArchived = [archivedRecord, ...archived.filter((item) => item.sessionId !== cleanSessionId)].sort(
    (a, b) => new Date(b.archivedAt || b.updatedAt || 0).getTime() - new Date(a.archivedAt || a.updatedAt || 0).getTime()
  );

  saveSessionRecords(nextActive);
  saveArchivedSessionRecords(nextArchived);

  // If neither Firestore nor the tenant-local list contained any active rows and
  // we reached this function only through forcedTarget, we do NOT publish an empty
  // shared session-list. The archive list itself suppresses the finalized target,
  // while leaving the existing cloud discovery document untouched protects any
  // other sessions that this browser has not hydrated yet.
  const safeToPersistActiveList = active.length > 0 || (cloudSessionListRead && cloudActive.length > 0);

  // Archive persistence safety:
  // Keep the archive list small (summary only) and write it directly to Firestore.
  // The full lines stay in the session-engine doc above. This prevents a refresh
  // from rehydrating the older cloud archive list and making the finalized session
  // appear to disappear again when browser cache is full or the archive list is near
  // Firestore's document size limit.
  try {
    await withMcTimeout(Promise.all([
      safeToPersistActiveList ? persistMcCloudItemFast(SESSION_LIST_KEY, JSON.stringify(nextActive)) : Promise.resolve(false),
      persistMcCloudItemFast(ARCHIVE_LIST_KEY, JSON.stringify(compactArchivedSessionRecordsForList(nextArchived))),
      persistMcCloudItemFast(archivedSessionStorageKeyFor(cleanSessionId), JSON.stringify(individualArchiveRecord)),
      archivedDetailSnapshot ? persistMcCloudItemFast(sessionEngineStorageKeyFor(cleanSessionId), JSON.stringify(archivedDetailSnapshot)) : Promise.resolve(false),
    ]), 4500, false);
  } catch (err) {
    console.warn("Morpheus Count archive direct cloud persistence failed", err);
  }

  removeSessionSeedById(cleanSessionId);
  return { active: nextActive, archived: compactArchivedSessionRecordsForList(nextArchived) };
}


export function permanentDeleteArchivedSessionRecord(sessionId) {
  const cleanId = String(sessionId || "").trim();
  if (!cleanId) return { active: loadSessionRecords(), archived: loadArchivedSessionRecords() };

  const nextArchived = loadArchivedSessionRecords().filter((item) => item?.sessionId !== cleanId);
  const nextActive = loadSessionRecords().filter((item) => item?.sessionId !== cleanId);

  saveArchivedSessionRecords(nextArchived);
  saveSessionRecords(nextActive);
  removeSessionSeedById(cleanId);
  mcRemoveItem(sessionEngineStorageKeyFor(cleanId));
  mcRemoveItem(legacySessionEngineStorageKeyFor(cleanId));
  mcRemoveItem(archivedSessionStorageKeyFor(cleanId));
  dispatchMcEvent(MC_SESSION_EVENT, { type: "permanent-delete-archive", sessionId: cleanId });

  return { active: nextActive, archived: nextArchived };
}

export function permanentClearArchivedSessionRecords() {
  const archived = loadArchivedSessionRecords();
  const archivedIds = archived.map((item) => item?.sessionId).filter(Boolean);

  archivedIds.forEach((sessionId) => {
    removeSessionSeedById(sessionId);
    mcRemoveItem(sessionEngineStorageKeyFor(sessionId));
    mcRemoveItem(legacySessionEngineStorageKeyFor(sessionId));
  });

  const activeIds = new Set(loadSessionRecords().map((item) => item?.sessionId).filter(Boolean));
  const nextActive = loadSessionRecords().filter((item) => !archivedIds.includes(item?.sessionId));

  if (nextActive.length !== activeIds.size) saveSessionRecords(nextActive);
  saveArchivedSessionRecords([]);
  mcRemoveItem(ARCHIVE_LIST_KEY);
  dispatchMcEvent(MC_SESSION_EVENT, { type: "permanent-clear-archives" });

  return { active: nextActive, archived: [] };
}



function restoreOperationalValue(value = "") {
  return String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
}

function restoreOperationalDate(session = {}) {
  return String(
    session.operationalDate || session.businessDate || session.countDate ||
    session.startedAt || session.createdAt || session.updatedAt || ""
  ).slice(0, 10);
}

function restoreOperationalKey(session = {}) {
  const site = restoreOperationalValue(session.storeKey || session.siteId || session.storeName || session.store || session.siteName);
  const cycle = restoreOperationalValue(session.cycleId || session.cycleKey || session.cycle?.cycleId || session.cycle?.id || session.cycleName || session.cycle?.name);
  const isFull = session.fullSite === true || session.isFullSite === true || session.fullCount === true || restoreOperationalValue(session.sessionType || session.countScope).includes("full");
  const section = isFull ? "full-count" : restoreOperationalValue(session.sectionId || session.ownerSectionId || session.countSectionId || session.sectionName || session.ownerSectionName || session.countSectionName || "section");
  return [restoreOperationalDate(session), site, cycle, section].join("::");
}

async function appendRestoreSafetyAudit(entry = {}) {
  const key = "mc-restore-safety-audit";
  let existing = [];
  try { existing = JSON.parse(mcGetItem(key) || "[]"); } catch (_) {}
  const next = [{ auditId: `restore-safety-${Date.now()}`, at: new Date().toISOString(), ...entry }, ...(Array.isArray(existing) ? existing : [])].slice(0, 250);
  mcSetItem(key, JSON.stringify(next));
  try { await persistMcCloudItemFast(key, JSON.stringify(next)); } catch (_) {}
}

function normalizeRecoverableSessionPayload(candidate, sessionId = "") {
  const cleanId = String(sessionId || "").trim();
  const seen = new Set();
  const queue = [{ value: candidate, depth: 0 }];
  const matches = [];

  while (queue.length) {
    const { value, depth } = queue.shift();
    if (!value || typeof value !== "object" || seen.has(value) || depth > 5) continue;
    seen.add(value);

    const directLines = Array.isArray(value.lines) ? value.lines
      : Array.isArray(value.countLines) ? value.countLines
      : Array.isArray(value.sessionLines) ? value.sessionLines
      : null;
    if (directLines?.length) {
      const meta = value.meta && typeof value.meta === "object" ? value.meta : {};
      const payloadId = String(meta.sessionId || value.sessionId || value.id || "").trim();
      if (!cleanId || !payloadId || payloadId === cleanId) {
        matches.push({
          ...value,
          lines: directLines,
          meta: { ...meta, sessionId: meta.sessionId || value.sessionId || cleanId },
        });
      }
    }

    // MC425 — historical Morpheus builds used several wrapper names around the
    // same durable session payload. Recovery must inspect those wrappers instead
    // of assuming the Firestore/IndexedDB value itself owns `lines`.
    ["payload", "snapshot", "detailSnapshot", "session", "detail", "data", "state", "completedSnapshot", "finalSnapshot", "ledger"].forEach((key) => {
      if (value[key] && typeof value[key] === "object") queue.push({ value: value[key], depth: depth + 1 });
    });
  }

  return matches.sort((a, b) => {
    const terminal = (x) => {
      const m = x?.meta || {};
      const status = String(m.status || "").toLowerCase();
      return Number(Boolean(m.completedAt || m.sectionCompletedAt || m.finalizedAt || ["completed", "complete", "section complete", "finalized"].includes(status)));
    };
    const touched = (x) => (x?.lines || []).filter((line) => line && (String(line.counted ?? "").trim() !== "" || String(line.waste ?? "").trim() !== "" || String(line.note ?? line.notes ?? "").trim() !== "")).length;
    return (terminal(b) - terminal(a)) || (touched(b) - touched(a)) || ((b?.lines?.length || 0) - (a?.lines?.length || 0));
  })[0] || null;
}

async function loadSessionDetailSnapshotForRestore(sessionId, target = {}, options = {}) {
  const cleanId = String(sessionId || "").trim();
  if (!cleanId) return null;

  // MC423 — normalize the session id once for all terminal-row discovery paths.
  // MC422 referenced cleanId during Firestore fallback discovery without defining it
  // in this function, which caused the ESLint compile failure before recovery could run.
  // MC421 — terminal-session rescue must inspect every durable mirror created by
  // the count engine, not only the current session-engine key. Migration-era and
  // interrupted-transition sessions may still have their complete row snapshot in
  // the legacy engine key, completed snapshot, checkpoint or canonical ledger.
  const localKeys = [
    `mc-count-ledger:${sessionId}`,
    `mc-session-completed:${sessionId}`,
    sessionEngineStorageKeyFor(sessionId),
    `mc-session-checkpoint:${sessionId}`,
    legacySessionEngineStorageKeyFor(sessionId),
  ];
  for (const key of localKeys) {
    try {
      const raw = mcGetItem(key);
      const parsed = raw ? JSON.parse(raw) : null;
      const normalized = normalizeRecoverableSessionPayload(parsed, cleanId);
      if (normalized) return normalized;
      // Large live-count payloads are intentionally moved out of localStorage into
      // IndexedDB. The old rescue path only checked synchronous browser storage,
      // so a perfectly healthy 91/91 count could look like 0/0 after refresh even
      // though the durable browser copy still existed.
      const indexed = await idbGetJson(key, null);
      const normalizedIndexed = normalizeRecoverableSessionPayload(indexed, cleanId);
      if (normalizedIndexed) return normalizedIndexed;
    } catch (_) {}
  }

  // MC424 — exact IndexedDB keys are not sufficient for older/migration-era
  // sessions. Large payloads may have survived under a compatibility key while
  // localStorage was intentionally cleared. Scan IndexedDB itself and match by
  // payload session identity before falling through to Firestore. This is the
  // browser equivalent of the MC422 cloud discovery fallback.
  try {
    const indexedRows = await idbListItems();
    const indexedMatches = (Array.isArray(indexedRows) ? indexedRows : [])
      .map((item) => {
        try {
          const rawParsed = item?.value ? JSON.parse(item.value) : null;
          const parsed = normalizeRecoverableSessionPayload(rawParsed, cleanId);
          if (!parsed) return null;
          const payloadSessionId = String(parsed?.meta?.sessionId || parsed?.sessionId || "").trim();
          const key = String(item?.key || "");
          const keyMatches = key.includes(cleanId);
          if (payloadSessionId !== cleanId && !keyMatches) return null;
          return { parsed, key, updatedAt: item?.updatedAt || "" };
        } catch (_) { return null; }
      })
      .filter(Boolean)
      .sort((a, b) => {
        const terminal = (x) => {
          const m = x?.parsed?.meta || {};
          const status = String(m.status || "").toLowerCase();
          return Number(Boolean(m.completedAt || m.sectionCompletedAt || m.finalizedAt || ["completed","complete","section complete","finalized"].includes(status)));
        };
        const touched = (x) => (x?.parsed?.lines || []).filter((line) => line && (String(line.counted ?? "").trim() !== "" || String(line.waste ?? "").trim() !== "" || String(line.note ?? line.notes ?? "").trim() !== "")).length;
        const lines = (x) => Number(x?.parsed?.lines?.length || 0);
        return (terminal(b) - terminal(a)) || (touched(b) - touched(a)) || (lines(b) - lines(a)) || (Date.parse(b.updatedAt || 0) - Date.parse(a.updatedAt || 0));
      });
    if (indexedMatches[0]?.parsed) return indexedMatches[0].parsed;
  } catch (_) {}

  const archiveSnapshot = target?.detailSnapshot && typeof target.detailSnapshot === "object" ? target.detailSnapshot : null;
  if (archiveSnapshot && Array.isArray(archiveSnapshot.lines) && archiveSnapshot.lines.length) return archiveSnapshot;

  const cloudKeys = [
    `mc-count-ledger:${sessionId}`,
    `mc-session-completed:${sessionId}`,
    sessionEngineStorageKeyFor(sessionId),
    `mc-session-checkpoint:${sessionId}`,
    legacySessionEngineStorageKeyFor(sessionId),
  ];
  for (const key of cloudKeys) {
    try {
      const cloudRaw = await withMcTimeout(mcGetCloudItem(key), 2500, null);
      const cloudParsed = cloudRaw ? JSON.parse(cloudRaw) : null;
      const normalizedCloud = normalizeRecoverableSessionPayload(cloudParsed, cleanId);
      if (normalizedCloud) return normalizedCloud;
    } catch (_) {}
  }

  // MC493 — deep Firestore collection discovery is reserved for explicit/manual
  // recovery. The Sessions page already knows its session IDs from the compact
  // routing authorities, so a normal zero-progress repair must stay on exact
  // per-session document reads. This prevents a single Sessions visit from
  // downloading the entire Morpheus collection while preserving the legacy
  // compatibility scan for administrator recovery flows.
  if (options?.allowDeepDiscovery !== false) {
    try {
      const candidates = await withMcTimeout(mcListCloudSessionDetails(), 5000, []);
      const matches = (Array.isArray(candidates) ? candidates : [])
        .filter((item) => String(item?.sessionId || '').trim() === cleanId)
        .map((item) => {
          try {
            const rawParsed = item?.value ? JSON.parse(item.value) : null;
            const parsed = normalizeRecoverableSessionPayload(rawParsed, cleanId);
            return parsed
              ? { parsed, authorityType: item.authorityType || '', updatedAt: item.updatedAt || '' }
              : null;
          } catch (_) { return null; }
        })
        .filter(Boolean)
        .sort((a, b) => {
          const terminal = (x) => {
            const m = x?.parsed?.meta || {};
            const status = String(m.status || '').toLowerCase();
            return Number(Boolean(m.completedAt || m.sectionCompletedAt || m.finalizedAt || ['completed','complete','section complete','finalized'].includes(status)));
          };
          const frozen = (x) => Number(['final','completed','ledger'].includes(String(x?.authorityType || '').toLowerCase()));
          const lines = (x) => Number(x?.parsed?.lines?.length || 0);
          const touched = (x) => (x?.parsed?.lines || []).filter((line) => line && (String(line.counted ?? '').trim() !== '' || String(line.waste ?? '').trim() !== '' || String(line.note ?? line.notes ?? '').trim() !== '')).length;
          return (terminal(b)-terminal(a)) || (frozen(b)-frozen(a)) || (touched(b)-touched(a)) || (lines(b)-lines(a)) || (Date.parse(b.updatedAt || 0)-Date.parse(a.updatedAt || 0));
        });
      if (matches[0]?.parsed) return matches[0].parsed;
    } catch (_) {}
  }

  return archiveSnapshot || null;
}


// MC475 — read-only row recovery for Compare Intelligence. Unlike the restore
// helper, this never writes or repairs lifecycle state. It inspects every durable
// session mirror and returns the strongest row-bearing payload for the exact
// session id, ranking completed/touched rows above the original prepared shell.
export async function loadSessionDetailSnapshotForCompare(sessionId, target = {}, options = {}) {
  const cleanId = String(sessionId || "").trim();
  if (!cleanId) return null;

  const candidates = [];
  const add = (value) => {
    try {
      const parsedRaw = typeof value === "string" ? JSON.parse(value) : value;
      const parsed = normalizeRecoverableSessionPayload(parsedRaw, cleanId);
      if (parsed?.lines?.length) candidates.push(parsed);
    } catch (_) {}
  };

  const keys = [
    `mc-count-ledger:${cleanId}`,
    `mc-session-completed:${cleanId}`,
    sessionEngineStorageKeyFor(cleanId),
    `mc-session-checkpoint:${cleanId}`,
    legacySessionEngineStorageKeyFor(cleanId),
  ];

  for (const key of keys) {
    try { add(mcGetItem(key)); } catch (_) {}
    try { add(await idbGetJson(key, null)); } catch (_) {}
  }

  if (target?.detailSnapshot) add(target.detailSnapshot);

  for (const key of keys) {
    try { add(await withMcTimeout(mcGetCloudItem(key), 2500, null)); } catch (_) {}
  }

  try {
    const indexedRows = await idbListItems();
    (Array.isArray(indexedRows) ? indexedRows : []).forEach((item) => {
      const key = String(item?.key || "");
      if (!key.includes(cleanId)) return;
      add(item?.value);
    });
  } catch (_) {}

  if (options?.allowDeepDiscovery !== false) {
    try {
      const cloudRows = await withMcTimeout(mcListCloudSessionDetails(), 5000, []);
      (Array.isArray(cloudRows) ? cloudRows : []).forEach((item) => {
        if (String(item?.sessionId || "").trim() === cleanId) add(item?.value);
      });
    } catch (_) {}
  }

  const touched = (payload) => (payload?.lines || []).filter((line) => line && (
    String(line.counted ?? "").trim() !== "" ||
    String(line.waste ?? "").trim() !== "" ||
    String(line.note ?? line.notes ?? "").trim() !== ""
  )).length;
  const terminal = (payload) => {
    const m = payload?.meta || {};
    const status = String(m.status || payload?.status || "").toLowerCase();
    return Number(Boolean(
      m.completedAt || m.sectionCompletedAt || m.finalizedAt || payload?.completedAt ||
      ["completed", "complete", "section complete", "section-complete", "finalized"].includes(status)
    ));
  };

  candidates.sort((a, b) =>
    (terminal(b) - terminal(a)) ||
    (touched(b) - touched(a)) ||
    ((b?.lines?.length || 0) - (a?.lines?.length || 0))
  );

  return candidates[0] || null;
}

export async function ensureCanonicalCountLedger(sessionId, target = {}, options = {}) {
  const cleanId = String(sessionId || "").trim();
  if (!cleanId) return { ok: false, reason: "missing-session-id", value: "", payload: null };
  const ledgerKey = `mc-count-ledger:${cleanId}`;

  let existingValue = "";
  try { existingValue = await mcGetCloudItem(ledgerKey); } catch (_) {}
  if (!existingValue) existingValue = mcGetItem(ledgerKey) || "";
  try {
    const parsed = existingValue ? JSON.parse(existingValue) : null;
    if (parsed && Array.isArray(parsed.lines) && parsed.lines.length) {
      const touched = parsed.lines.filter((line) => line && (
        String(line.counted ?? "").trim() !== "" ||
        String(line.waste ?? "").trim() !== "" ||
        String(line.note ?? line.notes ?? "").trim() !== ""
      )).length;

      // A canonical ledger can exist structurally while having lost all entered
      // values (for example 0/101 after a completed count). Previously the mere
      // presence of `lines` made this function declare the ledger healthy, which
      // prevented recovery from the richer completed/checkpoint/session mirrors.
      if (touched > 0) {
        return { ok: true, repaired: false, value: existingValue, payload: parsed };
      }

      const richer = await loadSessionDetailSnapshotForCompare(cleanId, target || {}, options);
      const richerTouched = (richer?.lines || []).filter((line) => line && (
        String(line.counted ?? "").trim() !== "" ||
        String(line.waste ?? "").trim() !== "" ||
        String(line.note ?? line.notes ?? "").trim() !== ""
      )).length;

      if (richer && Array.isArray(richer.lines) && richer.lines.length && richerTouched > touched) {
        const now = new Date().toISOString();
        const repairedPayload = {
          ...parsed,
          ...richer,
          meta: {
            ...(parsed?.meta || {}),
            ...(richer?.meta || {}),
            sessionId: cleanId,
            recoveredAt: now,
            recoveryMode: "richer-durable-mirror",
            recoveredFromZeroProgressLedger: true,
          },
          lines: richer.lines,
          savedAt: now,
        };
        const repairedValue = JSON.stringify(repairedPayload);
        mcSetItemSilent(ledgerKey, repairedValue);
        try { await mcSetCloudItemImmediate(ledgerKey, repairedValue); } catch (_) {}
        return { ok: true, repaired: true, value: repairedValue, payload: repairedPayload };
      }

      // No stronger durable copy exists. Do not rewrite or fabricate count data.
      return { ok: true, repaired: false, value: existingValue, payload: parsed };
    }
  } catch (_) {}

  let recovered = await loadSessionDetailSnapshotForRestore(cleanId, target || {}, options);
  if (!recovered || !Array.isArray(recovered.lines) || !recovered.lines.length) {
    // MC426 — legacy terminal escape hatch. Some migration-era sessions retained
    // their completed Sessions projection (91/91, exposure, flags, actor/time) but
    // permanently lost every row-bearing durable payload. Those rows cannot be
    // reconstructed safely. Do NOT invent them from today's prepared inventory.
    // Instead create an explicit zero-row legacy ledger so an administrator can
    // verify and finalize the stranded historical session. New/live sessions do
    // not qualify: only an already-terminal record with a positive historical
    // total can enter this path. The ledger is marked dataLoss=true permanently.
    const targetStatus = String(target?.status || "").trim().toLowerCase();
    const targetComplete = Boolean(
      target?.sectionCompletedAt || target?.completedAt ||
      ["section complete", "section-complete", "completed", "complete"].includes(targetStatus)
    );
    const historicalTotal = Number(target?.totalLines || target?.totalItems || target?.itemCount || 0);
    if (!targetComplete || historicalTotal <= 0) {
      return { ok: false, reason: "no-durable-row-snapshot", value: "", payload: null };
    }
    const completedAt = target?.completedAt || target?.sectionCompletedAt || target?.updatedAt || new Date().toISOString();
    recovered = {
      meta: {
        sessionId: cleanId,
        status: "Completed",
        lifecycleState: "complete",
        completedAt,
        sectionCompletedAt: target?.sectionCompletedAt || completedAt,
        storeName: target?.storeName || target?.store || target?.siteName || "",
        store: target?.store || target?.storeName || target?.siteName || "",
        cycleId: target?.cycleId || target?.cycleKey || "",
        cycleName: target?.cycleName || target?.cycle?.cycleName || target?.cycle?.name || "",
        sectionId: target?.sectionId || "",
        sectionName: target?.sectionName || "",
        fullSite: target?.fullSite === true,
        historicalTotalLines: historicalTotal,
        historicalCompletedLines: Number(target?.completedLines || historicalTotal),
        historicalFlaggedCount: Number(target?.flaggedCount || 0),
        historicalCostExposure: Number(target?.costExposure || 0),
        dataLoss: true,
        dataLossReason: "legacy-row-payload-unavailable",
        recoveryMode: "terminal-summary-ledger",
        recoveredAt: new Date().toISOString(),
      },
      lines: [],
      savedAt: new Date().toISOString(),
    };
  }

  const meta = { ...(recovered.meta || {}) };
  meta.sessionId = meta.sessionId || cleanId;
  const targetStatus = String(target?.status || "").trim().toLowerCase();
  const targetComplete = Boolean(
    target?.sectionCompletedAt || target?.completedAt ||
    ["section complete", "section-complete", "completed", "complete"].includes(targetStatus)
  );
  if (targetComplete) {
    const completedAt = meta.completedAt || meta.sectionCompletedAt || target?.completedAt || target?.sectionCompletedAt || target?.updatedAt || recovered.savedAt || new Date().toISOString();
    meta.status = "Completed";
    meta.completedAt = completedAt;
    meta.sectionCompletedAt = meta.sectionCompletedAt || completedAt;
  }
  const targetVerified = Boolean(
    target?.verified === true || target?.isVerified === true || target?.verifiedAt ||
    String(target?.verificationStatus || target?.verification?.status || "").trim().toLowerCase() === "verified"
  );
  if (targetVerified) {
    meta.verificationStatus = "verified";
    meta.verifiedAt = meta.verifiedAt || target?.verifiedAt || target?.updatedAt || new Date().toISOString();
    meta.verifiedBy = meta.verifiedBy || target?.verifiedBy || target?.lastEditedBy || target?.startedBy || "Administrator";
    meta.verifiedByProfileId = meta.verifiedByProfileId || target?.verifiedByProfileId || "";
    meta.verification = { ...(meta.verification || {}), status: "verified", verifiedAt: meta.verifiedAt, verifiedBy: meta.verifiedBy, verifiedByProfileId: meta.verifiedByProfileId };
  }

  const payload = { ...recovered, meta, savedAt: recovered.savedAt || new Date().toISOString() };
  const value = JSON.stringify(payload);
  mcSetItemSilent(ledgerKey, value);
  try { await mcSetCloudItemImmediate(ledgerKey, value); } catch (_) {}
  return { ok: true, repaired: true, value, payload };
}

export async function restoreArchivedSessionRecord(sessionId, options = {}) {
  const cleanId = String(sessionId || "").trim();
  const active = hydrateSessionRecordsFromSeeds();
  const archived = loadArchivedSessionRecords();
  const target = archived.find((item) => item.sessionId === cleanId);
  if (!target) return { ok: false, reason: "Archived session could not be found.", active, archived };

  const restoreModeRequested = options?.mode === "replace" ? "replace" : "new";
  const requestedReplaceId = restoreModeRequested === "replace" ? String(options?.replaceSessionId || "").trim() : "";
  const targetOperationalKey = restoreOperationalKey(target);
  const operationalConflicts = active.filter((item) => String(item?.sessionId || "") !== cleanId && restoreOperationalKey(item) === targetOperationalKey);
  const declaredReplacement = requestedReplaceId ? operationalConflicts.find((item) => String(item?.sessionId || "") === requestedReplaceId) : null;

  if (operationalConflicts.length && restoreModeRequested !== "replace") {
    await appendRestoreSafetyAudit({ type: "restore-blocked-duplicate", sessionId: cleanId, operationalKey: targetOperationalKey, conflictingSessionIds: operationalConflicts.map((item) => item.sessionId), actor: options?.restoreRequestedBy || "Administrator" });
    return { ok: false, code: "ACTIVE_CONFLICT", reason: "A matching active session already exists. Open it or choose Replace With Archived.", conflict: operationalConflicts[0], active, archived };
  }
  if (restoreModeRequested === "replace" && operationalConflicts.length && !declaredReplacement) {
    await appendRestoreSafetyAudit({ type: "restore-blocked-replacement-mismatch", sessionId: cleanId, operationalKey: targetOperationalKey, requestedReplaceId, conflictingSessionIds: operationalConflicts.map((item) => item.sessionId), actor: options?.restoreRequestedBy || "Administrator" });
    return { ok: false, code: "REPLACEMENT_MISMATCH", reason: "The active session changed before restore completed. Reopen Restore and review the current session.", conflict: operationalConflicts[0], active, archived };
  }

  // Restore is the opposite state transition. Mark it immediately so the archive
  // list snapshot cannot remove it from Live Sessions again after refresh.
  markSessionRestoredForSync(cleanId);

  const snapshot = await loadSessionDetailSnapshotForRestore(cleanId, target);
  const snapshotLines = Array.isArray(snapshot?.lines) ? snapshot.lines : [];
  const restoredStatus = String(target.archiveType || "").toLowerCase() === "completed" ? "Completed" : "Active";
  const restoredAt = new Date().toISOString();
  const restoreMode = restoreModeRequested;
  const restoreReason = String(options?.restoreReason || "").trim();
  const restoredBy = String(options?.restoreRequestedBy || "Administrator").trim() || "Administrator";
  const restoredByProfileId = options?.restoreRequestedByProfileId || null;
  const replacedSessionId = restoreMode === "replace" ? requestedReplaceId || null : null;
  const restoreAuditEvent = {
    id: `restore-${Date.now()}`,
    type: "restored",
    at: restoredAt,
    actor: restoredBy,
    actorProfileId: restoredByProfileId,
    reason: restoreReason,
    mode: restoreMode,
    replacedSessionId,
  };
  const restoredSnapshot = snapshot ? {
    ...snapshot,
    meta: {
      ...(snapshot.meta || {}),
      sessionId: cleanId,
      status: restoredStatus,
      archived: false,
      archivedAt: null,
      finalizedAt: null,
      finalizedBy: "",
      restoredAt,
      restoredFromArchive: true,
      restoredBy,
      restoreReason,
      restoreMode,
      restoreRequestedBy: restoredBy,
      restoreRequestedByProfileId: restoredByProfileId,
      replacedSessionId,
      restoreAudit: [
        ...(Array.isArray(snapshot?.meta?.restoreAudit) ? snapshot.meta.restoreAudit : []),
        restoreAuditEvent,
      ],
      lifecycleEvents: [
        ...(Array.isArray(snapshot?.meta?.lifecycleEvents) ? snapshot.meta.lifecycleEvents : []),
        restoreAuditEvent,
      ],
      store: target.store || target.storeName || snapshot?.meta?.store || snapshot?.meta?.storeName || "",
      storeName: target.storeName || target.store || snapshot?.meta?.storeName || snapshot?.meta?.store || "",
    },
    lines: snapshotLines,
    savedAt: snapshot.savedAt || target.updatedAt || target.archivedAt || restoredAt,
  } : null;

  if (restoredSnapshot) {
    mcSetItem(sessionEngineStorageKeyFor(cleanId), JSON.stringify(restoredSnapshot));
  }

  const restoredRecord = {
    ...target,
    sessionId: cleanId,
    status: restoredStatus,
    storeName: target.storeName || target.store || restoredSnapshot?.meta?.storeName || restoredSnapshot?.meta?.store || "",
    store: target.store || target.storeName || restoredSnapshot?.meta?.store || restoredSnapshot?.meta?.storeName || "",
    totalLines: snapshotLines.length || Number(target.totalLines || 0),
    completedLines: snapshotLines.length ? snapshotLines.filter((line) => line && (line.counted !== "" || line.waste !== "" || String(line.note || line.notes || "").trim())).length : Number(target.completedLines || 0),
    updatedAt: restoredAt,
    restoredAt,
    restoredFromArchive: true,
    restoredBy,
    restoreReason,
    restoreMode,
    restoreRequestedBy: restoredBy,
    restoreRequestedByProfileId: restoredByProfileId,
    replacedSessionId,
    // MC496 — the compact archive index intentionally does not carry historical
    // audit arrays. Restore them from the durable session snapshot instead so
    // archive-index compaction never discards lifecycle/correction evidence.
    correctionVersions: Array.isArray(restoredSnapshot?.meta?.correctionVersions)
      ? restoredSnapshot.meta.correctionVersions
      : [],
    correctedAt: restoredSnapshot?.meta?.correctedAt || target?.correctedAt || "",
    restoreAudit: [
      ...(Array.isArray(snapshot?.meta?.restoreAudit) ? snapshot.meta.restoreAudit : []),
      restoreAuditEvent,
    ],
    lifecycleEvents: [
      ...(Array.isArray(snapshot?.meta?.lifecycleEvents) ? snapshot.meta.lifecycleEvents : []),
      restoreAuditEvent,
    ],
  };
  delete restoredRecord.archived;
  delete restoredRecord.archivedAt;
  delete restoredRecord.archiveReason;
  delete restoredRecord.archiveType;
  delete restoredRecord.finalizedAt;
  delete restoredRecord.finalizedBy;
  delete restoredRecord.detailSnapshot;

  const nextArchived = archived.filter((item) => item.sessionId !== cleanId);
  const excludedActiveIds = new Set([cleanId]);
  if (restoreMode === "replace" && replacedSessionId) excludedActiveIds.add(replacedSessionId);
  operationalConflicts.forEach((item) => { if (restoreMode === "replace") excludedActiveIds.add(String(item?.sessionId || "")); });
  const nextActive = [restoredRecord, ...active.filter((item) => !excludedActiveIds.has(String(item?.sessionId || "")))].sort(
    (a, b) => new Date(b.updatedAt || b.startedAt || 0).getTime() - new Date(a.updatedAt || a.startedAt || 0).getTime()
  );

  // Write locally first so the UI updates immediately, then commit the exact same
  // active/archive lists to Firestore so a refresh or another device cannot undo
  // the restore with the older archive snapshot.
  mcRemoveItem(archivedSessionStorageKeyFor(cleanId));
  saveSessionRecords(nextActive);
  saveArchivedSessionRecords(nextArchived);

  try {
    await withMcTimeout(Promise.all([
      persistMcCloudItemFast(SESSION_LIST_KEY, JSON.stringify(nextActive)),
      persistMcCloudItemFast(ARCHIVE_LIST_KEY, JSON.stringify(compactArchivedSessionRecordsForList(nextArchived))),
      persistMcCloudItemFast(archivedSessionStorageKeyFor(cleanId), ""),
      restoredSnapshot ? persistMcCloudItemFast(sessionEngineStorageKeyFor(cleanId), JSON.stringify(restoredSnapshot)) : Promise.resolve(false),
    ]), 4500, false);
  } catch (err) {
    console.warn("Morpheus Count restore direct cloud persistence failed", err);
  }

  await appendRestoreSafetyAudit({
    type: restoreMode === "replace" ? "restore-replaced-active" : "restore-created-active",
    sessionId: cleanId,
    operationalKey: targetOperationalKey,
    replacedSessionId,
    reason: restoreReason,
    actor: restoredBy,
    actorProfileId: restoredByProfileId,
  });
  dispatchMcEvent(MC_SESSION_EVENT, { type: "restore", sessionId: cleanId, active: nextActive, archived: nextArchived });
  return { ok: true, active: nextActive, archived: nextArchived, restored: restoredRecord, replacedSessionId };
}


function liveCountNumberForRecovery(value) {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = Number(String(value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : 0;
}

function buildRecoveredSessionRecordFromDetail(detail = {}, fallbackSessionId = '') {
  const meta = detail?.meta && typeof detail.meta === 'object' ? detail.meta : {};
  const lines = Array.isArray(detail?.lines) ? detail.lines : [];
  const sessionId = String(meta.sessionId || fallbackSessionId || '').trim();
  if (!sessionId || !lines.length) return null;

  let completedLines = 0;
  let flaggedCount = 0;
  let costExposure = 0;
  lines.forEach((line = {}) => {
    const note = String(line.note || line.notes || '').trim();
    const touched = line.counted !== '' && line.counted !== undefined && line.counted !== null || line.waste !== '' && line.waste !== undefined && line.waste !== null || !!note;
    if (!touched) return;
    completedLines += 1;
    const counted = liveCountNumberForRecovery(line.counted);
    const waste = liveCountNumberForRecovery(line.waste);
    const variance = Number((counted + waste - Number(line.systemSoh || 0)).toFixed(2));
    const costVariance = Number((variance * Number(line.cost || 0)).toFixed(2));
    costExposure += costVariance;
    if (note || Math.abs(costVariance) >= 300) flaggedCount += 1;
  });

  const startedAt = meta.startedAt || detail.savedAt || new Date().toISOString();
  const lastEditedAt = meta.lastEditedAt || detail.savedAt || startedAt;
  const storedStatus = String(meta.status || '').trim().toLowerCase();
  const status = storedStatus === 'completed' || (completedLines >= lines.length && lines.length > 0) ? 'Completed' : 'Active';

  return {
    sessionId,
    storeName: meta.storeName || meta.store || meta.siteName || meta.storeKey || 'Unknown site',
    store: meta.store || meta.storeName || meta.siteName || meta.storeKey || '',
    storeKey: meta.storeKey || meta.store || meta.storeName || '',
    fileName: meta.sourceFile || meta.fileName || 'prepared import',
    startedAt,
    updatedAt: lastEditedAt,
    startedBy: meta.startedBy || 'User',
    lastEditedBy: meta.lastEditedBy || meta.startedBy || 'User',
    totalLines: lines.length,
    completedLines,
    flaggedCount,
    status,
    costExposure: Number(costExposure.toFixed(2)),
    recovered: true,
    recoveredAt: new Date().toISOString(),
  };
}

function operationalDateKey(value) {
  const date = value instanceof Date ? value : new Date(value || 0);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function sessionOperationalDate(record = {}) {
  const explicit = String(record?.operationalDate || record?.businessDate || record?.countDate || record?.meta?.operationalDate || '').slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(explicit)) return explicit;
  return operationalDateKey(record?.startedAt || record?.createdAt || record?.updatedAt || record?.savedAt);
}

export async function reconcileMissingSessionRecords(options = {}) {
  // P4.5 LEGACY CLEANUP: automatic reconciliation is permanently read-only.
  // Canonical ledgers/session detail may be inspected by diagnostics, but no
  // background process is allowed to recreate, promote, remove, or rewrite live
  // session discovery state. Explicit administrator recovery remains available
  // through recoverMissingSessionRecord(sessionId).
  const targetDate = String(options?.operationalDate || operationalDateKey(new Date())).slice(0, 10);
  return {
    ok: true,
    disabled: true,
    readOnly: true,
    operationalDate: targetDate,
    repaired: [],
    detailRepaired: [],
    backupRecovered: [],
    skipped: [],
    removedHistorical: [],
    active: loadSessionRecords(),
    reason: 'Automatic session reconciliation is disabled. Canonical ledgers are authoritative; use explicit manual recovery for a known session ID.',
  };
}

export async function recoverMissingSessionRecord(sessionId) {
  const cleanId = String(sessionId || '').trim();
  if (!cleanId) return { ok: false, reason: 'Enter a session ID.' };

  const active = loadSessionRecords();
  const archived = loadArchivedSessionRecords();
  if (active.some((item) => item?.sessionId === cleanId)) {
    return { ok: true, reason: 'Session is already in live sessions.', active, recovered: null };
  }
  if (archived.some((item) => item?.sessionId === cleanId)) {
    return { ok: false, reason: 'Session already exists in Archives. Use Restore from Archives instead.' };
  }

  // MC445 — explicit recovery must choose the strongest durable row snapshot, not
  // simply the first key that exists. A stale canonical 0/N shell can survive beside
  // a healthy completed/checkpoint snapshot. Choosing the first hit made recovery
  // faithfully restore the empty shell and made a real completed count look lost.
  const keys = [
    `mc-count-ledger:${cleanId}`,
    `mc-session-completed:${cleanId}`,
    sessionEngineStorageKeyFor(cleanId),
    `mc-session-checkpoint:${cleanId}`,
    legacySessionEngineStorageKeyFor(cleanId),
    `mc-session-detail:${cleanId}`,
  ];

  const candidates = [];
  const addCandidate = (value, source = '') => {
    try {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value;
      const normalized = normalizeRecoverableSessionPayload(parsed, cleanId);
      if (!normalized || !Array.isArray(normalized.lines) || !normalized.lines.length) return;
      const touched = normalized.lines.filter((line) => line && (
        String(line.counted ?? '').trim() !== '' ||
        String(line.waste ?? '').trim() !== '' ||
        String(line.note ?? line.notes ?? '').trim() !== ''
      )).length;
      const meta = normalized?.meta && typeof normalized.meta === 'object' ? normalized.meta : {};
      const status = String(meta.status || '').trim().toLowerCase();
      const terminal = Boolean(
        meta.completedAt || meta.sectionCompletedAt ||
        ['completed', 'complete', 'section complete', 'section-complete'].includes(status)
      );
      const savedAt = Date.parse(normalized.savedAt || meta.updatedAt || meta.lastEditedAt || meta.completedAt || meta.sectionCompletedAt || 0) || 0;
      candidates.push({ normalized, source, touched, terminal: terminal ? 1 : 0, lineCount: normalized.lines.length, savedAt });
    } catch (_) {}
  };

  for (const key of keys) {
    try { addCandidate(mcGetItem(key), `local:${key}`); } catch (_) {}
    try { addCandidate(await idbGetJson(key, null), `idb:${key}`); } catch (_) {}
  }

  // Read every exact cloud mirror before deciding. This is intentionally explicit
  // administrator recovery, so waiting for all candidates is preferable to picking
  // a stale first response.
  await Promise.all(keys.map(async (key) => {
    try { addCandidate(await withMcTimeout(mcGetCloudItem(key), 3500, null), `cloud:${key}`); } catch (_) {}
  }));

  // Compatibility/migration fallback: scan cloud session-detail documents by the
  // embedded session identity. This recovers counts whose durable payload survived
  // under an older localKey alias.
  try {
    const discovered = await withMcTimeout(mcListCloudSessionDetails(), 5000, []);
    (Array.isArray(discovered) ? discovered : [])
      .filter((item) => String(item?.sessionId || '').trim() === cleanId)
      .forEach((item) => addCandidate(item?.value, `cloud-scan:${item?.localKey || item?.key || 'compat'}`));
  } catch (_) {}

  candidates.sort((a, b) =>
    (b.touched - a.touched) ||
    (b.terminal - a.terminal) ||
    (b.lineCount - a.lineCount) ||
    (b.savedAt - a.savedAt)
  );

  let detail = candidates[0]?.normalized || null;

  if (!detail) {
    try {
      const backupRaw = mcGetItem(SESSION_LIST_BACKUP_KEY) || await mcGetCloudItem(SESSION_LIST_BACKUP_KEY);
      const parsedBackup = backupRaw ? JSON.parse(backupRaw) : [];
      const backupRecord = (Array.isArray(parsedBackup) ? parsedBackup : []).find((item) => String(item?.sessionId || '').trim() === cleanId);
      if (backupRecord) {
        const terminalStatus = String(backupRecord?.status || '').trim().toLowerCase();
        const isTerminal = backupRecord?.archived === true || backupRecord?.archivedAt || backupRecord?.finalizedAt || backupRecord?.deletedAt || ['archived', 'archive', 'finalized', 'deleted'].includes(terminalStatus);
        if (!isTerminal) {
          const activeOrgId = String(getMcActiveOrgId() || '').trim();
          const recovered = {
            ...backupRecord,
            tenantOrgId: String(backupRecord?.tenantOrgId || backupRecord?.orgId || activeOrgId).trim(),
            orgId: String(backupRecord?.orgId || backupRecord?.tenantOrgId || activeOrgId).trim(),
            status: backupRecord?.status || 'Active',
            recovered: true,
            tenantProofVersion: backupRecord?.tenantProofVersion || 2,
            recoveryMode: 'manual-session-list-backup',
            recoveredAt: new Date().toISOString(),
            detailAvailable: false,
          };
          if (recovered.tenantOrgId === activeOrgId) {
            const nextActive = [recovered, ...active.filter((item) => item?.sessionId !== cleanId)]
              .sort((a, b) => new Date(b.updatedAt || b.startedAt || b.createdAt || 0).getTime() - new Date(a.updatedAt || a.startedAt || a.createdAt || 0).getTime());
            try {
              await saveSessionRecordsDurable(nextActive);
            } catch (err) {
              return {
                ok: false,
                reason: `Recovered ${cleanId} from the durable session backup in this browser, but Firestore could not persist the Sessions routing record. Please retry recovery before refreshing.`,
                active: nextActive,
                recovered,
                detailMissing: true,
                durabilityError: err,
              };
            }
            dispatchMcEvent(MC_SESSION_EVENT, { type: 'manual-recover-backup', sessionId: cleanId, record: recovered });
            return { ok: true, reason: `Recovered ${cleanId} from the durable session backup. Its original count-detail document is no longer available, so the session ledger is restored but the old count lines cannot be reopened.`, active: nextActive, recovered, detailMissing: true };
          }
        }
      }
    } catch (_) {}
    return { ok: false, reason: `No session detail or backup ledger record found for ${cleanId}.` };
  }

  // Because the Archives index above proved this ID is NOT archived, strip stale
  // finalization/archive markers that may have leaked into a compatibility mirror.
  // Preserve completion/verification evidence and every counted row.
  detail = {
    ...detail,
    meta: {
      ...(detail.meta || {}),
      sessionId: cleanId,
      immutable: false,
      finalizedAt: null,
      finalizedBy: '',
      archivedAt: null,
      archivedBy: '',
      lifecycleState: String(detail?.meta?.completedAt || detail?.meta?.sectionCompletedAt || '').trim() ? 'completed' : (detail?.meta?.lifecycleState || 'active'),
      status: String(detail?.meta?.completedAt || detail?.meta?.sectionCompletedAt || '').trim() ? 'Completed' : (detail?.meta?.status || 'Active'),
    },
  };

  const recoveredBase = buildRecoveredSessionRecordFromDetail(detail, cleanId);
  if (!recoveredBase) {
    return { ok: false, reason: `Found ${cleanId}, but it has no recoverable count lines.` };
  }
  const activeOrgId = String(getMcActiveOrgId() || '').trim();
  const recovered = {
    ...recoveredBase,
    tenantOrgId: recoveredBase.tenantOrgId || activeOrgId,
    orgId: recoveredBase.orgId || activeOrgId,
    tenantProofVersion: recoveredBase.tenantProofVersion || 2,
    recoveryMode: 'manual-strongest-durable-snapshot',
    recoverySource: candidates[0]?.source || 'durable-snapshot',
  };

  const recoveredOperationalKey = restoreOperationalKey(recovered);
  const duplicate = active.find((item) => String(item?.sessionId || '') !== cleanId && restoreOperationalKey(item) === recoveredOperationalKey);
  if (duplicate) {
    await appendRestoreSafetyAudit({ type: 'manual-recovery-blocked-duplicate', sessionId: cleanId, operationalKey: recoveredOperationalKey, conflictingSessionId: duplicate.sessionId });
    return { ok: false, reason: `A matching active session already exists (${duplicate.sessionId}). Open that session instead of recovering a duplicate.`, conflict: duplicate };
  }

  const recoveredDetailValue = JSON.stringify({
    ...detail,
    meta: {
      ...(detail.meta || {}),
      sessionId: cleanId,
      status: recovered.status,
      store: recovered.store || recovered.storeName,
      storeName: recovered.storeName,
      lastEditedAt: recovered.updatedAt,
      recoveryMode: 'manual-strongest-durable-snapshot',
      recoverySource: candidates[0]?.source || 'durable-snapshot',
    },
    savedAt: detail.savedAt || recovered.updatedAt,
  });

  // Restore BOTH the canonical ledger and compatibility detail from the strongest
  // recovered payload. This is an explicit action initiated from Manual Recovery,
  // not a background hydration side effect.
  mcSetItem(`mc-count-ledger:${cleanId}`, recoveredDetailValue);
  mcSetItem(sessionEngineStorageKeyFor(cleanId), recoveredDetailValue);

  const nextActive = [recovered, ...active.filter((item) => item?.sessionId !== cleanId)].sort(
    (a, b) => new Date(b.updatedAt || b.startedAt || 0).getTime() - new Date(a.updatedAt || a.startedAt || 0).getTime()
  );

  // MC446 — a manual recovery is not complete until Firestore can reconstruct it
  // after a hard refresh. MC445 restored the local browser immediately but queued
  // the canonical/detail/list writes in the background, so Access Layer hydration
  // could clear that local routing row before Firestore had received it. Await all
  // three durable authorities before reporting recovery success.
  try {
    await Promise.all([
      mcSetCloudItemImmediate(`mc-count-ledger:${cleanId}`, recoveredDetailValue),
      mcSetCloudItemImmediate(sessionEngineStorageKeyFor(cleanId), recoveredDetailValue),
    ]);
    await saveSessionRecordsDurable(nextActive);
  } catch (err) {
    console.error('Morpheus Count manual recovery durability failed', { sessionId: cleanId, err });
    return {
      ok: false,
      reason: `Recovered ${cleanId} in this browser, but Firestore durability did not complete. Do not refresh yet; retry Recover Session so the canonical ledger and Sessions routing index can be committed.`,
      active: nextActive,
      recovered,
      durabilityError: err,
    };
  }

  dispatchMcEvent(MC_SESSION_EVENT, { type: 'manual-recover', sessionId: cleanId, record: recovered });
  return {
    ok: true,
    reason: `Recovered ${cleanId} from the strongest durable snapshot (${candidates[0]?.touched || 0}/${candidates[0]?.lineCount || recovered.totalLines} counted rows) and committed it to Firestore.`,
    active: nextActive,
    recovered,
  };
}

function normalizeOwnershipCategory(value = '') {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function resolveSingleAssignedSection(profile) {
  if (!profile || isAdminProfile(profile)) return null;
  const sections = activeSections();
  const allowed = getProfileAllowedSectionIds(profile, sections)
    .map(normalizeSectionId)
    .filter((id) => id && id !== 'ALL');
  const unique = Array.from(new Set(allowed));
  if (unique.length !== 1) return null;
  const sectionId = unique[0];
  const section = sections.find((item) => normalizeSectionId(item?.sectionId || item?.id || item?.name) === sectionId);
  return {
    sectionId,
    sectionName: section?.name || formatSectionLabel(sectionId),
  };
}

function filterPreparedRowsBySectionOwnership(rows = [], engine = {}, sectionId = '') {
  const target = normalizeSectionId(sectionId);
  if (!target) return rows;
  const ownership = engine?.config?.categoryOwnership || {};
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    const categoryKey = normalizeOwnershipCategory(row?.category || row?.output || row?.product_category || '');
    const owner = normalizeSectionId(ownership?.[categoryKey] || ownership?.[row?.category] || ownership?.[row?.output] || '');
    return owner && owner === target;
  });
}

export function buildSessionPayloadFromEngine(engine, overrides = {}) {
  const sessionId = overrides.sessionId || generateNextSessionId();
  const startedAt = overrides.startedAt || new Date().toISOString();
  const activeProfile = getCurrentProfile();
  const startedBy = overrides.startedBy || buildProfileDisplayName(activeProfile) || "User";
  const storeKey = normalizeScopeValue(overrides.storeKey || engine.mapping?.systemSoh || buildStoreInventoryHeader(engine.config?.storeName || DEFAULT_CONFIG.storeName));
  const storeName = overrides.storeName || labelFromInventoryHeader(storeKey) || engine.config?.storeName || DEFAULT_CONFIG.storeName;
  const sessionMapping = { ...(engine.mapping || DEFAULT_MAPPING), systemSoh: storeKey };
  const currentMappingKey = normalizeScopeValue(engine.mapping?.systemSoh || "");
  const basePreparedRows = Array.isArray(engine.preparedRows) && engine.preparedRows.length && currentMappingKey === storeKey
    ? engine.preparedRows
    : buildPreparedData(engine.rawRows || [], sessionMapping, engine.config, engine.rules);

  const isFullSite = overrides.fullSite === true;
  const sectionScope = isFullSite
    ? null
    : (overrides.sectionId
      ? { sectionId: normalizeSectionId(overrides.sectionId), sectionName: overrides.sectionName || formatSectionLabel(overrides.sectionId) }
      : resolveSingleAssignedSection(activeProfile));
  const sectionPreparedRows = sectionScope?.sectionId
    ? filterPreparedRowsBySectionOwnership(basePreparedRows, engine, sectionScope.sectionId)
    : basePreparedRows;
  const categoryNames = Array.from(new Set((overrides.categoryNames || []).map((item) => String(item || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  const countGroupNames = Array.from(new Set((overrides.countGroupNames || categoryNames).map((item) => String(item || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  const categoryScopeSource = overrides.categoryScopeSource || (countGroupNames.length ? 'count-groups' : 'normalized-categories');

  // Count Groups are display/operational scopes, not normalized row categories.
  // Resolve the selected groups back to their configured category outputs before
  // filtering. This keeps Categories → Count Groups as the single source of truth.
  const configuredGroups = Array.isArray(engine?.config?.categoryGroups) ? engine.config.categoryGroups : [];
  const selectedGroupNameSet = new Set(countGroupNames.map((item) => item.toLowerCase()));
  const selectedGroupOutputs = new Set(
    configuredGroups
      .filter((group) => selectedGroupNameSet.has(String(group?.name || '').trim().toLowerCase()))
      .flatMap((group) => Array.isArray(group?.outputs) ? group.outputs : [])
      .map((output) => String(output || '').trim().toLowerCase())
      .filter(Boolean)
  );
  const categorySet = new Set(categoryNames.map((item) => item.toLowerCase()));
  const hasExplicitScope = categoryNames.length > 0 || countGroupNames.length > 0;
  const preparedRows = hasExplicitScope
    ? sectionPreparedRows.filter((row) => {
        const normalizedCategory = String(row?.category || 'Other').trim().toLowerCase();
        if (categoryScopeSource === 'count-groups' && selectedGroupOutputs.size) {
          return selectedGroupOutputs.has(normalizedCategory);
        }
        return categorySet.has(normalizedCategory);
      })
    : sectionPreparedRows;

  return {
    sessionId,
    siteId: overrides.siteId || '',
    operationalDate: overrides.operationalDate || new Intl.DateTimeFormat('en-CA', {timeZone:'Africa/Johannesburg',year:'numeric',month:'2-digit',day:'2-digit'}).format(new Date(startedAt)),
    storeKey,
    storeName,
    fileName: engine.fileName || "prepared import",
    mapping: sessionMapping,
    config: engine.config || DEFAULT_CONFIG,
    preparedRows,
    sectionId: isFullSite ? '' : (sectionScope?.sectionId || ''),
    sectionName: isFullSite ? '' : (sectionScope?.sectionName || ''),
    fullSite: isFullSite,
    sessionType: isFullSite ? 'full-count' : 'section',
    countScope: isFullSite ? 'full-site' : 'section',
    categoryScopeType: (categoryNames.length || countGroupNames.length) ? 'selected' : 'all',
    categoryNames,
    countGroupNames,
    categoryIds: Array.isArray(overrides.categoryIds) ? overrides.categoryIds : (countGroupNames.length ? countGroupNames : categoryNames).map((item) => item.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')),
    categoryScopeLabel: overrides.categoryScopeLabel || (countGroupNames.length ? countGroupNames.join(' • ') : (categoryNames.length ? categoryNames.join(' • ') : 'All Count Groups')),
    categoryScopeSource,
    cycleId: overrides.cycleId || overrides.cycleKey || overrides.cycle?.cycleId || overrides.cycle?.id || '',
    cycleName: overrides.cycleName || overrides.cycle?.cycleName || overrides.cycle?.name || '',
    cycleKey: overrides.cycleKey || overrides.cycleId || overrides.cycle?.cycleId || overrides.cycle?.id || '',
    cycle: {
      cycleId: overrides.cycleId || overrides.cycleKey || overrides.cycle?.cycleId || overrides.cycle?.id || '',
      id: overrides.cycleId || overrides.cycleKey || overrides.cycle?.cycleId || overrides.cycle?.id || '',
      name: overrides.cycleName || overrides.cycle?.cycleName || overrides.cycle?.name || '',
    },
    createdAt: startedAt,
    startedAt,
    startedBy,
    lastEditedBy: overrides.lastEditedBy || startedBy,
  };
}

export function buildSessionDetailFromPayload(payload = {}) {
  const compact = compactSessionPayload(payload);
  return {
    meta: {
      sessionId: compact.sessionId,
      siteId: compact.siteId,
      operationalDate: compact.operationalDate,
      productScopeType: compact.productScopeType,
      productIds: compact.productIds,
      productNames: compact.productNames,
      productScopeLabel: compact.productScopeLabel,
      store: compact.storeName,
      storeName: compact.storeName,
      storeKey: compact.storeKey,
      sectionId: compact.sectionId || '',
      sectionName: compact.sectionName || '',
      fullSite: compact.fullSite === true,
      sessionType: compact.sessionType || (compact.fullSite === true ? 'full-count' : 'section'),
      countScope: compact.countScope || (compact.fullSite === true ? 'full-site' : 'section'),
      categoryScopeType: compact.categoryScopeType || 'all',
      categoryNames: compact.categoryNames || [],
      countGroupNames: compact.countGroupNames || compact.categoryNames || [],
      categoryIds: compact.categoryIds || [],
      categoryScopeLabel: compact.categoryScopeLabel || 'All Count Groups',
      categoryScopeSource: compact.categoryScopeSource || 'count-groups',
      cycleId: compact.cycleId || compact.cycleKey || '',
      cycleName: compact.cycleName || compact.cycle?.name || '',
      cycleKey: compact.cycleKey || compact.cycleId || '',
      cycle: compact.cycle || { cycleId: compact.cycleId || compact.cycleKey || '', id: compact.cycleId || compact.cycleKey || '', name: compact.cycleName || '' },
      status: "Active",
      startedBy: compact.startedBy || "User",
      lastEditedBy: compact.lastEditedBy || compact.startedBy || "User",
      importedRows: compact.preparedRows.length,
      sourceFile: compact.fileName || "prepared import",
      startedAt: compact.startedAt,
      billingStartVersion: compact.billingStartVersion,
      billingOriginalStartedAt: compact.billingOriginalStartedAt,
    },
    lines: compact.preparedRows.map((item, index) => ({
      id: item.id || `seed-${index + 1}`,
      product: item.product || "",
      morpheusSectionId: compact.sectionId || String(compact.config?.categoryOwnership?.[String(item.category || '').trim().toLowerCase()] || compact.config?.categoryOwnership?.[item.category] || ''),
      category: item.category || "Other",
      sectionId: compact.sectionId || '',
      sectionName: compact.sectionName || '',
      fullSite: compact.fullSite === true,
      sessionType: compact.sessionType || (compact.fullSite === true ? 'full-count' : 'section'),
      countScope: compact.countScope || (compact.fullSite === true ? 'full-site' : 'section'),
      categoryScopeType: compact.categoryScopeType || 'all',
      categoryNames: compact.categoryNames || [],
      countGroupNames: compact.countGroupNames || compact.categoryNames || [],
      categoryIds: compact.categoryIds || [],
      categoryScopeLabel: compact.categoryScopeLabel || 'All Count Groups',
      categoryScopeSource: compact.categoryScopeSource || 'count-groups',
      cycleId: compact.cycleId || compact.cycleKey || '',
      cycleName: compact.cycleName || compact.cycle?.name || '',
      cycleKey: compact.cycleKey || compact.cycleId || '',
      cycle: compact.cycle || { cycleId: compact.cycleId || compact.cycleKey || '', id: compact.cycleId || compact.cycleKey || '', name: compact.cycleName || '' },
      sku: item.sku || "—",
      cost: Number(item.effectiveCost ?? item.cost ?? 0),
      sourceCost: Number(item.sourceCost ?? item.cost ?? 0),
      countUnit: item.countUnit || "Unit",
      baseUnit: item.baseUnit || "Unit",
      systemUnitSize: Number(item.systemUnitSize || 1),
      priceUnitSize: Number(item.priceUnitSize || 1),
      priceBasis: item.priceBasis || "count",
      effectiveCost: Number(item.effectiveCost ?? item.cost ?? 0),
      conversionLabel: item.conversionLabel || "",
      systemSoh: Number(item.realSoh ?? item.systemSoh ?? 0),
      counted: "",
      waste: "",
      note: "",
    })),
    savedAt: new Date().toISOString(),
  };
}


export async function saveSessionDetailFromPayloadDurable(payload = {}) {
  const detail = buildSessionDetailFromPayload(payload);
  if (!detail.meta?.sessionId) return detail;

  const sessionId = String(detail.meta.sessionId).trim();
  const detailKey = sessionEngineStorageKeyFor(sessionId);
  const ledgerKey = `mc-count-ledger:${sessionId}`;
  const detailValue = JSON.stringify(detail);

  // Local first for instant rendering, but these are cache mirrors only.
  mcSetItemSilent(detailKey, detailValue);
  mcSetItemSilent(ledgerKey, detailValue);

  // MC449 invariant: a prepared session is not real until BOTH row-bearing
  // authorities exist in Firestore. The routing index is acknowledged separately
  // by upsertSessionRecordDurable().
  const [detailSaved, ledgerSaved] = await Promise.all([
    mcSetCloudItemImmediate(detailKey, detailValue),
    mcSetCloudItemImmediate(ledgerKey, detailValue),
  ]);

  if (detailSaved !== true || ledgerSaved !== true) {
    throw new Error(
      "Morpheus could not confirm the prepared count in Firestore. The count was not started."
    );
  }

  dispatchMcEvent(MC_SESSION_EVENT, { type: "detail", sessionId, detail });
  return detail;
}

export function saveSessionDetailFromPayload(payload = {}) {
  const detail = buildSessionDetailFromPayload(payload);
  if (!detail.meta?.sessionId) return detail;
  const detailValue = JSON.stringify(detail);
  mcSetItem(sessionEngineStorageKeyFor(detail.meta.sessionId), detailValue);
  mcSetCloudItem(sessionEngineStorageKeyFor(detail.meta.sessionId), detailValue).catch((err) => {
    console.warn("Morpheus Count could not immediately persist session detail to Firestore", err);
  });
  dispatchMcEvent(MC_SESSION_EVENT, { type: "detail", sessionId: detail.meta.sessionId, detail });
  return detail;
}


const SHARED_FLAG_CONFIG_KEY = `${FLAG_CONFIG_KEY}-shared`;
let flagConfigCloudTimer = null;
let flagConfigPendingValue = "";

function flagConfigStorageKey(scope = "ALL") {
  const normalized = scope === "ALL" ? "ALL" : normalizeStoreValue(scope || "");
  return `${FLAG_CONFIG_KEY}:${normalized || "ALL"}`;
}

function normalizeFlagConfigPayload(config = {}) {
  return {
    negativeFlagThreshold: Number(config?.negativeFlagThreshold ?? DEFAULT_CONFIG.negativeFlagThreshold ?? -300),
    positiveFlagThreshold: Number(config?.positiveFlagThreshold ?? DEFAULT_CONFIG.positiveFlagThreshold ?? 300),
    hideCountedInMosPdf: Boolean(config?.hideCountedInMosPdf ?? DEFAULT_CONFIG.hideCountedInMosPdf ?? false),
    hideZeroOrderItemsInMosPdf: Boolean(config?.hideZeroOrderItemsInMosPdf ?? DEFAULT_CONFIG.hideZeroOrderItemsInMosPdf ?? false),
  };
}

function parseFlagConfigValue(raw = null) {
  try {
    if (!raw) return null;
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (_) {
    return null;
  }
}

function sameFlagConfig(left = {}, right = {}) {
  const a = normalizeFlagConfigPayload(left);
  const b = normalizeFlagConfigPayload(right);
  return a.negativeFlagThreshold === b.negativeFlagThreshold
    && a.positiveFlagThreshold === b.positiveFlagThreshold
    && a.hideCountedInMosPdf === b.hideCountedInMosPdf
    && a.hideZeroOrderItemsInMosPdf === b.hideZeroOrderItemsInMosPdf;
}

function loadLegacyFlagConfig(scope = "ALL") {
  return parseFlagConfigValue(mcGetItem(flagConfigStorageKey(scope)));
}

function normalizeFlagConfigRecord(value = null) {
  const source = value && typeof value === "object" ? value : {};
  const siteOverrides = {};
  Object.entries(source?.siteOverrides || {}).forEach(([scope, config]) => {
    const normalizedScope = normalizeScopeValue(scope);
    if (normalizedScope !== "ALL") siteOverrides[normalizedScope] = normalizeFlagConfigPayload(config);
  });
  return {
    version: 2,
    global: normalizeFlagConfigPayload(source?.global || DEFAULT_CONFIG),
    siteOverrides,
    updatedAt: String(source?.updatedAt || ""),
  };
}

function legacyFlagConfigScopeCandidates(extraScope = "ALL") {
  const scopes = new Set();
  const add = (value) => {
    const normalized = normalizeScopeValue(value);
    if (normalized && normalized !== "ALL") scopes.add(normalized);
  };
  add(extraScope);

  if (typeof window !== "undefined") {
    try {
      for (let i = 0; i < window.localStorage.length; i += 1) {
        const key = window.localStorage.key(i) || "";
        if (!key.startsWith(`${FLAG_CONFIG_KEY}:`)) continue;
        add(key.slice(`${FLAG_CONFIG_KEY}:`.length));
      }
    } catch (_) {}
  }

  try {
    const sitesRaw = mcGetItem("mc-sites");
    const parsed = sitesRaw ? JSON.parse(sitesRaw) : [];
    const rows = Array.isArray(parsed) ? parsed
      : Array.isArray(parsed?.sites) ? parsed.sites
      : Array.isArray(parsed?.rows) ? parsed.rows : [];
    rows.forEach((site) => {
      [
        site?.inventoryKey,
        site?.inventory_key,
        site?.csvInventoryKey,
        site?.mapping?.systemSoh,
        site?.key,
        site?.storeKey,
        site?.name,
        site?.siteName,
      ].filter(Boolean).forEach(add);
    });
  } catch (_) {}

  return Array.from(scopes);
}

function buildLegacyFlagConfigRecord(scope = "ALL") {
  const global = normalizeFlagConfigPayload(loadLegacyFlagConfig("ALL") || DEFAULT_CONFIG);
  const siteOverrides = {};
  legacyFlagConfigScopeCandidates(scope).forEach((candidate) => {
    const legacy = loadLegacyFlagConfig(candidate);
    // Older builds mirrored All Sites into every local site key. Preserve only
    // genuinely different legacy values as explicit site overrides.
    if (legacy && !sameFlagConfig(legacy, global)) {
      siteOverrides[candidate] = normalizeFlagConfigPayload(legacy);
    }
  });
  return { version: 2, global, siteOverrides, updatedAt: "" };
}

function loadFlagConfigRecord(scope = "ALL") {
  const shared = parseFlagConfigValue(mcGetItem(SHARED_FLAG_CONFIG_KEY));
  if (shared && (shared.version === 2 || shared.global || shared.siteOverrides)) {
    return normalizeFlagConfigRecord(shared);
  }
  return buildLegacyFlagConfigRecord(scope);
}

function queueFlagConfigCloudSave(value = "") {
  flagConfigPendingValue = String(value || "");
  if (typeof window === "undefined") return;
  if (flagConfigCloudTimer) window.clearTimeout(flagConfigCloudTimer);
  flagConfigCloudTimer = window.setTimeout(() => {
    flagConfigCloudTimer = null;
    const pending = flagConfigPendingValue;
    flagConfigPendingValue = "";
    if (!pending) return;
    try { mcSetCloudItem(SHARED_FLAG_CONFIG_KEY, pending).catch(() => {}); } catch (_) {}
  }, 300);
}

export function loadFlagConfig(scope = "ALL") {
  const normalizedScope = normalizeScopeValue(scope);
  const shared = parseFlagConfigValue(mcGetItem(SHARED_FLAG_CONFIG_KEY));
  if (shared && (shared.version === 2 || shared.global || shared.siteOverrides)) {
    const record = normalizeFlagConfigRecord(shared);
    return normalizedScope === "ALL"
      ? record.global
      : (record.siteOverrides[normalizedScope] || null);
  }
  return loadLegacyFlagConfig(normalizedScope);
}

export function resolveFlagConfig(scope = "ALL") {
  const normalizedScope = normalizeScopeValue(scope);
  const record = loadFlagConfigRecord(normalizedScope);
  return normalizeFlagConfigPayload(
    normalizedScope !== "ALL" && record.siteOverrides[normalizedScope]
      ? record.siteOverrides[normalizedScope]
      : record.global,
  );
}

export async function hydrateFlagConfig(scope = "ALL") {
  const normalizedScope = normalizeScopeValue(scope);
  const cloudSharedRaw = await withMcTimeout(mcGetCloudItem(SHARED_FLAG_CONFIG_KEY), 5000, null);

  if (cloudSharedRaw) {
    const parsed = parseFlagConfigValue(cloudSharedRaw);
    if (parsed) {
      const normalizedRecord = normalizeFlagConfigRecord(parsed);
      try { mcSetCachedItem(SHARED_FLAG_CONFIG_KEY, JSON.stringify(normalizedRecord)); } catch (_) {}
      const resolved = normalizeFlagConfigPayload(
        normalizedScope !== "ALL" && normalizedRecord.siteOverrides[normalizedScope]
          ? normalizedRecord.siteOverrides[normalizedScope]
          : normalizedRecord.global,
      );
      dispatchMcEvent(MC_ENGINE_EVENT, { type: "flag-config-hydrated", scope: normalizedScope, payload: resolved });
      return resolved;
    }
  }

  // One-time migration for MC480: older builds stored a separate Firestore doc
  // per scope and mirrored All Sites into local site keys. If the new compact
  // shared authority does not exist yet, read those small legacy docs once,
  // preserve only genuinely different site values as overrides, then collapse
  // everything into one shared document for all future refreshes.
  const legacyScopes = legacyFlagConfigScopeCandidates(normalizedScope);
  const legacyKeys = [flagConfigStorageKey("ALL"), ...legacyScopes.map(flagConfigStorageKey)];
  const legacyCloudValues = await Promise.all(
    legacyKeys.map((key) => withMcTimeout(mcGetCloudItem(key), 5000, null)),
  );

  const localFallback = buildLegacyFlagConfigRecord(normalizedScope);
  const cloudGlobal = parseFlagConfigValue(legacyCloudValues[0]);
  const global = normalizeFlagConfigPayload(cloudGlobal || localFallback.global || DEFAULT_CONFIG);
  const siteOverrides = { ...(localFallback.siteOverrides || {}) };

  legacyScopes.forEach((candidate, index) => {
    const cloudScoped = parseFlagConfigValue(legacyCloudValues[index + 1]);
    if (!cloudScoped) return;
    if (sameFlagConfig(cloudScoped, global)) delete siteOverrides[candidate];
    else siteOverrides[candidate] = normalizeFlagConfigPayload(cloudScoped);
  });

  const migrated = {
    version: 2,
    global,
    siteOverrides,
    updatedAt: new Date().toISOString(),
  };
  const value = JSON.stringify(migrated);
  try { mcSetCachedItem(SHARED_FLAG_CONFIG_KEY, value); } catch (_) {}
  try { await withMcTimeout(mcSetCloudItem(SHARED_FLAG_CONFIG_KEY, value), 6000, false); } catch (_) {}

  const resolved = normalizeFlagConfigPayload(
    normalizedScope !== "ALL" && migrated.siteOverrides[normalizedScope]
      ? migrated.siteOverrides[normalizedScope]
      : migrated.global,
  );
  dispatchMcEvent(MC_ENGINE_EVENT, { type: "flag-config-migrated", scope: normalizedScope, payload: resolved });
  return resolved;
}

export function saveFlagConfig(config = {}, scope = "ALL") {
  const payload = normalizeFlagConfigPayload(config);
  const normalizedScope = normalizeScopeValue(scope);
  const current = loadFlagConfigRecord(normalizedScope);
  const next = {
    ...current,
    version: 2,
    global: normalizedScope === "ALL" ? payload : current.global,
    siteOverrides: normalizedScope === "ALL"
      ? { ...(current.siteOverrides || {}) }
      : { ...(current.siteOverrides || {}), [normalizedScope]: payload },
    updatedAt: new Date().toISOString(),
  };
  const value = JSON.stringify(next);

  // Cost mode: update this browser immediately, then collapse rapid typing into
  // one Firestore write. No listener is used. Other devices pick it up when the
  // Config page opens, the Morpheus refresh button is pressed, or the app reloads.
  try { mcSetCachedItem(SHARED_FLAG_CONFIG_KEY, value); } catch (_) {}
  queueFlagConfigCloudSave(value);
  dispatchMcEvent(MC_ENGINE_EVENT, { type: "flag-config", scope: normalizedScope, payload });
  return payload;
}

export function applyCategorySettingsAcrossScopes(engine = {}) {
  const source = engine && engine.config ? engine : { config: engine };
  return saveSharedLogicAcrossScopes({
    rules: Array.isArray(source.rules) ? source.rules : DEFAULT_RULES,
    config: source.config || DEFAULT_CONFIG,
  });
}

export function saveFlagConfigAcrossScopes(config = {}) {
  // All Sites is the shared default. Do not copy it into every site: a site only
  // becomes an override when an operator explicitly edits that site's Config.
  return saveFlagConfig(config, "ALL");
}

export function buildDefaultState(scope = "ALL") {
  return buildEmptyState(scope);
}
